import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Label,
  Row,
  Button,
  Input,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";
import Select, { components } from "react-select";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { getCustomers, searchCustomers } from "../../components/Api/GmsCustomer";
import { getEmployees } from "../../components/Api/GmsEmployee";
import { getRooms } from "../../components/Api/GmsRoom";
import { getBookings, saveBooking, /* checkAvailability, checkAvailabilityForTime, */ updateBulkCompletionStatus, updateCompletionStatus, updateServiceTimesForDone, exportBookingsToExcel, exportBookingDetailsToExcel, signBookingConsent, cancelBookingResource, rescheduleBooking, getCustomerBilledStandaloneServices } from "../../components/Api/GmsBooking";
import { getSpaServices } from "../../components/Api/GmsSpaService";
import { getSpaPackages, getSpaPackageById } from "../../components/Api/GmsSpaPackage";
import { getSpaPackageSeries } from "../../components/Api/GmsSpaPackageSeries";
import { getCustomerMemberships } from "../../components/Api/GmsCustomerMembership";
import { getEquipments } from "../../components/Api/GmsEquipment";
import { getSalesInvoiceById, getInvoiceForPrint } from "../../components/Api/GmsBilling";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getSources } from "../../components/Api/GmsSource";
import { Calendar, Clock, User, MapPin, Edit, X, Eye, Receipt, FileText, Download, CheckCircle, XCircle, SquarePen, Printer, Search, FileSignature } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Pagination from "rc-pagination";
import "rc-pagination/assets/index.css";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { useLocation, useNavigate } from "react-router-dom";
import QuickAddCustomer from "../../components/QuickAddCustomer";
import CustomerProfileModal from "../../components/CustomerProfileModal";
import ConsentFormModal from "../../components/ConsentFormModal";
import CustomerAdvancedSearchModal from "../../components/CustomerAdvancedSearchModal";
import html2pdf from "html2pdf.js";
import BASE_URL from "../../config";
import {
  hasPagePermissionWithAdmin,
  hasViewOnlyPermission,
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin
} from "../../components/Permission/PermissionService";

// Add CSS for loading spinner
const spinnerStyle = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Helper: format local date/time without timezone (no trailing 'Z')
const formatLocalDate = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatLocalDateTime = (date) => {
  if (!date) return null;
  const ymd = formatLocalDate(date);
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${ymd}T${hh}:${mm}:${ss}`;
};

// Helper: check if all services in a booking are complete
const areAllServicesComplete = (booking) => {
  if (!booking.details || booking.details.length === 0) {
    return false;
  }

  for (const detail of booking.details) {
    if (!detail.resourceDetails || detail.resourceDetails.length === 0) {
      return false;
    }

    for (const service of detail.resourceDetails) {
      if (service.completionStatus !== 'Complete') {
        return false;
      }
    }
  }

  return true;
};

// Session management
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    companyId: authUser.companyId || user.user_Company_Id || null,
    branchId: authUser.branchId || user.user_Branch_Id || null,
  };
};

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const defaultForm = {
  booking_Id: null,
  customer_Id: null,
  employee_Id: null,
  room_Id: null,
  from_Time: new Date(),
  to_Time: new Date(),
  entry_Date: new Date(),
  booking_Date: new Date(),
  is_Cancelled: false,
  cancellation_Reason: "",
  notes: "",
  source_Id: null, // Source ID from Source table
  sourceOther: "", // Additional source details (optional)
  modified_By: null,
  modified_On: null,
  // New fields for selected items
  selectedServices: [],
  selectedPackages: [],
  selectedPackageSeries: [],
  sendNotification: false,
  business_Id: null,
  branch_Id: null,
};

const emptyFilter = {
  booking_Id: null,
  customer_Id: null,
  employee_Id: null,
  room_Id: null,
  from_Date: null,
  to_Date: null,
  business_Id: null,
  branch_Id: null,
};

const BookingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Scroll helpers: always scroll to form on Edit/View, and restore list scroll on Back
  const formTopRef = useRef(null);
  const lastListScrollYRef = useRef(0);

  const scrollToFormTop = () => {
    // Defer until after render
    setTimeout(() => {
      if (formTopRef.current && typeof formTopRef.current.scrollIntoView === 'function') {
        formTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 0);
  };

  const restoreListScroll = () => {
    setTimeout(() => {
      window.scrollTo({ top: lastListScrollYRef.current || 0, behavior: 'smooth' });
    }, 0);
  };

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm);

  // Initialize filter with session values if available
  const getInitialFilter = () => {
    const session = getUserSession();
    const initialFilter = { ...emptyFilter };
    if (session.companyId) {
      initialFilter.business_Id = typeof session.companyId === 'string' ? parseInt(session.companyId) : session.companyId;
    }
    if (session.branchId) {
      initialFilter.branch_Id = typeof session.branchId === 'string' ? parseInt(session.branchId) : session.branchId;
    }
    return initialFilter;
  };

  const [filter, setFilter] = useState(getInitialFilter);
  const [bookings, setBookings] = useState([]);
  const [allBookings, setAllBookings] = useState([]); // Store full dataset
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
  const [selectedCustomerFilterOption, setSelectedCustomerFilterOption] = useState(null);
  const [selectedBusinessFilterOption, setSelectedBusinessFilterOption] = useState(null);
  const [selectedBranchFilterOption, setSelectedBranchFilterOption] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  // New state for services, packages, and package series
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [packageSeries, setPackageSeries] = useState([]);
  // State for service resource allocation
  const [serviceResources, setServiceResources] = useState([]);
  const [loading, setLoading] = useState(false);

  // Membership-related state
  const [customerMembership, setCustomerMembership] = useState(null);
  const [allCustomerMemberships, setAllCustomerMemberships] = useState([]);
  const [hasMembership, setHasMembership] = useState(false);
  const [membershipServices, setMembershipServices] = useState([]);
  const [membershipPackages, setMembershipPackages] = useState([]);
  const [membershipPackageSeries, setMembershipPackageSeries] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [formKey, setFormKey] = useState(0);
  const [filterKey, setFilterKey] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [bookingListSubTab, setBookingListSubTab] = useState("current-future"); // "current-future" or "past"

  // COMMENTED OUT per user request - availability checks not needed
  // New state for availability checking
  // const [availabilityResults, setAvailabilityResults] = useState({});
  // const [checkingAvailability, setCheckingAvailability] = useState(false);

  // Invoice print modal state
  const [showInvoicePrintModal, setShowInvoicePrintModal] = useState(false);
  const [invoicePrintData, setInvoicePrintData] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);

  // State to store package invoice IDs for bookings (to avoid repeated API calls)
  const [bookingPackageInvoiceMap, setBookingPackageInvoiceMap] = useState({});

  // State to track fully paid memberships (both packages and package series)
  const [fullyPaidMembershipMap, setFullyPaidMembershipMap] = useState({}); // {bookingId: {isFullyPaid: true, invoiceId: null, membershipType: 'package'|'packageSeries'}}

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsBooking');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsBooking');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsBooking');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsBooking');
  const isAdmin = isUserAdmin();

  // Get user session
  const session = useMemo(() => getUserSession(), []);

  const currencyCode = localStorage.getItem("currencyCode") || "AED";

  // Get current user's employee ID for filtering
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const currentEmployeeId = currentUser.user_EmployeeId || currentUser.employeeId;

  // COMMENTED OUT per user request - availability checks not needed
  // New state for time-based availability
  // const [timeAvailabilityResults, setTimeAvailabilityResults] = useState({});
  // const [checkingTimeAvailability, setCheckingTimeAvailability] = useState(false);
  // const [availabilityRefreshKey, setAvailabilityRefreshKey] = useState(0);

  // New state for cancellation modal
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  // New state for Done popup modal
  const [showDoneModal, setShowDoneModal] = useState(false);
  const [selectedBookingForDone, setSelectedBookingForDone] = useState(null);
  const [doneServiceTimes, setDoneServiceTimes] = useState([]);
  const [cancellingBookingId, setCancellingBookingId] = useState(null);

  // New state for entity cancellation modal
  const [showEntityCancelModal, setShowEntityCancelModal] = useState(false);
  const [entityCancelReason, setEntityCancelReason] = useState("");
  const [cancellingEntity, setCancellingEntity] = useState({ type: '', index: -1, name: '' });

  // New state for service resource cancellation modal
  const [showServiceCancelModal, setShowServiceCancelModal] = useState(false);
  const [serviceCancelReason, setServiceCancelReason] = useState("");
  const [cancellingResourceId, setCancellingResourceId] = useState(null);

  // New state for membership popup modal
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [selectedMembershipServices, setSelectedMembershipServices] = useState([]);
  const [selectedMembershipPackages, setSelectedMembershipPackages] = useState([]);

  const [selectedMembershipPackageSeries, setSelectedMembershipPackageSeries] = useState([]);

  // Billed services from invoices
  const [billedStandaloneServices, setBilledStandaloneServices] = useState([]);
  const [loadingBilledServices, setLoadingBilledServices] = useState(false);
  // Track if customer has billable items (memberships or billed services)
  const [customerHasBillableItems, setCustomerHasBillableItems] = useState(false);

  // Advanced search modal state
  const [showAdvancedSearchModal, setShowAdvancedSearchModal] = useState(false);

  // New state for hierarchical selection (keeping for backward compatibility if needed)
  const [hierarchicalSelection, setHierarchicalSelection] = useState({
    packageSeries: {},
    packages: {},
    services: {}
  });

  // New state for table-based selection
  const [tableSelection, setTableSelection] = useState({}); // { serviceId: { selected: boolean, requiredQty: number, membershipIds: [], packageIds: [] } }

  // Quick Add Customer state
  const [showQuickAddCustomer, setShowQuickAddCustomer] = useState(false);
  const [showCustomerProfile, setShowCustomerProfile] = useState(false);
  const [tableRowCustomerId, setTableRowCustomerId] = useState(null);

  // Consent form state
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentData, setConsentData] = useState({
    witnessName: '',
    witnessSignature: '',
    guestSignature: ''
  });
  const [consentLoading, setConsentLoading] = useState(false);
  const [loggedInEmployeeName, setLoggedInEmployeeName] = useState('');
  const [sources, setSources] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);

  // Reschedule modal state
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [rescheduleComment, setRescheduleComment] = useState("");
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [allDoneLoading, setAllDoneLoading] = useState(false);
  const [markSelectedDoneLoading, setMarkSelectedDoneLoading] = useState(false);

  // Source dropdown options - loaded from API
  // Handle both camelCase and PascalCase from API
  const sourceOptions = sources.map(source => ({
    value: source.Source_Id || source.source_Id,
    label: source.SourceName || source.sourceName || 'N/A'
  }));

  // Business and Branch options
  const businessOptions = useMemo(() => {
    return businesses.map(b => ({
      value: b.business_Id,
      label: b.business_NameEn || b.business_Name
    }));
  }, [businesses]);

  const branchOptions = useMemo(() => {
    // Filter branches based on selected business
    const filteredBranches = form.business_Id
      ? branches.filter(branch => branch.branch_Buisness_Id === form.business_Id)
      : branches;

    return filteredBranches.map(b => ({
      value: b.branch_Id,
      label: b.branch_NameEn || b.branch_Name
    }));
  }, [branches, form.business_Id]);

  // Filter options for business and branch
  const filterBusinessOptions = useMemo(() => {
    return businesses.map(b => ({
      value: b.business_Id,
      label: b.business_NameEn || b.business_Name
    }));
  }, [businesses]);

  const filterBranchOptions = useMemo(() => {
    // Filter branches based on selected business in filter
    const filteredBranches = filter.business_Id
      ? branches.filter(branch => branch.branch_Buisness_Id === filter.business_Id)
      : branches;

    return filteredBranches.map(b => ({
      value: b.branch_Id,
      label: b.branch_NameEn || b.branch_Name
    }));
  }, [branches, filter.business_Id]);

  useEffect(() => {
    if (hasPermission) {
      loadInitialData();
      loadLoggedInEmployeeName();

      // Check if there's a date parameter in the URL (from calendar page)
      const urlParams = new URLSearchParams(location.search);
      const dateParam = urlParams.get('date');

      // Note: Date parameter from calendar should redirect to AddBooking page instead
      // This page is now only for viewing/editing existing bookings

      // Set filter visibility - hidden by default for all users
      setShowFilters(false); // Hide filters by default, show only when filter button is clicked
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, isViewOnly, hasEditOrHigher, location.search]);

  // Consolidate booking loading triggers
  useEffect(() => {
    loadBookings();
  }, [page, pageSize, bookingListSubTab, filter]);

  // Auto-bind session companyId/branchId to filter dropdowns after businesses/branches load
  useEffect(() => {
    if (businesses.length > 0 && session.companyId) {
      const companyId = typeof session.companyId === 'string' ? parseInt(session.companyId) : session.companyId;
      const companyExists = businesses.find(b => b.business_Id === companyId);
      if (companyExists) {
        // Set filter value if not already set
        setFilter(prev => {
          if (prev.business_Id !== companyId) {
            return { ...prev, business_Id: companyId };
          }
          return prev;
        });
        // Set selected option
        const businessOption = businesses.find(b => b.business_Id === companyId);
        if (businessOption) {
          const option = {
            value: businessOption.business_Id,
            label: businessOption.business_NameEn || businessOption.business_Name
          };
          if (!selectedBusinessFilterOption || selectedBusinessFilterOption.value !== companyId) {
            setSelectedBusinessFilterOption(option);
          }
        }
        // Load branches for this company
        if (session.branchId) {
          loadBranches(companyId).then(() => {
            const branchId = typeof session.branchId === 'string' ? parseInt(session.branchId) : session.branchId;
            // Set filter value
            setFilter(prev => {
              if (prev.branch_Id !== branchId) {
                return { ...prev, branch_Id: branchId };
              }
              return prev;
            });
            // Set selected option after branches are loaded - use a separate effect
          });
        } else {
          loadBranches(companyId);
        }
      }
    }
  }, [businesses, session.companyId, session.branchId]);

  // Set branch filter option after branches are loaded
  useEffect(() => {
    if (branches.length > 0 && session.branchId && filter.business_Id) {
      const branchId = typeof session.branchId === 'string' ? parseInt(session.branchId) : session.branchId;
      const branchExists = branches.find(b => b.branch_Id === branchId && b.branch_Buisness_Id === filter.business_Id);
      if (branchExists) {
        const option = {
          value: branchExists.branch_Id,
          label: branchExists.branch_NameEn || branchExists.branch_Name
        };
        if (!selectedBranchFilterOption || selectedBranchFilterOption.value !== branchId) {
          setSelectedBranchFilterOption(option);
        }
      }
    }
  }, [branches, session.branchId, filter.business_Id]);

  // Auto-apply filter when session has companyId/branchId (on initial load only)
  const hasAutoAppliedFilter = useRef(false);
  // Auto-apply filter logic simplified - filter is already initialized from session
  // No need for a delayed reload
  useEffect(() => {
    if ((session.companyId || session.branchId) && !hasAutoAppliedFilter.current && businesses.length > 0) {
      hasAutoAppliedFilter.current = true;
      // Filter is already applied via getInitialFilter, no need to reload
    }
  }, [session.companyId, session.branchId, businesses.length]);

  // Check for fully paid package/package series memberships when bookings are loaded
  useEffect(() => {
    const checkFullyPaidMemberships = async () => {
      if (!bookings || bookings.length === 0) {
        return;
      }

      const invoiceMap = {};
      const fullyPaidMap = {};

      // Check each booking for membership
      for (const booking of bookings) {
        // Skip if already checked
        if (bookingPackageInvoiceMap[booking.booking_Id] && fullyPaidMembershipMap[booking.booking_Id]) {
          continue;
        }

        try {
          const result = await checkIfFromFullyPaidPackage(booking);
          if (result.isFromMembership) {
            // Store invoice ID if available (for packages)
            if (result.invoiceId) {
              invoiceMap[booking.booking_Id] = result.invoiceId;
            }
            // Store fully paid status (for both packages and package series)
            if (result.isFullyPaid) {
              fullyPaidMap[booking.booking_Id] = {
                isFullyPaid: true,
                invoiceId: result.invoiceId,
                membershipType: result.membershipType
              };
            }
          }
        } catch (error) {
          console.error(`Error checking membership for booking ${booking.booking_Id}:`, error);
        }
      }

      // Update states if we found any
      if (Object.keys(invoiceMap).length > 0) {
        setBookingPackageInvoiceMap(prev => ({ ...prev, ...invoiceMap }));
      }
      if (Object.keys(fullyPaidMap).length > 0) {
        setFullyPaidMembershipMap(prev => ({ ...prev, ...fullyPaidMap }));
      }
    };

    checkFullyPaidMemberships();
  }, [bookings]);

  // Handle navigation from notification
  useEffect(() => {
    if (location.state?.bookingId && location.state?.editMode) {
      const bookingId = location.state.bookingId;
      const booking = bookings.find(b => b.Booking_Id === bookingId);
      if (booking) {
        handleEdit(booking);
      }
    }
  }, [location.state, bookings]);

  // Listen for consent signed message from child window
  useEffect(() => {
    const handleMessage = (event) => {
      // Verify message origin for security
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data && event.data.type === 'CONSENT_SIGNED') {
        // Refresh booking list
        loadBookings();
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [filter]);

  // COMMENTED OUT per user request - availability checks not needed
  // Check availability when service resources change
  // useEffect(() => {
  //   if (serviceResources.length > 0 && form.booking_Date) {
  //     // Check time-based availability for each resource that has time set
  //     // Use a small delay to ensure state is fully updated
  //     const timeoutId = setTimeout(async () => {
  //       const resourcesToCheck = serviceResources.filter(resource => resource.fromTime && resource.toTime);
  //       
  //       if (resourcesToCheck.length > 0) {
  //         // Check availability for all resources
  //         // Note: form.booking_Id should be current here since useEffect runs after state updates
  //         const availabilityPromises = resourcesToCheck.map(resource => {
  //           return checkTimeAvailability(resource.id, resource.fromTime, resource.toTime);
  //         });
  //         
  //         // Wait for all checks to complete, then update refresh key once
  //         await Promise.all(availabilityPromises);
  //         setAvailabilityRefreshKey(prev => prev + 1);
  //       }
  //     }, 100);
  //     
  //     return () => clearTimeout(timeoutId);
  //   }
  // }, [serviceResources, form.booking_Date]);

  const loadInitialData = async () => {
    try {
      // Load all initial data in parallel (customers will also load in parallel with bookings)
      await Promise.all([
        loadEmployees(),
        loadRooms(),
        loadEquipments(),
        loadServices(),
        loadPackages(),
        loadPackageSeries(),
        loadBusinesses(),
        loadBranches(),
        loadSources(),
      ]);
      // Customers are loaded in parallel with bookings for better performance
    } catch (error) {
      console.error("Error loading initial data:", error);
      Swal.fire("Error", "Failed to load initial data", "error");
    }
  };

  const loadSources = async () => {
    setSourcesLoading(true);
    try {
      const filter = {
        Page: 1,
        PageSize: 1000, // Load all sources for dropdown
      };
      const response = await getSources(filter);

      // Handle both camelCase and PascalCase response formats
      const sourcesList = response?.Sources || response?.sources || [];

      if (Array.isArray(sourcesList) && sourcesList.length > 0) {
        setSources(sourcesList);
      } else if (Array.isArray(response)) {
        setSources(response);
      } else {
        setSources([]);
      }

      console.log('Loaded sources:', sourcesList.length, sourcesList);
    } catch (error) {
      console.error("Error loading sources:", error);
      // Don't show error to user, just set empty array
      setSources([]);
    } finally {
      setSourcesLoading(false);
    }
  };

  const loadBusinesses = async () => {
    try {
      const data = await getBusinesses({ business_Active: true });
      setBusinesses(data || []);
    } catch (error) {
      console.error("Error loading businesses:", error);
    }
  };

  const loadBranches = async (businessId = null) => {
    try {
      const filter = businessId ? { Business_Id: businessId, branch_Active: true } : { branch_Active: true };
      const response = await getBranches(filter);
      const data = response.data || response;
      if (Array.isArray(data)) {
        setBranches(data);
      } else {
        setBranches([]);
      }
    } catch (error) {
      console.error("Error loading branches:", error);
      setBranches([]);
    }
  };

  // Load all customers (for dropdowns, search, and table display)
  const loadCustomers = async () => {
    try {
      setLoadingCustomers(true);
      const data = await getCustomers({});
      const formattedCustomers = data.map(customer => ({
        value: customer.customer_Id,
        label: customer.customer_NameEn || customer.customer_Name,
        customer: customer // Store full customer object to access phone number
      }));
      setCustomers(formattedCustomers);
    } catch (error) {
      console.error('Error loading customers:', error);
      // Don't show error to user for table loading, just log it
      // Error will be shown if called from initial data load
    } finally {
      setLoadingCustomers(false);
    }
  };

  // Manual search function for customer search
  const searchCustomersManually = async (inputValue) => {
    console.log('🔍 searchCustomersManually called with inputValue:', inputValue);

    if (!inputValue || inputValue.length < 3) {
      console.log('❌ Input too short, clearing options');
      setCustomerSearchOptions([]);
      return;
    }

    try {
      setCustomerSearchLoading(true);
      const cleanedInput = inputValue.replace(/\s+/g, '');
      console.log('🚀 Making API call to searchCustomers with:', { cleanedInput, page: 1, pageSize: 20 });
      const response = await searchCustomers(cleanedInput, 1, 20);
      console.log('✅ API response received:', response);

      const formattedOptions = response.items.map(customer => ({
        value: customer.customer_Id,
        label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
        customer: customer
      }));

      console.log('📋 Formatted options:', formattedOptions);
      setCustomerSearchOptions(formattedOptions);
    } catch (error) {
      console.error('❌ Error searching customers:', error);
      setCustomerSearchOptions([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  // Manual search function for customer filter search
  const searchCustomersFilterManually = async (inputValue) => {
    console.log('🔍 searchCustomersFilterManually called with inputValue:', inputValue);

    if (!inputValue || inputValue.length < 3) {
      console.log('❌ Input too short, clearing options');
      setCustomerSearchOptions([]);
      return;
    }

    try {
      setCustomerSearchLoading(true);
      const cleanedInput = inputValue.replace(/\s+/g, '');
      console.log('🚀 Making API call to searchCustomers with:', { cleanedInput, page: 1, pageSize: 20 });
      const response = await searchCustomers(cleanedInput, 1, 20);
      console.log('✅ API response received:', response);

      const formattedOptions = response.items.map(customer => ({
        value: customer.customer_Id,
        label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
        customer: customer
      }));

      console.log('📋 Formatted options:', formattedOptions);
      setCustomerSearchOptions(formattedOptions);
    } catch (error) {
      console.error('❌ Error searching customers:', error);
      setCustomerSearchOptions([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  const handleCustomerAdded = async (newCustomer) => {
    console.log("🔍 BookingPage - handleCustomerAdded received:", newCustomer);

    // Get the customer ID from the response
    const customerId = newCustomer.customer_Id || newCustomer.id;
    console.log("🔍 Looking for customer ID:", customerId);

    try {
      // Try to fetch the specific customer by ID
      const customerData = await getCustomers({ customer_Id: customerId });
      console.log("🔍 Fetched customer data:", customerData);

      if (customerData && customerData.length > 0) {
        const addedCustomer = customerData[0];
        console.log("🔍 Found customer:", addedCustomer);

        // Set the newly added customer as selected
        setForm(prev => ({
          ...prev,
          customer_Id: customerId
        }));
        // Set the selected customer option for display
        setSelectedCustomerOption({
          value: customerId,
          label: `${addedCustomer.customer_NameEn || 'N/A'} — ${addedCustomer.customer_ContactNo || 'N/A'}`
        });
        console.log("🔍 Set customer option:", `${addedCustomer.customer_NameEn || 'N/A'} — ${addedCustomer.customer_ContactNo || 'N/A'}`);

        // Also reload the full customer list to keep it updated
        loadCustomers();
      } else {
        // Fallback: just set the ID and let the user search for the customer
        setForm(prev => ({
          ...prev,
          customer_Id: customerId
        }));
        setSelectedCustomerOption({
          value: customerId,
          label: `Customer ID: ${customerId}`
        });
        console.log("🔍 Customer not found, using fallback");

        // Still reload the full customer list
        loadCustomers();
      }
    } catch (error) {
      console.error("Error fetching customer:", error);
      // Fallback: just set the ID
      setForm(prev => ({
        ...prev,
        customer_Id: customerId
      }));
      setSelectedCustomerOption({
        value: customerId,
        label: `Customer ID: ${customerId}`
      });
      console.log("🔍 Error occurred, using fallback");
    }
  };

  const loadEmployees = async () => {
    try {
      // Build filter based on logged-in user's company and/or branch
      const filter = {};

      // Filter by company (business id) if available
      if (session.companyId) {
        filter.Employee_Business_Id = session.companyId;
      }

      // Filter by branch if available (only if company is also available)
      if (session.branchId && session.companyId) {
        filter.Employee_Branch_Id = session.branchId;
      }

      const data = await getEmployees(filter);
      console.log('Employees API response:', data);
      const formattedEmployees = data.map(emp => ({
        value: emp.employee_Id,
        label: emp.employee_NameEn || emp.employee_Name,
      }));
      console.log('Formatted employees:', formattedEmployees);
      setEmployees(formattedEmployees);
    } catch (error) {
      console.error('Error loading employees:', error);
      Swal.fire("Error", "Failed to load employees", "error");
    }
  };

  const loadRooms = async () => {
    try {
      const data = await getRooms({});
      console.log('Rooms API response:', data);
      const formattedRooms = data.map(room => ({
        value: room.room_Id,
        label: room.room_Name_English || room.room_Name_Arabic || room.room_Name,
      }));
      console.log('Formatted rooms:', formattedRooms);
      setRooms(formattedRooms);
    } catch (error) {
      console.error('Error loading rooms:', error);
      Swal.fire("Error", "Failed to load rooms", "error");
    }
  };

  // New load functions for services, packages, and package series
  const loadServices = async () => {
    try {
      const data = await getSpaServices({ status: true });
      // Additional client-side filtering to ensure only active services are stored
      const activeServices = (data || []).filter(service => service.status === true);
      const formattedServices = activeServices.map(service => ({
        value: service.spaService_Id,
        label: service.spaServiceName_En || service.spaServiceName_Ar || service.spaService_NameEn || service.spaService_Name,
        duration: service.duration || 30, // Include duration field from API response
      }));
      setServices(formattedServices);
    } catch (error) {
      console.error('Error loading services:', error);
      Swal.fire("Error", "Failed to load services", "error");
    }
  };

  const loadPackages = async () => {
    try {
      const data = await getSpaPackages({ status: true });
      // Additional client-side filtering to ensure only active packages are stored
      const activePackages = (data || []).filter(pkg => pkg.status === true);
      const formattedPackages = activePackages.map(pkg => ({
        value: pkg.packageId,
        label: pkg.packageName_En || pkg.packageName_Ar || pkg.package_NameEn || pkg.package_Name,
      }));
      setPackages(formattedPackages);
    } catch (error) {
      console.error('Error loading packages:', error);
      Swal.fire("Error", "Failed to load packages", "error");
    }
  };

  const loadPackageSeries = async () => {
    try {
      const data = await getSpaPackageSeries({ status: true });
      // Additional client-side filtering to ensure only active package series are stored
      const activePackageSeries = (data || []).filter(series => series.status === true);
      const formattedPackageSeries = activePackageSeries.map(series => ({
        value: series.seriesId,
        label: series.seriesName_En || series.seriesName_Ar,
      }));
      setPackageSeries(formattedPackageSeries);
    } catch (error) {
      console.error('Error loading package series:', error);
      Swal.fire("Error", "Failed to load package series", "error");
    }
  };

  // Load logged-in employee name for witness field
  const loadLoggedInEmployeeName = async () => {
    try {
      const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const employeeId = authUser.employeeId || user.user_EmployeeId;

      if (employeeId) {
        const employees = await getEmployees({ employee_Id: employeeId });
        if (employees && employees.length > 0) {
          const employee = employees[0];
          const employeeName = employee.employee_NameEn || employee.employee_Name || '';
          setLoggedInEmployeeName(employeeName);
          // Pre-fill witness name in consent data
          setConsentData(prev => ({
            ...prev,
            witnessName: prev.witnessName || employeeName
          }));
        }
      }
    } catch (error) {
      console.error('Error loading logged-in employee name:', error);
    }
  };

  const loadEquipments = async () => {
    try {
      const data = await getEquipments({ status: true });
      const formattedEquipments = data.map(equipment => ({
        value: equipment.eq_Id,
        label: equipment.eq_Name_English || equipment.eq_Name_Arabic,
      }));
      setEquipments(formattedEquipments);
    } catch (error) {
      console.error('Error loading equipments:', error);
      Swal.fire("Error", "Failed to load equipments", "error");
    }
  };

  // Function to fetch services from selected packages
  const fetchPackageServices = async (packageIds) => {
    try {
      const allPackageServices = [];
      const seenServiceKeys = new Set(); // Track services to prevent duplicates within the same package

      for (const packageId of packageIds) {
        const packageData = await getSpaPackageById(packageId);
        // Only process active packages
        if (packageData && packageData.status === true && packageData.details) {
          packageData.details.forEach(detail => {
            const service = services.find(s => s.value === detail.service_Id);
            // Only include services that are in the active services list
            if (service) {
              // Create a key to detect duplicates (same package + same service)
              // Note: We allow the same service to appear multiple times if it has different quantities
              // But we prevent exact duplicates (same package + same service + same quantity)
              const serviceKey = `${packageId}_${detail.service_Id}_${detail.quantity || 1}`;

              if (seenServiceKeys.has(serviceKey)) {
                console.warn(`⚠️ Duplicate service in package ${packageId}: Service ${detail.service_Id} with quantity ${detail.quantity || 1} - skipping duplicate`);
                return; // Skip this duplicate
              }
              seenServiceKeys.add(serviceKey);

              allPackageServices.push({
                ...service,
                packageId: packageId,
                packageName: packageData.packageName_En || packageData.packageName_Ar,
                serviceId: detail.service_Id,
                quantity: detail.quantity || 1,
                duration: service.duration || 30 // Use service duration from loaded services
              });
            }
          });
        }
      }

      return allPackageServices;
    } catch (error) {
      console.error('Error fetching package services:', error);
      return [];
    }
  };

  // Helper function to get package series from membership
  const getPackageSeriesFromMembership = async (membershipId) => {
    try {
      const memberships = await getCustomerMemberships({});
      const membership = memberships.find(m => m.membershipId === membershipId);
      if (membership && membership.installments) {
        const uniqueSeriesIds = [...new Set(membership.installments.map(installment => installment.seriesId))];
        return uniqueSeriesIds.map(seriesId => {
          const series = packageSeries.find(s => s.value === seriesId);
          return series;
        }).filter(Boolean);
      }
      return [];
    } catch (error) {
      console.error('Error getting package series from membership:', error);
      return [];
    }
  };

  // Check customer membership and populate dropdowns accordingly
  const checkCustomerMembership = async (customerId) => {
    if (!customerId) {
      // Reset membership data when no customer is selected
      setCustomerMembership(null);
      setAllCustomerMemberships([]);
      setHasMembership(false);
      setMembershipServices([]);
      setMembershipPackages([]);
      setMembershipPackageSeries([]);
      setBilledStandaloneServices([]);
      setCustomerHasBillableItems(false);
      return;
    }

    try {
      // Load both memberships and billed services in parallel
      const [memberships, billedServices] = await Promise.all([
        getCustomerMemberships({ customerId: customerId }),
        getCustomerBilledStandaloneServices({
          CustomerId: customerId,
          BranchId: null
        }).catch(err => {
          console.error('Error fetching billed standalone services:', err);
          return []; // Return empty array on error
        })
      ]);

      // Set billed services
      const hasBilledServices = billedServices && billedServices.length > 0;
      setBilledStandaloneServices(billedServices || []);

      if (memberships && memberships.length > 0) {
        // Customer has membership - store all memberships
        setAllCustomerMemberships(memberships);
        // Use the first membership for backward compatibility (or aggregate all)
        const membership = memberships[0];
        setCustomerMembership(membership);
        setHasMembership(true);

        // Aggregate data from all memberships
        let allPendingDetails = [];
        let allSeriesIds = new Set();

        memberships.forEach(m => {
          if (m.details && m.details.length > 0) {
            const pendingDetails = m.details.filter(detail => detail.completionStatus === "Pending");
            allPendingDetails = [...allPendingDetails, ...pendingDetails];
          }
          if (m.installments && m.installments.length > 0) {
            m.installments.forEach(inst => {
              if (inst.seriesId) {
                allSeriesIds.add(inst.seriesId);
              }
            });
          }
        });

        // Get unique service IDs from pending details
        const uniqueServiceIds = [...new Set(allPendingDetails.map(detail => detail.serviceId).filter(Boolean))];
        const uniquePackageIds = [...new Set(allPendingDetails.map(detail => detail.packageId).filter(Boolean))];
        const uniqueSeriesIds = Array.from(allSeriesIds);

        // Populate membership-specific dropdowns
        const membershipServicesData = [];
        const membershipPackagesData = [];
        const membershipPackageSeriesData = [];

        // Get service names from the main services list
        uniqueServiceIds.forEach(serviceId => {
          const service = services.find(s => s.value === serviceId);
          if (service) {
            membershipServicesData.push(service);
          }
        });

        // Get package names from the main packages list
        uniquePackageIds.forEach(packageId => {
          const pkg = packages.find(p => p.value === packageId);
          if (pkg) {
            membershipPackagesData.push(pkg);
          }
        });

        // Get package series names from the main package series list
        uniqueSeriesIds.forEach(seriesId => {
          const series = packageSeries.find(s => s.value === seriesId);
          if (series) {
            membershipPackageSeriesData.push(series);
          }
        });

        setMembershipServices(membershipServicesData);
        setMembershipPackages(membershipPackagesData);
        setMembershipPackageSeries(membershipPackageSeriesData);

      } else {
        // Customer has no membership
        setCustomerMembership(null);
        setAllCustomerMemberships([]);
        setHasMembership(false);
        setMembershipServices([]);
        setMembershipPackages([]);
        setMembershipPackageSeries([]);
      }

      // Set billable items status (memberships OR billed services)
      const hasMemberships = memberships && memberships.length > 0;
      setCustomerHasBillableItems(hasMemberships || hasBilledServices);
    } catch (error) {
      console.error('Error checking customer membership:', error);
      // On error, assume no membership
      setCustomerMembership(null);
      setAllCustomerMemberships([]);
      setHasMembership(false);
      setMembershipServices([]);
      setMembershipPackages([]);
      setMembershipPackageSeries([]);
      setBilledStandaloneServices([]);
      setCustomerHasBillableItems(false);
    }
  };

  // COMMENTED OUT per user request - availability checks not needed
  // Check availability for specific time slot
  // const checkTimeAvailability = async (resourceId, fromTime, toTime, bookingIdOverride = null) => {
  //   if (!form.booking_Date || !fromTime || !toTime) {
  //     return;
  //   }

  //   try {
  //     setCheckingTimeAvailability(true);
  //     
  //     const bookingDate = new Date(form.booking_Date);
  //     const bookingDateStr = formatLocalDate(bookingDate);
  //     const fromTimeStr = formatLocalDateTime(fromTime);
  //     const toTimeStr = formatLocalDateTime(toTime);

  //     // Use bookingIdOverride if provided, otherwise use form.booking_Id
  //     const bookingIdToUse = bookingIdOverride !== null ? bookingIdOverride : (form.booking_Id > 0 ? form.booking_Id : null);

  //     const timeData = {
  //       booking_Id: bookingIdToUse,
  //       booking_Date: bookingDateStr,
  //       from_Time: fromTimeStr,
  //       to_Time: toTimeStr
  //     };
  //     
  //     const results = await checkAvailabilityForTime(timeData);
  //     
  //     // Store results for this specific resource
  //     setTimeAvailabilityResults(prev => {
  //       const updated = {
  //         ...prev,
  //         [resourceId]: results
  //       };
  //       return updated;
  //     });
  //     
  //     // Increment refresh key to force Select components to refresh
  //     // Use setTimeout to ensure state update has propagated before updating refresh key
  //     setTimeout(() => {
  //       setAvailabilityRefreshKey(prev => {
  //         const newKey = prev + 1;
  //         return newKey;
  //       });
  //     }, 50);
  //     
  //   } catch (error) {
  //     // Don't show error to user as this is a background check
  //     // Don't show error to user as this is a background check
  //   } finally {
  //     setCheckingTimeAvailability(false);
  //   }
  // };

  // COMMENTED OUT per user request - availability checks not needed
  // Check availability for service resources
  // const checkAvailabilityForSelection = async () => {
  //   if (!form.booking_Date || serviceResources.length === 0) {
  //     return;
  //   }

  //   try {
  //     setCheckingAvailability(true);
  //     
  //     const bookingDate = new Date(form.booking_Date);
  //     const bookingDateStr = formatLocalDate(bookingDate);

  //     // Create service resources for availability check
  //     const serviceResourcesForCheck = serviceResources
  //       .filter(resource => resource.employeeId || resource.roomId) // Only include resources with employee or room
  //       .map(resource => ({
  //         employee_Id: resource.employeeId || null,
  //         room_Id: resource.roomId || null,
  //         from_Time: resource.fromTime ? formatLocalDateTime(resource.fromTime) : null,
  //         to_Time: resource.toTime ? formatLocalDateTime(resource.toTime) : null
  //       }));

  //     // Don't make API call if no valid resources
  //     if (serviceResourcesForCheck.length === 0) {
  //       setAvailabilityResults({});
  //       return;
  //     }

  //     const availabilityData = {
  //       booking_Id: form.booking_Id > 0 ? form.booking_Id : null,
  //       booking_Date: bookingDateStr,
  //       serviceResources: serviceResourcesForCheck
  //     };
  //     
  //     const results = await checkAvailability(availabilityData);
  //     
  //     // Convert results to a map for easy lookup
  //     const availabilityMap = {};
  //     results.forEach(result => {
  //       availabilityMap[`${result.type}_${result.id}`] = result;
  //     });
  //     
  //     setAvailabilityResults(availabilityMap);
  //   } catch (error) {
  //     // Don't show error to user as this is a background check
  //   } finally {
  //     setCheckingAvailability(false);
  //   }
  // };

  const loadBookings = async () => {
    try {
      setLoading(true);
      // Load customers in parallel with bookings for faster loading
      const customersPromise = loadCustomers();

      // Format dates properly to avoid timezone issues
      const formatDateForAPI = (date) => {
        if (!date) return null;
        // Create date in local timezone and format as YYYY-MM-DD
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      // Set default from_Date to today if no date filter is set and we're viewing current-future bookings
      // Only apply default if user hasn't manually set date filters
      let fromDate = filter.from_Date;
      let toDate = filter.to_Date;

      // Check if user has manually set date filters (by checking if filter was explicitly set)
      const hasManualDateFilter = filter.from_Date !== null || filter.to_Date !== null;

      if (!hasManualDateFilter) {
        if (bookingListSubTab === "current-future") {
          // Default: show only current and future bookings
          fromDate = new Date();
          fromDate.setHours(0, 0, 0, 0);
        } else if (bookingListSubTab === "past") {
          // For past bookings, set to_Date to yesterday and remove from_Date
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);
          yesterday.setHours(23, 59, 59, 999);
          toDate = yesterday;
          fromDate = null;
        }
      }

      const filterData = {
        ...filter,
        from_Date: formatDateForAPI(fromDate),
        to_Date: formatDateForAPI(toDate),
        business_Id: filter.business_Id || null,
        branch_Id: filter.branch_Id || null,
        page: page,
        pageSize: pageSize,
      };

      console.log('📤 loadBookings - Filter data being sent:', filterData);

      // For users without edit permission, filter by their employee ID only
      if (!hasEditOrHigher && currentEmployeeId) {
        filterData.employee_Id = currentEmployeeId;
        console.log('📤 loadBookings - Added employee filter for non-edit user:', currentEmployeeId);
      }

      console.log('📤 loadBookings - Calling getBookings API with:', filterData);
      const response = await getBookings(filterData);
      console.log('📥 loadBookings - API response received:', response);
      console.log('📥 loadBookings - Response type:', typeof response);
      console.log('📥 loadBookings - Is array?', Array.isArray(response));
      console.log('📥 loadBookings - Has bookings property?', response && response.bookings);

      // Handle paginated response from backend
      let bookingsData = [];
      let totalCountValue = 0;

      if (response && Array.isArray(response.bookings)) {
        // New paginated response format
        console.log('📥 loadBookings - Using paginated response format');
        bookingsData = response.bookings;
        totalCountValue = response.totalCount || bookingsData.length || 0;
        console.log('📥 loadBookings - Bookings count:', bookingsData.length, 'Total count:', totalCountValue);
      } else if (Array.isArray(response)) {
        // Fallback for non-paginated response (backward compatibility)
        console.log('📥 loadBookings - Using array response format (backward compatibility)');
        bookingsData = response;
        totalCountValue = response.length;
        console.log('📥 loadBookings - Bookings count:', bookingsData.length);
      } else if (response && Array.isArray(response.data)) {
        // Some backends wrap the array in a data property
        console.log('📥 loadBookings - Using response.data array format');
        bookingsData = response.data;
        totalCountValue = response.totalCount || bookingsData.length || 0;
        console.log('📥 loadBookings - Bookings count (data):', bookingsData.length, 'Total count:', totalCountValue);
      } else {
        console.error('📥 loadBookings - Unexpected response format:', response);
        bookingsData = [];
        totalCountValue = 0;
      }

      // Sort by booking_id in descending order (latest first)
      const sortedData = Array.isArray(bookingsData)
        ? bookingsData.sort((a, b) => (b.booking_Id || 0) - (a.booking_Id || 0))
        : [];
      console.log('📊 loadBookings - Sorted data count:', sortedData.length);

      // Set bookings directly (server-side pagination)
      setBookings(sortedData);
      setAllBookings(sortedData); // Keep for backward compatibility
      setTotalCount(totalCountValue);

      // Wait for customers to finish loading (they're loading in parallel)
      await customersPromise;

      console.log('✅ loadBookings - Successfully loaded bookings. Displaying:', sortedData.length, 'of', totalCountValue);
    } catch (error) {
      console.error("❌ loadBookings - Error loading bookings:", error);
      console.error("❌ loadBookings - Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        stack: error.stack
      });

      const errorMessage = error.response?.data?.message || error.message || "Failed to load bookings";
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = async (field, value) => {
    // CRITICAL: For selectedPackages, preserve bookingDetail_Id from existing form state
    // This must happen BEFORE setForm to ensure the enriched value is used
    let finalValue = value;
    if (field === 'selectedPackages' && value && value.length > 0) {
      finalValue = value.map(newPkg => {
        const existingPkg = form.selectedPackages.find(
          ep => ep.value === newPkg.value ||
            parseInt(ep.value) === parseInt(newPkg.value) ||
            ep.value === parseInt(newPkg.value) ||
            parseInt(ep.value) === newPkg.value
        );
        if (existingPkg && (existingPkg.bookingDetail_Id || existingPkg.bookingDetailId)) {
          return {
            ...newPkg,
            bookingDetail_Id: existingPkg.bookingDetail_Id || existingPkg.bookingDetailId,
            is_Cancelled: existingPkg.is_Cancelled,
            cancel_Reason: existingPkg.cancel_Reason
          };
        }
        return newPkg;
      });
    }

    setForm(prev => ({
      ...prev,
      [field]: finalValue
    }));

    // Check customer membership when customer is selected
    if (field === 'customer_Id') {
      if (value) {
        checkCustomerMembership(value);
      } else {
        // Clear form when customer is cleared
        setServiceResources([]);
        setForm(prev => ({
          ...prev,
          selectedServices: [],
          selectedPackages: [],
          selectedPackageSeries: []
        }));
        // Reset membership data
        setCustomerMembership(null);
        setAllCustomerMemberships([]);
        setHasMembership(false);
        setMembershipServices([]);
        setMembershipPackages([]);
        setMembershipPackageSeries([]);
        // Clear consent data when customer is cleared (but keep employee name)
        setConsentData({
          witnessName: loggedInEmployeeName,
          witnessSignature: '',
          guestSignature: ''
        });
      }
    }

    // Handle package selection - fetch services from selected packages
    if (field === 'selectedPackages') {
      if (finalValue && finalValue.length > 0) {
        const packageIds = finalValue.map(pkg => pkg.value);
        const packageServices = await fetchPackageServices(packageIds);

        // Update service resources with new package services
        setServiceResources(prev => {
          // Remove existing non-membership package services
          const filtered = prev.filter(resource => !(resource.packageId && !resource.membershipId));

          // Expand services based on quantity - create multiple entries for services with quantity > 1
          const expandedServices = [];
          packageServices.forEach(service => {
            const quantity = service.quantity || 1;
            for (let q = 0; q < quantity; q++) {
              expandedServices.push({
                ...service,
                quantityIndex: q // Track which instance this is (0, 1, 2, etc.)
              });
            }
          });

          // Add new package services with unique time slots
          const bookingDate = new Date(form.booking_Date);
          let latestEndTime = new Date(bookingDate);
          latestEndTime.setHours(9, 0, 0, 0);

          // Find the latest end time from existing resources to ensure sequential timing
          if (filtered.length > 0) {
            filtered.forEach(resource => {
              if (resource.toTime) {
                const resourceToTime = new Date(resource.toTime);
                if (resourceToTime > latestEndTime) {
                  latestEndTime = resourceToTime;
                }
              }
            });
          }

          const newResources = expandedServices.map((service, index) => {
            // Calculate start time for each service instance
            let fromTime = new Date(latestEndTime);

            // Add duration of previous services in this batch
            if (index > 0) {
              for (let i = 0; i < index; i++) {
                const prevSrv = expandedServices[i];
                const prevDur = prevSrv.duration || 30;
                fromTime = new Date(fromTime.getTime() + (prevDur * 60000));
              }
            }

            const duration = service.duration || 30;
            const toTime = new Date(fromTime.getTime() + (duration * 60000));

            return {
              id: `pkg-${service.packageId}-${service.serviceId}-${Date.now()}-${Math.random()}-${index}-${service.quantityIndex}`,
              serviceId: service.serviceId,
              serviceName: service.label,
              packageId: service.packageId,
              packageName: service.packageName,
              employeeId: null,
              roomId: null,
              equipmentId: null,
              fromTime: fromTime,
              toTime: toTime
            };
          });
          return [...filtered, ...newResources];
        });
      } else {
        // Remove non-membership package services when no packages selected
        setServiceResources(prev => prev.filter(resource => !(resource.packageId && !resource.membershipId)));
      }
    }

    // Handle individual service selection
    if (field === 'selectedServices') {
      if (value && value.length > 0) {
        // Update service resources with individual services
        setServiceResources(prev => {
          // Get existing non-membership individual service resources
          const existingIndividualResources = prev.filter(resource => !resource.packageId && !resource.membershipId);

          // Get service IDs from the new selection
          const selectedServiceIds = value.map(service => service.value);

          // Keep existing resources that are still in the selection (preserve their employee/room/equipment values)
          const preservedResources = existingIndividualResources.filter(resource =>
            selectedServiceIds.includes(resource.serviceId)
          );

          // Get service IDs that already have resources
          const existingServiceIds = preservedResources.map(resource => resource.serviceId);

          // Find services that need new resources
          const newServices = value.filter(service => !existingServiceIds.includes(service.value));

          // Create new resources only for services that don't already exist
          const bookingDate = new Date(form.booking_Date);
          let latestEndTime = new Date(bookingDate);
          latestEndTime.setHours(9, 0, 0, 0);

          // Find the latest end time from all existing resources to ensure sequential timing
          const existingResources = [...otherResources, ...preservedResources];
          if (existingResources.length > 0) {
            existingResources.forEach(resource => {
              if (resource.toTime) {
                const resourceToTime = new Date(resource.toTime);
                if (resourceToTime > latestEndTime) {
                  latestEndTime = resourceToTime;
                }
              }
            });
          }

          const newResources = newServices.map((service, index) => {
            let fromTime = new Date(latestEndTime);

            // Add duration of previous new services in this batch
            if (index > 0) {
              for (let i = 0; i < index; i++) {
                const prevSrv = newServices[i];
                const prevDur = prevSrv.duration || 30;
                fromTime = new Date(fromTime.getTime() + (prevDur * 60000));
              }
            }

            const duration = service.duration || 30;
            const toTime = new Date(fromTime.getTime() + (duration * 60000));

            return {
              id: `srv-${service.value}-${Date.now()}-${Math.random()}-${index}`,
              serviceId: service.value,
              serviceName: service.label,
              packageId: null,
              packageName: null,
              employeeId: null,
              roomId: null,
              equipmentId: null,
              fromTime: fromTime,
              toTime: toTime
            };
          });

          // Keep all non-individual resources (packages, memberships) and preserved individual resources, then add new ones
          const otherResources = prev.filter(resource => resource.packageId || resource.membershipId);
          return [...otherResources, ...preservedResources, ...newResources];
        });
      } else {
        // Remove non-membership individual services when no services selected
        setServiceResources(prev => prev.filter(resource => !(!resource.packageId && !resource.membershipId)));
      }
    }

    // COMMENTED OUT per user request - availability checks not needed
    // Check availability when booking date changes
    // if (field === 'booking_Date') {
    //   // Use setTimeout to avoid too many API calls while user is typing
    //   setTimeout(() => {
    //     // Trigger time-based availability checks for each resource
    //     serviceResources.forEach(resource => {
    //       if (resource.fromTime && resource.toTime) {
    //         checkTimeAvailability(resource.id, resource.fromTime, resource.toTime);
    //       }
    //     });
    //     // Also trigger bulk availability check
    //     checkAvailabilityForSelection();
    //   }, 500);
    // }
  };

  const handleFilterChange = (field, value) => {
    setFilter(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      // Reset branch when business changes
      if (field === 'business_Id') {
        updated.branch_Id = null;
        setSelectedBranchFilterOption(null);
      }
      return updated;
    });

    // Update selected options for business/branch
    if (field === 'business_Id') {
      setSelectedBusinessFilterOption(
        value ? filterBusinessOptions.find(opt => opt.value === value) : null
      );
    } else if (field === 'branch_Id') {
      setSelectedBranchFilterOption(
        value ? filterBranchOptions.find(opt => opt.value === value) : null
      );
    }
  };

  // Handle resource allocation changes for services
  const handleResourceChange = (resourceId, field, value) => {
    let updatedResource = null;

    setServiceResources(prev => {
      const updated = prev.map(resource => {
        if (resource.id === resourceId) {
          updatedResource = { ...resource, [field]: value || null };
          return updatedResource;
        }
        return resource;
      });
      return updated;
    });

    // COMMENTED OUT per user request - availability checks not needed
    // Check time-based availability when time changes
    // if (['fromTime', 'toTime'].includes(field) && updatedResource) {
    //   // Use the updated resource directly instead of reading from state
    //   const fromTime = field === 'fromTime' ? value : updatedResource.fromTime;
    //   const toTime = field === 'toTime' ? value : updatedResource.toTime;
    //   
    //   if (fromTime && toTime) {
    //     setCheckingTimeAvailability(true);
    //     setTimeout(() => {
    //       checkTimeAvailability(resourceId, fromTime, toTime).finally(() => {
    //         setCheckingTimeAvailability(false);
    //         // Also trigger bulk availability check to refresh all dropdowns
    //         checkAvailabilityForSelection();
    //       });
    //     }, 500);
    //   }
    // }
    // 
    // // Check availability when employee or room changes
    // if (['employeeId', 'roomId'].includes(field)) {
    //   setTimeout(() => {
    //     checkAvailabilityForSelection();
    //   }, 500);
    // }
  };

  const handleFromTimeChange = (resourceId, newFromTime) => {
    if (!newFromTime) return;

    setServiceResources(prev => {
      const index = prev.findIndex(r => r.id === resourceId);
      if (index === -1) return prev;

      const updated = [...prev];
      let currentTime = new Date(newFromTime);

      for (let i = index; i < updated.length; i++) {
        const resource = updated[i];
        // Skip cancelled services in the cascade
        if (resource.isCancelled || resource.completionStatus === 'Cancel') continue;

        const service = services.find(s => s.value === resource.serviceId);
        const duration = service?.duration || 30;

        updated[i] = {
          ...resource,
          fromTime: new Date(currentTime),
          toTime: new Date(currentTime.getTime() + (duration * 60000))
        };

        // Next resource starts after this one finishes
        currentTime = new Date(updated[i].toTime);
      }

      // Sort the entire list to keep it chronological
      updated.sort((a, b) => {
        if (!a.fromTime) return 1;
        if (!b.fromTime) return -1;
        return a.fromTime.getTime() - b.fromTime.getTime();
      });

      return updated;
    });
  };

  // COMMENTED OUT per user request - availability checks not needed
  // Helper function to format options with availability indicators
  // Now returns plain options without availability indicators
  const formatOptionsWithAvailability = (options, type, resourceId) => {
    // Return plain options without availability checks
    return options;

    // OLD CODE - COMMENTED OUT
    // const timeAvailability = timeAvailabilityResults[resourceId];
    // 
    // const formattedOptions = options.map(option => {
    //   let isAvailable = true;
    //   let availabilityInfo = null;
    //   
    //   if (timeAvailability) {
    //     if (type === 'Employee') {
    //       const employee = timeAvailability.availableEmployees?.find(emp => emp.employee_Id === option.value);
    //       isAvailable = employee ? employee.isAvailable : true;
    //       availabilityInfo = employee;
    //     } else if (type === 'Room') {
    //       const room = timeAvailability.availableRooms?.find(room => room.room_Id === option.value);
    //       isAvailable = room ? room.isAvailable : true;
    //       availabilityInfo = room;
    //     }
    //   }
    //   
    //   return {
    //     ...option,
    //     isDisabled: !isAvailable, // Disable unavailable options
    //     label: (
    //       <div className="d-flex align-items-center">
    //         <span className={`me-2 ${isAvailable ? 'text-success' : 'text-danger'}`}>
    //           {isAvailable ? '✓' : '✗'}
    //         </span>
    //         <span>{option.label}</span>
    //       </div>
    //     )
    //   };
    // });
    // 
    // return formattedOptions;
  };

  // Handle marking a service as done
  const handleMarkServiceDone = async (resource) => {
    console.log('🔵 handleMarkServiceDone - Starting', {
      resourceId: resource.id,
      serviceName: resource.serviceName,
      bookingResourceDetail_Id: resource.bookingResourceDetail_Id,
      employeeId: resource.employeeId,
      isEditing,
      booking_Id: form.booking_Id
    });

    // Check if employee is selected
    if (!resource.employeeId) {
      console.log('⚠️ handleMarkServiceDone - No employee selected');
      Swal.fire("Warning", "Please select an employee for this service before marking it as done", "warning");
      return;
    }

    // Only allow marking done for existing services (those with bookingResourceDetail_Id)
    if (!resource.bookingResourceDetail_Id) {
      console.log('⚠️ handleMarkServiceDone - No bookingResourceDetail_Id');
      Swal.fire("Warning", "Please save the booking first before marking services as done", "warning");
      return;
    }

    try {
      let updatedResourceDetailId = resource.bookingResourceDetail_Id;

      // If we're in edit mode and employee was just selected, save the booking first
      // to ensure the employee assignment is saved in the database
      if (isEditing && form.booking_Id) {
        console.log('💾 handleMarkServiceDone - Saving booking first to persist employee assignment');
        // Check if we need to save the booking first by attempting to save
        // This ensures the employee assignment is persisted before marking as done
        try {
          setLoading(true);

          // Get current user ID from localStorage
          const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
          const currentUserId = authUser.userId || authUser.user_Id || 1;

          // Prepare booking details for selected items (same logic as handleSubmit)
          const details = [];
          const resourceDetails = [];
          let detailIndex = 1;

          // Group services by type (individual, package, membership)
          const individualServices = serviceResources.filter(r => !r.packageId && !r.membershipId);
          const packageServices = serviceResources.filter(r => r.packageId && !r.membershipId);
          const membershipServices = serviceResources.filter(r => r.membershipId);

          // Get unique packages from package services
          const uniquePackages = [...new Set(packageServices.map(r => r.packageId))];

          // Get unique memberships from membership services
          const uniqueMemberships = [...new Set(membershipServices.map(r => r.membershipId))];

          // Add individual services to details
          if (individualServices.length > 0) {
            individualServices.forEach(service => {
              const detailId = detailIndex++;
              const detailObj = {
                tempDetailId: detailId,
                entity_Id: service.serviceId,
                entity_Type: 'ser',
                is_Cancelled: false,
                cancel_Reason: null,
                created_By: currentUserId,
                created_On: new Date().toISOString(),
                modified_By: currentUserId,
                modified_On: new Date().toISOString(),
              };
              if (service.bookingDetail_Id && service.bookingDetail_Id > 0) {
                detailObj.bookingDetail_Id = service.bookingDetail_Id;
              }
              details.push(detailObj);

              const bookingDate = new Date(form.booking_Date);
              const fromTime = service.fromTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(),
                service.fromTime.getHours(), service.fromTime.getMinutes(), service.fromTime.getSeconds()) : null;
              const toTime = service.toTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(),
                service.toTime.getHours(), service.toTime.getMinutes(), service.toTime.getSeconds()) : null;

              const resourceDetailObj = {
                tempDetailId: detailId,
                service_Id: service.serviceId,
                package_Id: null,
                membership_Id: null,
                room_Id: service.roomId,
                equipment_Id: service.equipmentId,
                employee_Id: service.employeeId,
                from_Time: fromTime ? formatLocalDateTime(fromTime) : null,
                to_Time: toTime ? formatLocalDateTime(toTime) : null,
                modified_By: currentUserId,
                modified_On: new Date().toISOString(),
                completionStatus: service.isCancelled ? 'Cancel' : (service.completionStatus || null),
                cancellation_Reason: service.isCancelled ? (service.cancellationReason || null) : null,
              };
              if (service.bookingResourceDetail_Id && service.bookingResourceDetail_Id > 0) {
                resourceDetailObj.bookingResourceDetail_Id = service.bookingResourceDetail_Id;
              }
              if (service.bookingDetail_Id && service.bookingDetail_Id > 0) {
                resourceDetailObj.bookingDetail_Id = service.bookingDetail_Id;
              }
              resourceDetails.push(resourceDetailObj);
            });
          }

          // Add packages to details
          // CRITICAL FIX: Group by bookingDetail_Id first to preserve multiple package instances
          // If multiple instances of the same package exist, they have different bookingDetail_Id values
          if (uniquePackages.length > 0) {
            // CRITICAL: First, create instances based on selectedPackages (authoritative source)
            // selectedPackages has one entry per package instance with bookingDetail_Id
            const packageInstances = new Map();

            // Step 1: Initialize instances from selectedPackages (if editing)
            // This ensures we have the correct number of instances even if services don't have bookingDetail_Id
            if (form.selectedPackages && form.selectedPackages.length > 0) {
              form.selectedPackages.forEach(selectedPkg => {
                const packageId = selectedPkg.value;
                const bookingDetailId = selectedPkg.bookingDetail_Id || selectedPkg.bookingDetailId;

                if (bookingDetailId && bookingDetailId > 0) {
                  const instanceKey = `detail_${bookingDetailId}`;
                  if (!packageInstances.has(instanceKey)) {
                    packageInstances.set(instanceKey, {
                      packageId: packageId,
                      bookingDetailId: parseInt(bookingDetailId),
                      services: []
                    });
                  }
                }
              });
            }

            // Step 2: Assign services to instances based on bookingDetail_Id
            packageServices.forEach(service => {
              const packageId = service.packageId;
              // CRITICAL: Get bookingDetail_Id from service resource (preserved from handleEdit)
              // This is the authoritative source - each service resource knows which detail it belongs to
              // IMPORTANT: Parse as integer and validate it's a positive number
              let bookingDetailId = null;
              if (service.bookingDetail_Id) {
                const parsed = parseInt(service.bookingDetail_Id);
                if (!isNaN(parsed) && parsed > 0) {
                  bookingDetailId = parsed;
                }
              }

              // Find or create the appropriate instance
              let instanceKey = null;
              let instance = null;

              if (bookingDetailId) {
                // Service has bookingDetail_Id - use it to find/create instance
                instanceKey = `detail_${bookingDetailId}`;
                if (!packageInstances.has(instanceKey)) {
                  packageInstances.set(instanceKey, {
                    packageId: packageId,
                    bookingDetailId: bookingDetailId,
                    services: []
                  });
                }
                instance = packageInstances.get(instanceKey);
              } else {
                // Service doesn't have bookingDetail_Id - try to find an instance without one for this packageId
                // Or create a new one if none exists
                instanceKey = `package_${packageId}_new_${service.id || service.bookingResourceDetail_Id || Date.now()}_${Math.random()}`;
                if (!packageInstances.has(instanceKey)) {
                  packageInstances.set(instanceKey, {
                    packageId: packageId,
                    bookingDetailId: null,
                    services: []
                  });
                }
                instance = packageInstances.get(instanceKey);
              }

              instance.services.push(service);
            });

            // Debug: Log all package instances found with detailed service info
            console.log('📦 Package Instances Found:', Array.from(packageInstances.entries()).map(([key, inst]) => ({
              key,
              packageId: inst.packageId,
              bookingDetailId: inst.bookingDetailId,
              serviceCount: inst.services.length,
              services: inst.services.map(s => ({
                id: s.id,
                serviceId: s.serviceId,
                bookingDetail_Id: s.bookingDetail_Id,
                bookingResourceDetail_Id: s.bookingResourceDetail_Id
              }))
            })));

            // CRITICAL: Verify we have the correct number of instances
            // If we have 2 package instances in selectedPackages, we should have 2 instances here
            const expectedPackageInstances = form.selectedPackages?.length || 0;
            const actualPackageInstances = packageInstances.size;
            if (expectedPackageInstances > 0 && actualPackageInstances !== expectedPackageInstances) {
              console.warn(`⚠️ Package Instance Count Mismatch! Expected: ${expectedPackageInstances}, Found: ${actualPackageInstances}`);
              console.warn('selectedPackages:', form.selectedPackages.map(p => ({ value: p.value, bookingDetail_Id: p.bookingDetail_Id })));
            }

            // Create one detail entry per package instance
            packageInstances.forEach((instance, instanceKey) => {
              const detailId = detailIndex++;
              const packageId = instance.packageId;
              // CRITICAL: Get bookingDetailId from instance (set during grouping)
              // Try multiple fallbacks to ensure we get the correct bookingDetail_Id
              let bookingDetailId = instance.bookingDetailId;

              // Fallback 1: Get from first service that has bookingDetail_Id
              if (!bookingDetailId || bookingDetailId <= 0) {
                const serviceWithId = instance.services.find(s => s.bookingDetail_Id && s.bookingDetail_Id > 0);
                if (serviceWithId) {
                  bookingDetailId = parseInt(serviceWithId.bookingDetail_Id);
                }
              }

              // Fallback 2: If still not found, try to match from selectedPackages
              // This handles cases where services don't have bookingDetail_Id but selectedPackages does
              if ((!bookingDetailId || bookingDetailId <= 0) && form.selectedPackages && form.selectedPackages.length > 0) {
                // Find a selectedPackage that matches this packageId and hasn't been used yet
                // We need to track which bookingDetailIds have been used
                const usedDetailIds = new Set();
                packageInstances.forEach((otherInst, otherKey) => {
                  if (otherInst.bookingDetailId && otherInst.bookingDetailId > 0) {
                    usedDetailIds.add(otherInst.bookingDetailId);
                  }
                });

                // Find an unused bookingDetail_Id from selectedPackages for this packageId
                const matchingPackage = form.selectedPackages.find(p =>
                  (p.value === packageId || parseInt(p.value) === parseInt(packageId)) &&
                  p.bookingDetail_Id &&
                  p.bookingDetail_Id > 0 &&
                  !usedDetailIds.has(p.bookingDetail_Id)
                );

                if (matchingPackage) {
                  bookingDetailId = parseInt(matchingPackage.bookingDetail_Id);
                  // Update the instance so it's marked as used
                  instance.bookingDetailId = bookingDetailId;
                }
              }

              // Debug logging to track package instances
              console.log(`📦 Package Instance: packageId=${packageId}, bookingDetailId=${bookingDetailId}, services=${instance.services.length}`, {
                serviceBookingDetailIds: instance.services.map(s => s.bookingDetail_Id),
                instanceKey: instanceKey
              });

              const packageDetailObj = {
                tempDetailId: detailId,
                entity_Id: packageId,
                entity_Type: 'pac',
                is_Cancelled: false,
                cancel_Reason: null,
                created_By: currentUserId,
                created_On: new Date().toISOString(),
                modified_By: currentUserId,
                modified_On: new Date().toISOString(),
              };

              // Only add bookingDetail_Id if it's a valid positive integer
              if (bookingDetailId && !isNaN(bookingDetailId) && bookingDetailId > 0) {
                packageDetailObj.bookingDetail_Id = bookingDetailId;
                console.log(`✅ Set bookingDetail_Id=${bookingDetailId} for package instance ${packageId}`);
              } else {
                console.warn(`⚠️ No valid bookingDetail_Id for package instance ${packageId}`, {
                  services: instance.services.map(s => ({
                    id: s.id,
                    bookingDetail_Id: s.bookingDetail_Id,
                    bookingResourceDetail_Id: s.bookingResourceDetail_Id
                  })),
                  selectedPackages: form.selectedPackages.filter(p => p.value === packageId || parseInt(p.value) === parseInt(packageId))
                });
              }

              details.push(packageDetailObj);

              const servicesInPackage = instance.services;

              // Deduplicate services to prevent exact duplicates (same serviceId, times, employee, room)
              // This allows multiple instances of the same service with different times (for quantity > 1)
              // but prevents true duplicates that might occur due to race conditions or double processing
              // NOTE: We do NOT use resource ID in the key, as that would allow duplicates with same times
              const seenServiceKeys = new Set();
              const uniqueServicesInPackage = servicesInPackage.filter(service => {
                const bookingDate = new Date(form.booking_Date);
                const fromTime = service.fromTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(),
                  service.fromTime.getHours(), service.fromTime.getMinutes(), service.fromTime.getSeconds()) : null;
                const toTime = service.toTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(),
                  service.toTime.getHours(), service.toTime.getMinutes(), service.toTime.getSeconds()) : null;

                // Create a unique key WITHOUT resource ID - this ensures true duplicates are caught
                // Key based on: serviceId, packageId, fromTime, toTime, employeeId, roomId
                // This allows multiple instances with DIFFERENT times (for quantity > 1) but prevents exact duplicates
                const key = `${service.serviceId}_${packageId}_${fromTime ? fromTime.getTime() : 'null'}_${toTime ? toTime.getTime() : 'null'}_${service.employeeId || 'null'}_${service.roomId || 'null'}`;

                if (seenServiceKeys.has(key)) {
                  // Duplicate service resource filtered
                  return false;
                }
                seenServiceKeys.add(key);
                return true;
              });


              uniqueServicesInPackage.forEach(service => {
                const bookingDate = new Date(form.booking_Date);
                const fromTime = service.fromTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(),
                  service.fromTime.getHours(), service.fromTime.getMinutes(), service.fromTime.getSeconds()) : null;
                const toTime = service.toTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(),
                  service.toTime.getHours(), service.toTime.getMinutes(), service.toTime.getSeconds()) : null;

                const packageResourceDetailObj = {
                  tempDetailId: detailId,
                  service_Id: service.serviceId,
                  package_Id: packageId,
                  membership_Id: null,
                  room_Id: service.roomId,
                  equipment_Id: service.equipmentId,
                  employee_Id: service.employeeId,
                  from_Time: formatLocalDateTime(fromTime),
                  to_Time: formatLocalDateTime(toTime),
                  modified_By: currentUserId,
                  modified_On: new Date().toISOString(),
                  completionStatus: service.isCancelled ? 'Cancel' : (service.completionStatus || null),
                  cancellation_Reason: service.isCancelled ? (service.cancellationReason || null) : null,
                };
                if (service.bookingResourceDetail_Id && service.bookingResourceDetail_Id > 0) {
                  packageResourceDetailObj.bookingResourceDetail_Id = service.bookingResourceDetail_Id;
                }
                if (packageDetailObj.bookingDetail_Id && packageDetailObj.bookingDetail_Id > 0) {
                  packageResourceDetailObj.bookingDetail_Id = packageDetailObj.bookingDetail_Id;
                }
                resourceDetails.push(packageResourceDetailObj);
              });
            });
          }

          // Add memberships to details
          if (uniqueMemberships.length > 0) {
            uniqueMemberships.forEach(membershipId => {
              const detailId = detailIndex++;
              const membershipDetailObj = {
                tempDetailId: detailId,
                entity_Id: membershipId,
                entity_Type: 'mem',
                is_Cancelled: false,
                cancel_Reason: null,
                created_By: currentUserId,
                created_On: new Date().toISOString(),
                modified_By: currentUserId,
                modified_On: new Date().toISOString(),
              };
              // FIX: Get bookingDetail_Id from form.selectedPackageSeries (which has it from handleEdit)
              // instead of looking in the details array which is being built
              if (membershipId && membershipId > 0) {
                // Check both selectedPackageSeries (for membership series) and serviceResources (for direct membership services)
                const selectedSeries = form.selectedPackageSeries.find(s => s.value === membershipId || s.membershipId === membershipId);
                if (selectedSeries && selectedSeries.bookingDetail_Id && selectedSeries.bookingDetail_Id > 0) {
                  membershipDetailObj.bookingDetail_Id = selectedSeries.bookingDetail_Id;
                } else {
                  // Fallback: check if any serviceResource has this membershipId with bookingDetail_Id
                  const membershipService = membershipServices.find(s => s.membershipId === membershipId);
                  if (membershipService && membershipService.bookingDetail_Id && membershipService.bookingDetail_Id > 0) {
                    membershipDetailObj.bookingDetail_Id = membershipService.bookingDetail_Id;
                  }
                }
              }
              details.push(membershipDetailObj);

              const servicesInMembership = membershipServices.filter(r => r.membershipId === membershipId);
              servicesInMembership.forEach(service => {
                const bookingDate = new Date(form.booking_Date);
                const fromTime = service.fromTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(),
                  service.fromTime.getHours(), service.fromTime.getMinutes(), service.fromTime.getSeconds()) : null;
                const toTime = service.toTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(),
                  service.toTime.getHours(), service.toTime.getMinutes(), service.toTime.getSeconds()) : null;

                const membershipResourceDetailObj = {
                  tempDetailId: detailId,
                  service_Id: service.serviceId,
                  package_Id: service.packageId,
                  membership_Id: membershipId,
                  room_Id: service.roomId,
                  equipment_Id: service.equipmentId,
                  employee_Id: service.employeeId,
                  from_Time: formatLocalDateTime(fromTime),
                  to_Time: formatLocalDateTime(toTime),
                  modified_By: currentUserId,
                  modified_On: new Date().toISOString(),
                  completionStatus: service.isCancelled ? 'Cancel' : (service.completionStatus || null),
                  cancellation_Reason: service.isCancelled ? (service.cancellationReason || null) : null,
                };
                if (service.bookingResourceDetail_Id && service.bookingResourceDetail_Id > 0) {
                  membershipResourceDetailObj.bookingResourceDetail_Id = service.bookingResourceDetail_Id;
                }
                if (membershipDetailObj.bookingDetail_Id && membershipDetailObj.bookingDetail_Id > 0) {
                  membershipResourceDetailObj.bookingDetail_Id = membershipDetailObj.bookingDetail_Id;
                }
                resourceDetails.push(membershipResourceDetailObj);
              });
            });
          }

          // form.source_Id contains Source_Id (number) from the dropdown
          // form.sourceOther contains additional details (optional - not stored in booking table)

          // Final deduplication pass on resourceDetails before sending to API
          // This is a safety net to catch any duplicates that might have slipped through
          const finalDeduplicationKey = new Set();
          const deduplicatedResourceDetails = [];
          resourceDetails.forEach(rd => {
            // Create a key based on all identifying fields.
            // IMPORTANT: include BookingDetail context so multiple instances of the same package/service
            // (e.g. same package booked twice) don't get incorrectly deduplicated.
            const detailCtx = rd.bookingDetail_Id || rd.tempDetailId || 'null';
            const key = `${detailCtx}_${rd.service_Id}_${rd.package_Id || 'null'}_${rd.membership_Id || 'null'}_${rd.from_Time || 'null'}_${rd.to_Time || 'null'}_${rd.employee_Id || 'null'}_${rd.room_Id || 'null'}_${rd.equipment_Id || 'null'}`;

            if (finalDeduplicationKey.has(key)) {
              console.warn('🚨 Final deduplication: Duplicate resource detail filtered:', {
                detailCtx,
                serviceId: rd.service_Id,
                packageId: rd.package_Id,
                fromTime: rd.from_Time,
                toTime: rd.to_Time,
                key: key
              });
              return; // Skip this duplicate
            }
            finalDeduplicationKey.add(key);
            deduplicatedResourceDetails.push(rd);
          });

          // Final deduplication completed


          // Calculate the earliest from time and latest to time from all services
          const bookingDate = new Date(form.booking_Date);
          const allFromTimes = deduplicatedResourceDetails.map(rd => rd.from_Time ? new Date(rd.from_Time) : null).filter(d => d !== null);
          const allToTimes = deduplicatedResourceDetails.map(rd => rd.to_Time ? new Date(rd.to_Time) : null).filter(d => d !== null);

          const earliestFromTime = allFromTimes.length > 0 ? new Date(Math.min(...allFromTimes)) : bookingDate;
          const latestToTime = allToTimes.length > 0 ? new Date(Math.max(...allToTimes)) : bookingDate;

          // Prepare booking data
          const bookingData = {
            ...form,
            booking_Date: formatLocalDate(new Date(form.booking_Date)),
            from_Time: formatLocalDateTime(earliestFromTime),
            to_Time: formatLocalDateTime(latestToTime),
            entry_Date: formatLocalDateTime(new Date()),
            source_Id: form.source_Id || null,
            modified_By: currentUserId,
            modified_On: formatLocalDateTime(new Date()),
            details: details,
            resourceDetails: deduplicatedResourceDetails,
          };

          // Save the booking to persist employee assignment
          console.log('💾 handleMarkServiceDone - Calling saveBooking API', {
            booking_Id: bookingData.booking_Id,
            resourceDetailsCount: resourceDetails.length
          });

          // Debug logging: Show details being sent
          console.log('📋 Booking Payload Summary:', {
            booking_Id: bookingData.booking_Id,
            detailsCount: bookingData.details?.length || 0,
            details: bookingData.details?.map(d => ({
              tempDetailId: d.tempDetailId,
              bookingDetail_Id: d.bookingDetail_Id,
              entity_Id: d.entity_Id,
              entity_Type: d.entity_Type
            })),
            resourceDetailsCount: bookingData.resourceDetails?.length || 0
          });

          const saveResult = await saveBooking(bookingData);
          console.log('💾 handleMarkServiceDone - Save result:', saveResult);

          if (!saveResult.id) {
            console.error('❌ handleMarkServiceDone - Save failed:', saveResult);
            Swal.fire("Error", "Failed to save employee assignment. Please try again.", "error");
            setLoading(false);
            return;
          }

          // After saving, the booking resource details are recreated with new IDs
          // We need to reload the booking to get the updated resource detail IDs
          console.log('🔄 handleMarkServiceDone - Reloading booking to get updated resource detail IDs');
          try {
            const updatedBookingsResponse = await getBookings({ booking_Id: form.booking_Id });
            console.log('🔄 handleMarkServiceDone - Reloaded bookings response:', updatedBookingsResponse);

            // Handle both array and object response formats
            let updatedBookings = Array.isArray(updatedBookingsResponse)
              ? updatedBookingsResponse
              : (updatedBookingsResponse?.bookings || []);

            console.log('🔄 handleMarkServiceDone - Processed bookings array:', updatedBookings);
            console.log('🔄 handleMarkServiceDone - Resource to match:', {
              serviceId: resource.serviceId,
              packageId: resource.packageId,
              membershipId: resource.membershipId,
              fromTime: resource.fromTime,
              employeeId: resource.employeeId
            });

            if (updatedBookings && updatedBookings.length > 0) {
              const updatedBooking = updatedBookings[0];
              console.log('🔄 handleMarkServiceDone - Updated booking details:', updatedBooking.details);

              // Find the matching resource detail by service/package/membership details
              let foundResourceDetail = null;
              if (updatedBooking.details && Array.isArray(updatedBooking.details)) {
                for (const detail of updatedBooking.details) {
                  if (detail.resourceDetails && Array.isArray(detail.resourceDetails)) {
                    console.log('🔄 handleMarkServiceDone - Checking resource details:', detail.resourceDetails);
                    for (const resDetail of detail.resourceDetails) {
                      // Match by service ID, package ID, membership ID, and times
                      const matchesService = resDetail.service_Id === resource.serviceId;
                      const matchesPackage = (resDetail.package_Id || null) === (resource.packageId || null);
                      const matchesMembership = (resDetail.membership_Id || null) === (resource.membershipId || null);

                      console.log('🔄 handleMarkServiceDone - Matching check:', {
                        resDetail: {
                          service_Id: resDetail.service_Id,
                          package_Id: resDetail.package_Id,
                          membership_Id: resDetail.membership_Id,
                          from_Time: resDetail.from_Time,
                          bookingResourceDetail_Id: resDetail.bookingResourceDetail_Id
                        },
                        resource: {
                          serviceId: resource.serviceId,
                          packageId: resource.packageId,
                          membershipId: resource.membershipId,
                          fromTime: resource.fromTime
                        },
                        matchesService,
                        matchesPackage,
                        matchesMembership
                      });

                      // Try to match by time if available
                      let matchesTime = true;
                      if (resource.fromTime && resDetail.from_Time) {
                        const resourceTime = new Date(resource.fromTime).getTime();
                        const detailTime = new Date(resDetail.from_Time).getTime();
                        // Allow 1 minute difference for timezone/rounding issues
                        matchesTime = Math.abs(resourceTime - detailTime) < 60000;
                        console.log('🔄 handleMarkServiceDone - Time match:', {
                          resourceTime: new Date(resource.fromTime).toISOString(),
                          detailTime: new Date(resDetail.from_Time).toISOString(),
                          resourceTimeMs: resourceTime,
                          detailTimeMs: detailTime,
                          diff: Math.abs(resourceTime - detailTime),
                          matchesTime
                        });
                      }

                      if (matchesService && matchesPackage && matchesMembership && matchesTime) {
                        foundResourceDetail = resDetail;
                        console.log('✅ handleMarkServiceDone - Found matching resource detail:', {
                          oldId: resource.bookingResourceDetail_Id,
                          newId: foundResourceDetail.bookingResourceDetail_Id,
                          serviceId: foundResourceDetail.service_Id,
                          employeeId: foundResourceDetail.employee_Id
                        });
                        break;
                      }
                    }
                    if (foundResourceDetail) break;
                  }
                }
              }

              if (foundResourceDetail && foundResourceDetail.bookingResourceDetail_Id) {
                updatedResourceDetailId = foundResourceDetail.bookingResourceDetail_Id;
                console.log('✅ handleMarkServiceDone - Updated resource detail ID:', {
                  old: resource.bookingResourceDetail_Id,
                  new: updatedResourceDetailId
                });

                // Update the local state with the new ID
                setServiceResources(prev =>
                  prev.map(r =>
                    r.id === resource.id
                      ? { ...r, bookingResourceDetail_Id: updatedResourceDetailId }
                      : r
                  )
                );
              } else {
                console.warn('⚠️ handleMarkServiceDone - Could not find matching resource detail after save');
                console.warn('⚠️ handleMarkServiceDone - Trying fallback: find by service ID only');
                // Try to use the first resource detail with matching service as fallback
                if (updatedBooking.details && Array.isArray(updatedBooking.details)) {
                  for (const detail of updatedBooking.details) {
                    if (detail.resourceDetails && Array.isArray(detail.resourceDetails)) {
                      const matchingService = detail.resourceDetails.find(rd => rd.service_Id === resource.serviceId);
                      if (matchingService && matchingService.bookingResourceDetail_Id) {
                        updatedResourceDetailId = matchingService.bookingResourceDetail_Id;
                        console.log('⚠️ handleMarkServiceDone - Using fallback resource detail ID:', updatedResourceDetailId);
                        break;
                      }
                    }
                  }
                }
              }
            } else {
              console.warn('⚠️ handleMarkServiceDone - No bookings found after reload');
            }
          } catch (reloadError) {
            console.error('❌ handleMarkServiceDone - Error reloading booking:', reloadError);
            // Continue with original ID as fallback
            console.warn('⚠️ handleMarkServiceDone - Continuing with original resource detail ID');
          }
        } catch (saveError) {
          console.error("❌ handleMarkServiceDone - Error saving booking before marking as done:", saveError);
          Swal.fire("Error", "Failed to save employee assignment. Please save the booking first.", "error");
          setLoading(false);
          return;
        } finally {
          setLoading(false);
        }
      }

      // Show confirmation dialog
      const confirmResult = await Swal.fire({
        title: 'Mark Service as Done',
        text: `Are you sure you want to mark "${resource.serviceName}" as done?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, mark as done',
        cancelButtonText: 'Cancel'
      });

      if (!confirmResult.isConfirmed) {
        console.log('❌ handleMarkServiceDone - User cancelled');
        return; // User cancelled
      }

      // Use the updated resource detail ID (or original if not updated)
      console.log('📤 handleMarkServiceDone - Calling updateCompletionStatus API', {
        bookingResourceDetail_Id: updatedResourceDetailId,
        originalId: resource.bookingResourceDetail_Id,
        completionStatus: 'Complete'
      });

      // Call the API to update completion status
      const result = await updateCompletionStatus(updatedResourceDetailId, 'Complete');

      console.log('📥 handleMarkServiceDone - Update completion status result:', result);

      if (result.isSuccess) {
        console.log('✅ handleMarkServiceDone - Successfully marked as done');
        // Update the local state to reflect the change
        setServiceResources(prev =>
          prev.map(r =>
            r.id === resource.id
              ? { ...r, completionStatus: 'Complete', bookingResourceDetail_Id: updatedResourceDetailId }
              : r
          )
        );

        Swal.fire("Success", result.message || "Service marked as done successfully", "success");

        // Reset form after successful marking as done (similar to booking save)
        loadBookings();
        resetForm();
      } else {
        console.error('❌ handleMarkServiceDone - Failed to mark as done:', result);
        Swal.fire("Error", result.message || "Failed to mark service as done", "error");
      }
    } catch (error) {
      console.error("❌ handleMarkServiceDone - Error marking service as done:", error);
      Swal.fire("Error", "Failed to mark service as done", "error");
    }
  };

  // Handle service resource cancellation (opens modal)
  // Handle removal of service resource (only in Add mode)
  const handleRemoveService = (resourceId) => {
    // Find the resource to remove
    const resourceToRemove = serviceResources.find(r => r.id === resourceId);

    if (!resourceToRemove) {
      return;
    }

    // Remove from service resources
    setServiceResources(prev => prev.filter(r => r.id !== resourceId));
  };

  const handleCancelServiceResource = (resourceId) => {
    setCancellingResourceId(resourceId);
    setServiceCancelReason("");
    setShowServiceCancelModal(true);
  };

  // Confirm service resource cancellation
  const confirmCancelServiceResource = async () => {
    if (!cancellingResourceId) return;

    if (!serviceCancelReason.trim()) {
      Swal.fire("Validation Error", "Please provide a cancellation reason", "error");
      return;
    }

    const reason = serviceCancelReason.trim();
    const resource = serviceResources.find(r => r.id === cancellingResourceId);
    if (!resource) return;

    // If this is an existing booking resource, cancel it immediately in DB (no need to click Save Booking)
    if (resource.bookingResourceDetail_Id && resource.bookingResourceDetail_Id > 0) {
      try {
        const resp = await cancelBookingResource(resource.bookingResourceDetail_Id, reason);
        if (!resp?.isSuccess && !resp?.IsSuccess) {
          Swal.fire("Error", resp?.message || resp?.Message || "Failed to cancel service", "error");
          return;
        }

        // Keep booking list state in sync so reopening Edit reflects the cancelled status
        // (otherwise the list may still hold stale booking data until a manual refresh)
        const bookingId = form.booking_Id;
        if (bookingId && bookingId > 0) {
          const computeBookingCompletionStatus = (b) => {
            const details = b.details || b.Details || [];
            const allResources = [];
            details.forEach(d => {
              (d.resourceDetails || d.ResourceDetails || []).forEach(r => allResources.push(r));
            });

            // Normalize status field
            const getStatus = (r) => r.completionStatus || r.CompletionStatus || null;

            const nonCancelled = allResources.filter(r => getStatus(r) !== 'Cancel');
            if (nonCancelled.length === 0) {
              // If everything is cancelled, booking isn't billable; keep as Cancel-ish
              return 'Cancel';
            }

            const allNonCancelledComplete = nonCancelled.every(r => getStatus(r) === 'Complete');
            if (allNonCancelledComplete) return 'Complete';

            // Otherwise, don't force-change (preserve whatever server gave us)
            return b.completionStatus || b.CompletionStatus || 'Pending';
          };

          const patchBooking = (b) => {
            if ((b.booking_Id || b.Booking_Id) !== bookingId) return b;
            const details = (b.details || b.Details || []).map(d => ({
              ...d,
              resourceDetails: (d.resourceDetails || d.ResourceDetails || []).map(rd => {
                if ((rd.bookingResourceDetail_Id || rd.BookingResourceDetail_Id) !== resource.bookingResourceDetail_Id) return rd;
                return {
                  ...rd,
                  completionStatus: 'Cancel',
                  cancellation_Reason: reason
                };
              })
            }));
            const next = { ...b, details };
            const nextStatus = computeBookingCompletionStatus(next);
            return { ...next, completionStatus: nextStatus, CompletionStatus: nextStatus };
          };
          setBookings(prev => (Array.isArray(prev) ? prev.map(patchBooking) : prev));
          setAllBookings(prev => (Array.isArray(prev) ? prev.map(patchBooking) : prev));
        }
      } catch (e) {
        console.error("Error cancelling booking resource:", e);
        Swal.fire("Error", "Failed to cancel service", "error");
        return;
      }
    }

    // Update UI state
    setServiceResources(prev =>
      prev.map(r =>
        r.id === cancellingResourceId
          ? { ...r, isCancelled: true, cancellationReason: reason, completionStatus: "Cancel" }
          : r
      )
    );

    setShowServiceCancelModal(false);
    setCancellingResourceId(null);
    setServiceCancelReason("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.customer_Id || !form.source_Id || !form.business_Id || !form.branch_Id) {
      Swal.fire("Validation Error", "Please fill all required fields (Customer, Source, Company, and Branch)", "error");
      return;
    }

    // Validate that we have services to book
    if (serviceResources.length === 0) {
      Swal.fire("Validation Error", "Please select at least one service or package", "error");
      return;
    }

    // Validate that all selected services and packages are active
    const inactiveEntities = [];

    // Check selected services
    if (form.selectedServices && form.selectedServices.length > 0) {
      for (const service of form.selectedServices) {
        const serviceData = services.find(s => s.value === service.value);
        if (!serviceData) {
          // Try to find in original data if not in formatted list
          // This shouldn't happen if filtering is working, but add as safety check
          inactiveEntities.push({ name: service.label || `Service ${service.value}`, type: 'Service' });
        }
      }
    }

    // Check selected packages
    if (form.selectedPackages && form.selectedPackages.length > 0) {
      for (const pkg of form.selectedPackages) {
        const packageData = packages.find(p => p.value === pkg.value);
        if (!packageData) {
          inactiveEntities.push({ name: pkg.label || `Package ${pkg.value}`, type: 'Package' });
        }
      }
    }

    // Check service resources (which may include services from packages)
    for (const resource of serviceResources) {
      if (resource.serviceId) {
        const serviceData = services.find(s => s.value === resource.serviceId);
        if (!serviceData) {
          inactiveEntities.push({ name: resource.serviceName || `Service ${resource.serviceId}`, type: 'Service' });
        }
      }
    }

    if (inactiveEntities.length > 0) {
      const entityList = inactiveEntities.map(e => `${e.type}: ${e.name}`).join('\n');
      Swal.fire("Validation Error", `Cannot book inactive entities. Please remove the following inactive items:\n\n${entityList}`, "error");
      return;
    }

    // Validate resource allocation for each service (only validate time logic if times are provided)
    for (const resource of serviceResources) {
      // Only validate time logic if both times are provided
      if (resource.fromTime && resource.toTime) {
        if (new Date(resource.toTime) <= new Date(resource.fromTime)) {
          Swal.fire("Validation Error", `To time must be after from time for service: ${resource.serviceName}`, "error");
          return;
        }
      }
    }

    try {
      setLoading(true);

      // Get current user ID from localStorage
      const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
      const currentUserId = authUser.userId || authUser.user_Id || 1;

      // Prepare booking details for selected items
      const details = [];
      const resourceDetails = [];
      let detailIndex = 1;

      // Group services by type (individual, package, membership)
      const individualServices = serviceResources.filter(r => !r.packageId && !r.membershipId);
      const packageServices = serviceResources.filter(r => r.packageId && !r.membershipId);
      const membershipServices = serviceResources.filter(r => r.membershipId);

      // Get unique memberships from membership services
      const uniqueMemberships = [...new Set(membershipServices.map(r => r.membershipId))];

      // Add individual services to details
      if (individualServices.length > 0) {
        individualServices.forEach(service => {
          const detailId = detailIndex++;
          const detailObj = {
            tempDetailId: detailId,
            entity_Id: service.serviceId,
            entity_Type: 'ser',
            is_Cancelled: false,
            cancel_Reason: null,
            created_By: currentUserId,
            created_On: new Date().toISOString(),
            modified_By: currentUserId,
            modified_On: new Date().toISOString(),
          };
          // Only include bookingDetail_Id if it exists and is > 0 (update operation)
          if (service.bookingDetail_Id && service.bookingDetail_Id > 0) {
            detailObj.bookingDetail_Id = service.bookingDetail_Id;
          }
          details.push(detailObj);

          // Create local date-time without timezone conversion
          const bookingDate = new Date(form.booking_Date);
          const fromTime = service.fromTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(),
            service.fromTime.getHours(), service.fromTime.getMinutes(), service.fromTime.getSeconds()) : null;
          const toTime = service.toTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(),
            service.toTime.getHours(), service.toTime.getMinutes(), service.toTime.getSeconds()) : null;

          // Add resource detail for individual service
          const resourceDetailObj = {
            tempDetailId: detailId,
            service_Id: service.serviceId,
            package_Id: null,
            membership_Id: null,
            room_Id: service.roomId,
            equipment_Id: service.equipmentId,
            employee_Id: service.employeeId,
            from_Time: fromTime ? formatLocalDateTime(fromTime) : null,
            to_Time: toTime ? formatLocalDateTime(toTime) : null,
            modified_By: currentUserId,
            modified_On: new Date().toISOString(),
            completionStatus: service.isCancelled ? 'Cancel' : (service.completionStatus || null),
            cancellation_Reason: service.isCancelled ? (service.cancellationReason || null) : null,
          };
          // Only include ID fields if they exist and are > 0 (update operation)
          if (service.bookingResourceDetail_Id && service.bookingResourceDetail_Id > 0) {
            resourceDetailObj.bookingResourceDetail_Id = service.bookingResourceDetail_Id;
          }
          if (service.bookingDetail_Id && service.bookingDetail_Id > 0) {
            resourceDetailObj.bookingDetail_Id = service.bookingDetail_Id;
          }
          resourceDetails.push(resourceDetailObj);
        });
      }

      // Add packages to details
      // CRITICAL FIX: preserve multiple instances of the same package (same package_Id booked multiple times)
      // Old logic used `uniquePackages` and collapsed them into a single BookingDetails row on save.
      // We must group package resources by their `bookingDetail_Id` (loaded from DB in handleEdit),
      // and create ONE detail per instance.
      if (packageServices.length > 0) {
        const packageInstances = new Map(); // key -> { packageId, bookingDetailId, services: [] }

        // Step 1: Initialize instances from selectedPackages (authoritative for how many instances exist)
        if (form.selectedPackages && form.selectedPackages.length > 0) {
          form.selectedPackages.forEach(selectedPkg => {
            const packageId = selectedPkg.value;
            const bookingDetailId = selectedPkg.bookingDetail_Id || selectedPkg.bookingDetailId;
            if (bookingDetailId && parseInt(bookingDetailId) > 0) {
              const key = `detail_${parseInt(bookingDetailId)}`;
              if (!packageInstances.has(key)) {
                packageInstances.set(key, {
                  packageId,
                  bookingDetailId: parseInt(bookingDetailId),
                  services: []
                });
              }
            }
          });
        }

        // Step 2: Assign services to instances (prefer service.bookingDetail_Id which was preserved in handleEdit)
        packageServices.forEach(service => {
          const packageId = service.packageId;
          let bookingDetailId = null;
          if (service.bookingDetail_Id) {
            const parsed = parseInt(service.bookingDetail_Id);
            if (!isNaN(parsed) && parsed > 0) bookingDetailId = parsed;
          }

          let key;
          if (bookingDetailId) {
            key = `detail_${bookingDetailId}`;
            if (!packageInstances.has(key)) {
              packageInstances.set(key, { packageId, bookingDetailId, services: [] });
            }
          } else {
            // New/unsaved package instance (no bookingDetail_Id yet). Keep separate bucket per packageId.
            // Note: BookingPage currently doesn't support adding *multiple new* instances of the same package
            // before saving, so one bucket is fine here.
            key = `package_${packageId}_new`;
            if (!packageInstances.has(key)) {
              packageInstances.set(key, { packageId, bookingDetailId: null, services: [] });
            }
          }

          packageInstances.get(key).services.push(service);
        });

        // Step 3: Emit one detail row per instance and attach its resources
        packageInstances.forEach((instance) => {
          const detailId = detailIndex++;
          const packageId = instance.packageId;
          const bookingDetailId = instance.bookingDetailId;

          const packageDetailObj = {
            tempDetailId: detailId,
            entity_Id: packageId,
            entity_Type: 'pac',
            is_Cancelled: false,
            cancel_Reason: null,
            created_By: currentUserId,
            created_On: new Date().toISOString(),
            modified_By: currentUserId,
            modified_On: new Date().toISOString(),
          };

          if (bookingDetailId && bookingDetailId > 0) {
            packageDetailObj.bookingDetail_Id = bookingDetailId;
          }

          details.push(packageDetailObj);

          instance.services.forEach(service => {
            const bookingDate = new Date(form.booking_Date);
            const fromTime = service.fromTime
              ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(),
                service.fromTime.getHours(), service.fromTime.getMinutes(), service.fromTime.getSeconds())
              : null;
            const toTime = service.toTime
              ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(),
                service.toTime.getHours(), service.toTime.getMinutes(), service.toTime.getSeconds())
              : null;

            const packageResourceDetailObj = {
              tempDetailId: detailId,
              service_Id: service.serviceId,
              package_Id: packageId,
              membership_Id: null,
              room_Id: service.roomId,
              equipment_Id: service.equipmentId,
              employee_Id: service.employeeId,
              from_Time: fromTime ? formatLocalDateTime(fromTime) : null,
              to_Time: toTime ? formatLocalDateTime(toTime) : null,
              modified_By: currentUserId,
              modified_On: new Date().toISOString(),
              completionStatus: service.isCancelled ? 'Cancel' : (service.completionStatus || null),
              cancellation_Reason: service.isCancelled ? (service.cancellationReason || null) : null,
            };

            if (service.bookingResourceDetail_Id && service.bookingResourceDetail_Id > 0) {
              packageResourceDetailObj.bookingResourceDetail_Id = service.bookingResourceDetail_Id;
            }
            if (packageDetailObj.bookingDetail_Id && packageDetailObj.bookingDetail_Id > 0) {
              packageResourceDetailObj.bookingDetail_Id = packageDetailObj.bookingDetail_Id;
            }

            resourceDetails.push(packageResourceDetailObj);
          });
        });
      }

      // Add memberships to details
      if (uniqueMemberships.length > 0) {
        uniqueMemberships.forEach(membershipId => {
          const detailId = detailIndex++;
          const membershipDetailObj = {
            tempDetailId: detailId,
            entity_Id: membershipId,
            entity_Type: 'mem',
            is_Cancelled: false,
            cancel_Reason: null,
            created_By: currentUserId,
            created_On: new Date().toISOString(),
            modified_By: currentUserId,
            modified_On: new Date().toISOString(),
          };
          // Only include bookingDetail_Id if it exists and is > 0 (update operation)
          if (membershipId && membershipId > 0) {
            // Check if this membership detail already has an ID from existing booking
            const existingMembershipDetail = details.find(d => d.entity_Id === membershipId && d.entity_Type === 'mem');
            if (existingMembershipDetail && existingMembershipDetail.bookingDetail_Id && existingMembershipDetail.bookingDetail_Id > 0) {
              membershipDetailObj.bookingDetail_Id = existingMembershipDetail.bookingDetail_Id;
            }
          }
          details.push(membershipDetailObj);

          // Add resource details for all services in this membership
          const servicesInMembership = membershipServices.filter(r => r.membershipId === membershipId);
          servicesInMembership.forEach(service => {
            // Create local date-time without timezone conversion
            const bookingDate = new Date(form.booking_Date);
            const fromTime = service.fromTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(),
              service.fromTime.getHours(), service.fromTime.getMinutes(), service.fromTime.getSeconds()) : null;
            const toTime = service.toTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(),
              service.toTime.getHours(), service.toTime.getMinutes(), service.toTime.getSeconds()) : null;

            const membershipResourceDetailObj = {
              tempDetailId: detailId,
              service_Id: service.serviceId,
              package_Id: service.packageId,
              membership_Id: membershipId,
              room_Id: service.roomId,
              equipment_Id: service.equipmentId,
              employee_Id: service.employeeId,
              from_Time: formatLocalDateTime(fromTime),
              to_Time: formatLocalDateTime(toTime),
              modified_By: currentUserId,
              modified_On: new Date().toISOString(),
              completionStatus: service.isCancelled ? 'Cancel' : (service.completionStatus || null),
              cancellation_Reason: service.isCancelled ? (service.cancellationReason || null) : null,
            };
            // Only include ID fields if they exist and are > 0 (update operation)
            if (service.bookingResourceDetail_Id && service.bookingResourceDetail_Id > 0) {
              membershipResourceDetailObj.bookingResourceDetail_Id = service.bookingResourceDetail_Id;
            }
            if (membershipDetailObj.bookingDetail_Id && membershipDetailObj.bookingDetail_Id > 0) {
              membershipResourceDetailObj.bookingDetail_Id = membershipDetailObj.bookingDetail_Id;
            }
            resourceDetails.push(membershipResourceDetailObj);
          });
        });
      }

      // form.source_Id is already set from the dropdown

      // Calculate the earliest from time and latest to time from all services
      const bookingDate = new Date(form.booking_Date);
      const allFromTimes = resourceDetails.map(rd => rd.from_Time ? new Date(rd.from_Time) : null).filter(d => d !== null);
      const allToTimes = resourceDetails.map(rd => rd.to_Time ? new Date(rd.to_Time) : null).filter(d => d !== null);

      // Use booking date as default if no times are available
      const earliestFromTime = allFromTimes.length > 0 ? new Date(Math.min(...allFromTimes)) : bookingDate;
      const latestToTime = allToTimes.length > 0 ? new Date(Math.max(...allToTimes)) : bookingDate;

      // Prepare booking data - remove ID field for inserts
      const bookingData = {
        ...form,
        booking_Date: formatLocalDate(new Date(form.booking_Date)),
        from_Time: formatLocalDateTime(earliestFromTime),
        to_Time: formatLocalDateTime(latestToTime),
        entry_Date: formatLocalDateTime(new Date()),
        source_Id: form.source_Id || null,
        modified_By: currentUserId,
        modified_On: formatLocalDateTime(new Date()),
        business_Id: form.business_Id || 1,
        branch_Id: form.branch_Id || 1,
        sendNotification: form.sendNotification || false,
        details: details,
        resourceDetails: resourceDetails,
      };

      // Remove booking_Id if it's null, 0, or undefined (insert operation)
      if (!bookingData.booking_Id || bookingData.booking_Id === 0) {
        delete bookingData.booking_Id;
      }

      // Debug logging: Show details being sent
      console.log('📋 Booking Payload Summary (handleSubmit):', {
        booking_Id: bookingData.booking_Id,
        detailsCount: bookingData.details?.length || 0,
        details: bookingData.details?.map(d => ({
          tempDetailId: d.tempDetailId,
          bookingDetail_Id: d.bookingDetail_Id,
          entity_Id: d.entity_Id,
          entity_Type: d.entity_Type
        })),
        resourceDetailsCount: bookingData.resourceDetails?.length || 0
      });

      const result = await saveBooking(bookingData);

      if (result.id && result.msg) {
        // Save consent data if it exists (was signed before booking save)
        if (consentData.guestSignature || consentData.witnessSignature) {
          try {
            const consentPayload = {
              booking_Id: result.id,
              witnessName: consentData.witnessName?.trim() || null,
              witnessSignature: consentData.witnessSignature || null,
              guestSignature: consentData.guestSignature || null,
            };
            await signBookingConsent(consentPayload);
            console.log('Consent saved successfully with booking');
          } catch (error) {
            console.error('Error saving consent:', error);
            // Don't block booking success, just log the error
          }
        }

        Swal.fire("Success", result.msg || result.message || "Booking saved successfully", "success");
        loadBookings();

        // Hide form and reset
        setShowForm(false);
        resetForm();
      } else {
        Swal.fire("Error", result.msg || result.message || "Failed to save booking", "error");
      }
    } catch (error) {
      console.error("Error saving booking:", error);
      Swal.fire("Error", "Failed to save booking", "error");
    } finally {
      setLoading(false);
    }
  };


  const resetForm = () => {
    setForm({
      booking_Id: null,
      customer_Id: null,
      employee_Id: null,
      room_Id: null,
      from_Time: new Date(),
      to_Time: new Date(),
      entry_Date: new Date(),
      booking_Date: new Date(),
      is_Cancelled: false,
      cancellation_Reason: "",
      notes: "",
      source_Id: null,
      sourceOther: "",
      modified_By: null,
      modified_On: null,
      selectedServices: [],
      selectedPackages: [],
      selectedPackageSeries: [],
      business_Id: null,
      branch_Id: null,
      sendNotification: false,
    });
    setServiceResources([]);
    setSelectedCustomerOption(null);
    setIsEditing(false);
    setIsViewing(false);
    setFormKey(prev => prev + 1);

    // Reset membership data
    setCustomerMembership(null);
    setAllCustomerMemberships([]);
    setHasMembership(false);
    setMembershipServices([]);
    setMembershipPackages([]);
    setMembershipPackageSeries([]);

    // Reset membership modal state
    setShowMembershipModal(false);
    setSelectedMembershipServices([]);
    setSelectedMembershipPackages([]);
    setSelectedMembershipPackageSeries([]);

    // Reset consent data (but keep employee name)
    setConsentData({
      witnessName: loggedInEmployeeName,
      witnessSignature: '',
      guestSignature: ''
    });
  };

  // Handle consent signing
  const handleSignConsent = async (consentPayload) => {
    try {
      setConsentLoading(true);
      // Store consent data in state (will be saved with booking)
      setConsentData({
        witnessName: consentPayload.witnessName || '',
        witnessSignature: consentPayload.witnessSignature || '',
        guestSignature: consentPayload.guestSignature || ''
      });

      // If booking is already saved, save consent immediately
      if (form.booking_Id > 0) {
        const result = await signBookingConsent({
          ...consentPayload,
          booking_Id: form.booking_Id
        });
        if (result.isSuccess) {
          Swal.fire("Success", result.message || "Consent signed successfully", "success");
          loadBookings();
        } else {
          Swal.fire("Error", result.message || "Failed to sign consent", "error");
        }
      } else {
        // Booking not saved yet, consent will be saved when booking is saved
        Swal.fire("Success", "Consent form signed. It will be saved when you save the booking.", "success");
      }
    } catch (error) {
      console.error('Error signing consent:', error);
      Swal.fire("Error", "Failed to sign consent", "error");
    } finally {
      setConsentLoading(false);
    }
  };

  const handleEdit = async (booking) => {
    // Save list scroll so Back can restore it, then scroll to form top after opening
    lastListScrollYRef.current = window.scrollY || 0;

    // Load existing booking details if available
    let selectedServices = [];
    let selectedPackages = [];
    let selectedPackageSeries = [];

    if (booking.details && Array.isArray(booking.details)) {
      for (const detail of booking.details) {
        if (detail.entity_Type === 'ser') {
          const service = services.find(s => s.value === detail.entity_Id);
          if (service) {
            selectedServices.push({
              ...service,
              bookingDetail_Id: detail.bookingDetail_Id,
              is_Cancelled: detail.is_Cancelled,
              cancel_Reason: detail.cancel_Reason
            });
          }
        } else if (detail.entity_Type === 'pac') {
          const pkg = packages.find(p => p.value === detail.entity_Id);
          if (pkg) {
            selectedPackages.push({
              ...pkg,
              bookingDetail_Id: detail.bookingDetail_Id,
              is_Cancelled: detail.is_Cancelled,
              cancel_Reason: detail.cancel_Reason
            });
          }
        } else if (detail.entity_Type === 'sep') {
          const series = packageSeries.find(s => s.value === detail.entity_Id);
          if (series) {
            selectedPackageSeries.push({
              ...series,
              bookingDetail_Id: detail.bookingDetail_Id,
              is_Cancelled: detail.is_Cancelled,
              cancel_Reason: detail.cancel_Reason
            });
          }
        } else if (detail.entity_Type === 'mem') {
          // Handle membership bookings - get package series from membership
          const membershipSeries = await getPackageSeriesFromMembership(detail.entity_Id);
          if (membershipSeries.length > 0) {
            // For membership, we'll show the first available series
            // In a real scenario, you might want to store which specific series was selected
            selectedPackageSeries.push({
              ...membershipSeries[0],
              bookingDetail_Id: detail.bookingDetail_Id,
              is_Cancelled: detail.is_Cancelled,
              cancel_Reason: detail.cancel_Reason,
              isMembership: true,
              membershipId: detail.entity_Id
            });
          }
        }
      }
    }

    // Handle Source_Id from API (can be Source_Id or source_Id)
    const sourceId = booking.Source_Id || booking.source_Id || null;

    // Get employee and room IDs from resource details (first available)
    const firstResource = booking.details?.[0]?.resourceDetails?.[0];
    const employeeId = firstResource?.employee_Id || null;
    const roomId = firstResource?.room_Id || null;

    // Handle Business_Id and Branch_Id from API
    const businessId = booking.Business_Id || booking.business_Id || null;
    const branchId = booking.Branch_Id || booking.branch_Id || null;

    setForm({
      booking_Id: booking.booking_Id,
      customer_Id: booking.customer_Id,
      employee_Id: employeeId,
      room_Id: roomId,
      from_Time: booking.from_Time ? new Date(booking.from_Time) : null,
      to_Time: booking.to_Time ? new Date(booking.to_Time) : null,
      entry_Date: new Date(booking.entry_Date),
      booking_Date: new Date(booking.booking_Date),
      is_Cancelled: booking.is_Cancelled,
      cancellation_Reason: booking.cancellation_Reason || "",
      notes: booking.notes || "",
      source_Id: sourceId, // 👈 SET SOURCE_ID FOR EDIT
      sourceOther: "", // Source details field (optional, not stored in booking)
      modified_By: booking.modified_By,
      modified_On: booking.modified_On,
      selectedServices: selectedServices,
      selectedPackages: selectedPackages,
      selectedPackageSeries: selectedPackageSeries,
      business_Id: businessId,
      branch_Id: branchId,
      sendNotification: booking.sendNotification || booking.SendNotification || false,
    });

    // Load branches for the business if businessId exists
    if (businessId) {
      loadBranches(businessId);
    }

    // Populate service resources for editing
    const serviceResources = [];
    if (booking.details && Array.isArray(booking.details)) {
      for (const detail of booking.details) {
        if (detail.resourceDetails && Array.isArray(detail.resourceDetails)) {
          for (const resource of detail.resourceDetails) {
            const service = services.find(s => s.value === resource.service_Id);
            const serviceName = service ? service.label : `Service ${resource.service_Id}`;
            const packageName = resource.package_Id ? packages.find(p => p.value === resource.package_Id)?.label : null;

            // For completed services, use Start_Time and Stop_Time (actual times)
            // For other services, use From_Time and To_Time (scheduled times)
            const isComplete = resource.completionStatus === 'Complete';
            const displayFromTime = isComplete && resource.start_Time
              ? new Date(resource.start_Time)
              : (resource.from_Time ? new Date(resource.from_Time) : null);
            const displayToTime = isComplete && resource.stop_Time
              ? new Date(resource.stop_Time)
              : (resource.to_Time ? new Date(resource.to_Time) : null);

            serviceResources.push({
              id: `edit-${resource.bookingResourceDetail_Id}`,
              serviceId: resource.service_Id,
              serviceName: serviceName,
              packageId: resource.package_Id,
              packageName: packageName,
              membershipId: resource.membership_Id,
              employeeId: resource.employee_Id,
              roomId: resource.room_Id,
              equipmentId: resource.equipment_Id,
              fromTime: displayFromTime,
              toTime: displayToTime,
              bookingResourceDetail_Id: resource.bookingResourceDetail_Id,
              // CRITICAL: preserve BookingDetail_Id so saveBooking updates instead of inserting new BookingDetails
              bookingDetail_Id: resource.bookingDetail_Id || detail.bookingDetail_Id,
              completionStatus: resource.completionStatus || null,
              isCancelled: resource.completionStatus === 'Cancel',
              cancellationReason: resource.cancellation_Reason || null,
              // Preserve time tracking fields for Done flow
              checkIn_Time: resource.checkIn_Time || null,
              start_Time: resource.start_Time || null,
              stop_Time: resource.stop_Time || null
            });
          }
        }
      }
    }

    // Sort service resources by fromTime to display chronologically
    serviceResources.sort((a, b) => {
      if (!a.fromTime) return 1;
      if (!b.fromTime) return -1;
      return a.fromTime.getTime() - b.fromTime.getTime();
    });

    setServiceResources(serviceResources);

    // Check if customer has membership for the badge
    await checkCustomerMembership(booking.customer_Id);

    // Fetch and set customer data for display in readonly field
    if (booking.customer_Id) {
      try {
        const customerData = await getCustomers({ customer_Id: booking.customer_Id });
        if (customerData && customerData.length > 0) {
          const customer = customerData[0];
          setSelectedCustomerOption({
            value: customer.customer_Id,
            label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
            customer: customer
          });
        } else {
          // Fallback if customer not found
          setSelectedCustomerOption({
            value: booking.customer_Id,
            label: `Customer ID: ${booking.customer_Id}`
          });
        }
      } catch (error) {
        console.error("Error fetching customer for edit:", error);
        // Fallback on error
        setSelectedCustomerOption({
          value: booking.customer_Id,
          label: `Customer ID: ${booking.customer_Id}`
        });
      }
    }

    // Store booking_Id in a variable to use in availability checks (form state update is async)
    const currentBookingId = booking.booking_Id;

    // Update form state first (this is synchronous for the setForm call)
    setForm(prev => ({
      ...prev,
      booking_Id: currentBookingId
    }));

    setIsEditing(true);
    setIsViewing(false);
    setShowForm(true);
    scrollToFormTop();

    // COMMENTED OUT per user request - availability checks not needed
    // Trigger availability checks IMMEDIATELY (don't wait for setTimeout)
    // This ensures checks start as soon as possible
    // const resourcesToCheck = serviceResources.filter(resource => resource.fromTime && resource.toTime);
    // 
    // if (resourcesToCheck.length > 0) {
    //   // Start all availability checks in parallel
    //   const availabilityPromises = resourcesToCheck.map(resource => {
    //     return checkTimeAvailability(resource.id, resource.fromTime, resource.toTime, currentBookingId);
    //   });
    //   
    //   // After ALL checks complete, force a final refresh to update dropdowns
    //   Promise.all(availabilityPromises).then(() => {
    //     // Use setTimeout to ensure state updates have propagated
    //     setTimeout(() => {
    //       setAvailabilityRefreshKey(prev => {
    //         const newKey = prev + 1;
    //         return newKey;
    //       });
    //     }, 100);
    //   });
    //   
    //   // Also trigger bulk availability check
    //   setTimeout(() => {
    //     checkAvailabilityForSelection();
    //   }, 300);
    // }
  };

  // New function to handle cancellation with reason
  const handleCancel = async (bookingId) => {
    setCancellingBookingId(bookingId);
    setCancelReason("");
    setShowCancelModal(true);
  };

  // Function to open reschedule modal
  const handleOpenReschedule = () => {
    if (!form.booking_Date) {
      Swal.fire("Warning", "Please select a booking date first", "warning");
      return;
    }
    setRescheduleDate(form.booking_Date);
    setRescheduleComment("");
    setShowRescheduleModal(true);
  };

  // Function to handle reschedule save
  const handleRescheduleSave = async () => {
    if (!rescheduleDate) {
      Swal.fire("Warning", "Please select a new date", "warning");
      return;
    }

    // Validate comment is mandatory
    if (!rescheduleComment || rescheduleComment.trim() === "") {
      Swal.fire("Warning", "Please enter a comment/notes for the reschedule", "warning");
      return;
    }

    if (!form.booking_Id || form.booking_Id === 0) {
      Swal.fire("Error", "Invalid booking ID", "error");
      return;
    }

    // Check if booking is completed or cancelled
    const currentBooking = bookings.find(b => b.booking_Id === form.booking_Id);
    if (currentBooking) {
      const isCompleted = currentBooking.completionStatus === 'Complete' ||
        currentBooking.CompletionStatus === 'Complete' ||
        currentBooking.is_Cancelled === true ||
        currentBooking.Is_Cancelled === true;
      if (isCompleted) {
        Swal.fire("Error", "Cannot reschedule a completed or cancelled booking", "error");
        setShowRescheduleModal(false);
        return;
      }
    }

    // Show confirmation about clearing resources
    const confirmResult = await Swal.fire({
      title: 'Confirm Reschedule',
      html: `
        <p>This will clear the staff, room, and equipment selection for all services in this booking.</p>
        <p>You will need to reassign resources after rescheduling.</p>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Reschedule',
      cancelButtonText: 'Cancel'
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    try {
      setRescheduleLoading(true);

      // Get current user ID
      const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
      const currentUserId = authUser.userId || authUser.user_Id || 1;

      // Format dates
      const oldDate = new Date(form.booking_Date);
      const newDate = new Date(rescheduleDate);
      const oldDateStr = formatLocalDate(oldDate);
      const newDateStr = formatLocalDate(newDate);

      // Call reschedule API
      const rescheduleData = {
        booking_Id: form.booking_Id,
        changed_By: currentUserId,
        old_Date: oldDateStr,
        new_Date: newDateStr,
        notes: rescheduleComment || null
      };

      const response = await rescheduleBooking(rescheduleData);

      if (response && response.isSuccess) {
        // Immediately clear employee, room, and equipment for all services in UI
        // This ensures the Service Resource Allocation section is updated right away
        const clearedResources = serviceResources.map(resource => ({
          ...resource,
          employeeId: null,
          roomId: null,
          equipmentId: null,
          employeeName: null,
          roomName: null
        }));

        // Update booking date in form first
        setForm(prev => ({
          ...prev,
          booking_Date: newDate
        }));

        // COMMENTED OUT per user request - availability checks not needed
        // Clear old availability results
        // setTimeAvailabilityResults({});
        // setAvailabilityResults({});

        // Update service resources
        setServiceResources(clearedResources);

        // Force re-render of Select components by incrementing formKey
        setFormKey(prev => prev + 1);
        // COMMENTED OUT per user request - availabilityRefreshKey not needed
        // setAvailabilityRefreshKey(prev => prev + 1);

        // Close modal first
        setShowRescheduleModal(false);

        // Show success message
        Swal.fire("Success", "Booking rescheduled successfully. Please reassign staff, room, and equipment.", "success");

        // Reload bookings list in background (don't wait for it to avoid blocking UI update)
        loadBookings().catch(err => console.error("Error reloading bookings:", err));

        // COMMENTED OUT per user request - availability checks not needed
        // Trigger time-based availability checks for all resources with existing times
        // This ensures dropdowns show correct availability immediately based on existing times
        // Use a longer timeout to ensure state updates are complete
        // setTimeout(async () => {
        //   // Check availability for all resources with existing times
        //   const availabilityPromises = clearedResources
        //     .filter(resource => resource.fromTime && resource.toTime)
        //     .map(resource => {
        //       return checkTimeAvailability(resource.id, resource.fromTime, resource.toTime);
        //     });
        //   
        //   // Wait for all availability checks to complete
        //   await Promise.all(availabilityPromises);
        //   
        //   // After all checks complete, increment refresh key once to update all dropdowns
        //   setAvailabilityRefreshKey(prev => prev + 1);
        //   
        //   // Also trigger bulk availability check
        //   checkAvailabilityForSelection();
        // }, 800);
      } else {
        Swal.fire("Error", response?.message || "Failed to reschedule booking", "error");
      }
    } catch (error) {
      console.error("Error rescheduling booking:", error);
      Swal.fire("Error", error.response?.data?.message || "Failed to reschedule booking", "error");
    } finally {
      setRescheduleLoading(false);
    }
  };

  // Function to handle Pay Bill navigation
  const handlePayBill = (booking) => {
    // Filter out cancelled services AND billed services - only include services that are:
    // 1. Not cancelled (completionStatus !== 'Cancel')
    // 2. Not already billed (handle all possible isBilled values: false, 0, null, undefined, '0', 'false')
    // 3. Complete (completionStatus === 'Complete') - only complete services can be billed
    // 4. Not from membership (membership_Id === null) - membership services are handled separately
    const filteredBookingDetails = booking.details?.map(detail => {
      const filteredResources = detail.resourceDetails?.filter(
        resource => {
          // Check completion status
          if (resource.completionStatus === 'Cancel') return false;
          if (resource.completionStatus !== 'Complete') return false;

          // Check membership (exclude membership services)
          if (resource.membership_Id !== null && resource.membership_Id !== undefined) return false;

          // Check isBilled - handle all possible values (number, boolean, string, null, undefined)
          // Also check InvoiceDetail_Id as an indicator of being billed
          const isBilled = resource.isBilled || resource.IsBilled || resource.is_Billed;
          const invoiceDetailId = resource.invoiceDetail_Id || resource.InvoiceDetail_Id || resource.Invoice_Detail_Id;
          const isBilledValue = isBilled === true || isBilled === 1 || isBilled === '1' || isBilled === 'true';
          const hasInvoiceDetailId = invoiceDetailId !== null && invoiceDetailId !== undefined && invoiceDetailId !== '';

          if (isBilledValue || hasInvoiceDetailId) return false;  // Exclude if already billed

          return true;  // Include this resource
        }
      ) || [];

      return {
        ...detail,
        resourceDetails: filteredResources
      };
    }).filter(detail => detail.resourceDetails && detail.resourceDetails.length > 0) || [];

    // Navigate to billing page with customer ID, booking details, and business/branch IDs
    navigate('/GmsBilling', {
      state: {
        customerId: booking.customer_Id,
        bookingId: booking.booking_Id,
        bookingDetails: filteredBookingDetails,
        business_Id: booking.business_Id || booking.Business_Id,
        branch_Id: booking.branch_Id || booking.Branch_Id
      }
    });
  };

  // Function to handle View Bill - shows invoice print modal
  const handleViewBill = async (booking, originalPackageInvoiceId = null) => {
    try {
      const invoiceId = originalPackageInvoiceId || booking.invoice_Id;

      if (!invoiceId) {
        Swal.fire("Error", "No invoice found for this booking", "error");
        return;
      }

      setPrintLoading(true);

      // Fetch invoice data for print (same as print button in invoice list)
      const invoiceData = await getInvoiceForPrint(invoiceId);

      if (!invoiceData || !invoiceData.header) {
        Swal.fire("Error", "Invoice not found or unable to load invoice data", "error");
        setPrintLoading(false);
        return;
      }

      setInvoicePrintData(invoiceData);
      setShowInvoicePrintModal(true);
      setPrintLoading(false);
    } catch (error) {
      console.error("Error loading invoice for print:", error);
      Swal.fire("Error", `Failed to load invoice: ${error.response?.data?.message || error.message || "Unknown error"}`, "error");
      setPrintLoading(false);
    }
  };

  // Helper function to check if booking service is from a fully paid package/package series membership
  const checkIfFromFullyPaidPackage = async (booking) => {
    try {
      // Check if booking has resourceDetails with membership_Id
      if (!booking.details || booking.details.length === 0) {
        return { isFromMembership: false, invoiceId: null, isFullyPaid: false, membershipType: null };
      }

      // Find first resourceDetail with membership_Id
      let membershipId = null;
      for (const detail of booking.details) {
        if (detail.resourceDetails && detail.resourceDetails.length > 0) {
          const resourceWithMembership = detail.resourceDetails.find(
            resource => resource.membership_Id || resource.membershipId
          );
          if (resourceWithMembership) {
            membershipId = resourceWithMembership.membership_Id || resourceWithMembership.membershipId;
            break;
          }
        }
      }

      if (!membershipId) {
        return { isFromMembership: false, invoiceId: null, isFullyPaid: false, membershipType: null };
      }

      // Fetch membership details
      const memberships = await getCustomerMemberships({ membershipId: membershipId });
      if (!memberships || memberships.length === 0) {
        return { isFromMembership: false, invoiceId: null, isFullyPaid: false, membershipType: null };
      }

      const membership = memberships[0];
      const entityType = membership.entity_Type?.toUpperCase();

      // Check if it's a package (Entity_Type = 'PAC')
      if (entityType === 'PAC') {
        // For packages, check if installment has Invoice_Id (means it's fully paid)
        if (membership.installments && membership.installments.length > 0) {
          // Find package installment (SeriesId is NULL, InstallmentId = 1)
          const packageInstallment = membership.installments.find(
            inst => (inst.seriesId === null || inst.seriesId === 0) && inst.installmentId === 1
          );

          if (packageInstallment && packageInstallment.invoice_Id) {
            // Package is fully paid, return the invoice ID
            return {
              isFromMembership: true,
              invoiceId: packageInstallment.invoice_Id,
              isFullyPaid: true,
              membershipType: 'package'
            };
          }
        }
      }
      // Check if it's a package series (Entity_Type = 'SEP')
      else if (entityType === 'SEP') {
        // For package series, check if ALL installments are fully paid
        if (membership.installments && membership.installments.length > 0) {
          // Check if all installments have BilledAmount >= Amount
          const allInstallmentsPaid = membership.installments.every(inst => {
            const billedAmount = inst.billedAmount || 0;
            const amount = inst.amount || 0;
            return billedAmount >= amount;
          });

          if (allInstallmentsPaid) {
            // All installments are fully paid - services are already covered
            // Package series might have multiple invoices (one per installment)
            // We'll return the first installment's invoice ID if available, or just mark as fully paid
            const firstPaidInstallment = membership.installments.find(inst => inst.invoice_Id);
            return {
              isFromMembership: true,
              invoiceId: firstPaidInstallment?.invoice_Id || null,
              isFullyPaid: true,
              membershipType: 'packageSeries'
            };
          }
        }
      }

      return { isFromMembership: false, invoiceId: null, isFullyPaid: false, membershipType: null };
    } catch (error) {
      console.error('Error checking package membership:', error);
      return { isFromMembership: false, invoiceId: null, isFullyPaid: false, membershipType: null };
    }
  };

  // Function to handle bulk check in
  const handleBulkCheckIn = async (booking) => {
    if (checkInLoading) return; // Prevent multiple clicks

    try {
      // Show confirmation dialog
      const result = await Swal.fire({
        title: 'Confirm Check In',
        text: 'Are you sure you want to check in all services for this booking?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, check in!'
      });

      if (result.isConfirmed) {
        setCheckInLoading(true);
        // Get current timestamp for check-in time (local time, no timezone conversion)
        const checkInTime = new Date();
        // Format as local datetime string (YYYY-MM-DDTHH:mm:ss) without timezone conversion
        const checkInTimeString = formatLocalDateTime(checkInTime);
        // Call the bulk update API with Check In status and time
        const response = await updateBulkCompletionStatus(booking.booking_Id, 'Check In', checkInTimeString);

        if (response.isSuccess) {
          Swal.fire("Success", response.message, "success");
          // Refresh the booking list
          loadBookings();
        } else {
          Swal.fire("Error", response.message, "error");
        }
      }
    } catch (error) {
      console.error("Error updating bulk check in status:", error);
      Swal.fire("Error", "Failed to check in services", "error");
    } finally {
      setCheckInLoading(false);
    }
  };

  // Function to handle bulk completion status update (Done - opens popup)
  const handleBulkCompletionStatusUpdate = async (booking) => {
    if (allDoneLoading) return; // Prevent multiple clicks

    try {
      // Check if all services are complete (to mark as Pending) or checked in (to mark as Complete)
      const allComplete = areAllServicesComplete(booking);
      const allCheckedIn = checkIfAllServicesCheckedIn(booking);

      // If all are complete, allow reverting to pending (no popup needed)
      if (allComplete) {
        const result = await Swal.fire({
          title: 'Confirm Action',
          text: 'Are you sure you want to mark all services as pending?',
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Yes, do it!'
        });

        if (result.isConfirmed) {
          setAllDoneLoading(true);
          const response = await updateBulkCompletionStatus(booking.booking_Id, 'Pending');
          if (response.isSuccess) {
            Swal.fire("Success", response.message, "success");
            loadBookings();
          } else {
            Swal.fire("Error", response.message, "error");
          }
          setAllDoneLoading(false);
        }
        return;
      }

      // If there are services that are not complete, open popup for time selection
      // This includes services with status: Pending, Start, or Stop (but not Complete)
      if (hasServicesNotComplete(booking)) {
        // Collect all services from the booking with proper names (only non-complete ones)
        const servicesList = [];
        booking.details?.forEach(detail => {
          detail.resourceDetails?.forEach(resource => {
            // Only show eligible services in popup:
            // - non-cancelled
            // - not complete
            // - checked-in-ish (Check In / Start / Stop)
            // - employee assigned
            const isCheckedInIsh =
              resource.completionStatus === 'Check In' ||
              resource.completionStatus === 'Start' ||
              resource.completionStatus === 'Stop';

            if (resource.completionStatus !== 'Cancel' &&
              resource.completionStatus !== 'Complete' &&
              isCheckedInIsh &&
              resource.employee_Id) {
              // Get service name from services list
              const service = services.find(s => s.value === resource.service_Id);
              const serviceName = service?.label || resource.serviceName || `Service ${resource.service_Id}`;

              // Get employee name from employees list
              const employee = employees.find(e => e.value === resource.employee_Id);
              const employeeName = employee?.label || resource.employeeName || 'N/A';

              // Create date objects for times, using booking date for time-only pickers
              const bookingDate = booking.booking_Date ? new Date(booking.booking_Date) : new Date();
              const fromTime = resource.from_Time ? new Date(resource.from_Time) : null;
              const toTime = resource.to_Time ? new Date(resource.to_Time) : null;

              // Handle times based on completion status
              let startTime = null;
              let stopTime = null;

              if (resource.completionStatus === 'Pending' || resource.completionStatus === 'Check In') {
                // For Pending and Check In: use from_Time and to_Time
                if (fromTime) {
                  startTime = new Date(bookingDate);
                  startTime.setHours(fromTime.getHours(), fromTime.getMinutes(), 0, 0);
                }
                if (toTime) {
                  stopTime = new Date(bookingDate);
                  stopTime.setHours(toTime.getHours(), toTime.getMinutes(), 0, 0);
                }
              } else if (resource.completionStatus === 'Start') {
                // For Start: use start_Time if available, otherwise from_Time. For stop, only use stop_Time if it exists, otherwise leave empty
                if (resource.start_Time) {
                  startTime = new Date(resource.start_Time);
                } else if (fromTime) {
                  startTime = new Date(bookingDate);
                  startTime.setHours(fromTime.getHours(), fromTime.getMinutes(), 0, 0);
                }
                // Stop time: only use stop_Time if it exists, do NOT fall back to to_Time for Start status
                if (resource.stop_Time) {
                  stopTime = new Date(resource.stop_Time);
                }
                // If stop_Time is null/empty, stopTime remains null (do not use to_Time)
              } else if (resource.completionStatus === 'Stop') {
                // For Stop: use both start_Time and stop_Time from booking resource table
                if (resource.start_Time) {
                  startTime = new Date(resource.start_Time);
                } else if (fromTime) {
                  startTime = new Date(bookingDate);
                  startTime.setHours(fromTime.getHours(), fromTime.getMinutes(), 0, 0);
                }
                if (resource.stop_Time) {
                  stopTime = new Date(resource.stop_Time);
                } else if (toTime) {
                  stopTime = new Date(bookingDate);
                  stopTime.setHours(toTime.getHours(), toTime.getMinutes(), 0, 0);
                }
              }

              servicesList.push({
                bookingResourceDetail_Id: resource.bookingResourceDetail_Id,
                serviceName: serviceName,
                employeeName: employeeName,
                serviceId: resource.service_Id,
                employeeId: resource.employee_Id,
                fromTime: fromTime,
                toTime: toTime,
                startTime: startTime,
                stopTime: stopTime,
                completionStatus: resource.completionStatus,
                bookingDate: bookingDate,
                selected: false
              });
            }
          });
        });

        if (servicesList.length === 0) {
          Swal.fire(
            "Warning",
            "No eligible services to mark as done. Only services with status Check In/Start/Stop and an assigned employee can be marked done.",
            "warning"
          );
          return;
        }

        // Initialize service times with names
        const initialServiceTimes = servicesList.map(service => ({
          bookingResourceDetail_Id: service.bookingResourceDetail_Id,
          startTime: service.startTime,
          stopTime: service.stopTime,
          bookingDate: service.bookingDate,
          serviceName: service.serviceName,
          employeeName: service.employeeName,
          completionStatus: service.completionStatus,
          selected: false
        }));

        setDoneServiceTimes(initialServiceTimes);
        setSelectedBookingForDone(booking);
        setShowDoneModal(true);
      }
    } catch (error) {
      console.error("Error opening done popup:", error);
      Swal.fire("Error", "Failed to open done popup", "error");
    }
  };

  // Function to handle marking services as done from popup
  const handleMarkServicesDone = async () => {
    if (markSelectedDoneLoading) return; // Prevent multiple clicks

    try {
      if (!selectedBookingForDone) return;

      // Get selected services (or all if none selected)
      const selectedServices = doneServiceTimes.filter(st => st.selected);
      const servicesToUpdate = selectedServices.length > 0 ? selectedServices : doneServiceTimes;

      if (servicesToUpdate.length === 0) {
        Swal.fire("Warning", "Please select at least one service", "warning");
        return;
      }

      setMarkSelectedDoneLoading(true);

      // Validate that all services to update have valid start and stop times
      const validationErrors = [];

      for (const service of servicesToUpdate) {
        // Check if start time is missing
        if (!service.startTime) {
          validationErrors.push(`${service.serviceName || 'Service'}: Start time is required`);
          continue;
        }

        // Check if stop time is missing
        if (!service.stopTime) {
          validationErrors.push(`${service.serviceName || 'Service'}: Stop time is required`);
          continue;
        }

        // Validate that stop time is after start time
        const startTime = new Date(service.startTime);
        const stopTime = new Date(service.stopTime);

        if (stopTime <= startTime) {
          validationErrors.push(`${service.serviceName || 'Service'}: Stop time must be after start time`);
        }
      }

      // If there are validation errors, show them and return
      if (validationErrors.length > 0) {
        const errorMessage = validationErrors.length === 1
          ? validationErrors[0]
          : `Please fix the following errors:\n\n${validationErrors.map((err, idx) => `${idx + 1}. ${err}`).join('\n')}`;

        Swal.fire({
          title: 'Validation Error',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK'
        });
        return;
      }

      // Prepare service times for API
      // If times are not set, use From_Time and To_Time from the booking
      const bookingDate = selectedBookingForDone.booking_Date ? new Date(selectedBookingForDone.booking_Date) : new Date();

      const serviceTimes = servicesToUpdate.map(st => {
        // Get the original resource to access from_Time and to_Time
        let originalResource = null;
        selectedBookingForDone.details?.forEach(detail => {
          detail.resourceDetails?.forEach(resource => {
            if (resource.bookingResourceDetail_Id === st.bookingResourceDetail_Id) {
              originalResource = resource;
            }
          });
        });

        // Use provided times, or fall back to from_Time/to_Time, or use booking date with default times
        let startTime = st.startTime;
        let stopTime = st.stopTime;

        if (!startTime && originalResource?.from_Time) {
          startTime = new Date(originalResource.from_Time);
        } else if (!startTime) {
          // Use booking date with a default time (9:00 AM)
          startTime = new Date(bookingDate);
          startTime.setHours(9, 0, 0, 0);
        }

        if (!stopTime && originalResource?.to_Time) {
          stopTime = new Date(originalResource.to_Time);
        } else if (!stopTime) {
          // Use booking date with a default time (10:00 AM)
          stopTime = new Date(bookingDate);
          stopTime.setHours(10, 0, 0, 0);
        }

        return {
          BookingResourceDetail_Id: st.bookingResourceDetail_Id,
          Start_Time: startTime ? formatLocalDateTime(startTime) : null,
          Stop_Time: stopTime ? formatLocalDateTime(stopTime) : null
        };
      });

      // Call API to update service times and mark as done
      const response = await updateServiceTimesForDone(selectedBookingForDone.booking_Id, serviceTimes);

      if (response.isSuccess) {
        Swal.fire("Success", response.message, "success");
        setShowDoneModal(false);
        setSelectedBookingForDone(null);
        setDoneServiceTimes([]);
        loadBookings();
      } else {
        Swal.fire("Error", response.message, "error");
      }
    } catch (error) {
      console.error("Error marking services as done:", error);
      Swal.fire("Error", "Failed to mark services as done", "error");
    } finally {
      setMarkSelectedDoneLoading(false);
    }
  };

  // Helper function to check if any services are pending
  const checkIfAnyServicePending = (booking) => {
    if (!booking.details || booking.details.length === 0) {
      return false;
    }

    for (const detail of booking.details) {
      if (!detail.resourceDetails || detail.resourceDetails.length === 0) {
        continue;
      }

      for (const service of detail.resourceDetails) {
        // Skip cancelled services
        if (service.completionStatus === 'Cancel') {
          continue;
        }

        if (service.completionStatus === 'Pending' || !service.completionStatus) {
          return true;
        }
      }
    }

    return false;
  };

  // Helper function to check if any services are checked in
  const checkIfAnyServiceCheckedIn = (booking) => {
    if (!booking.details || booking.details.length === 0) {
      return false;
    }

    for (const detail of booking.details) {
      if (!detail.resourceDetails || detail.resourceDetails.length === 0) {
        continue;
      }

      for (const service of detail.resourceDetails) {
        if (service.completionStatus === 'Check In') {
          return true;
        }
      }
    }

    return false;
  };

  // Helper function to check if all services are checked in
  const checkIfAllServicesCheckedIn = (booking) => {
    if (!booking.details || booking.details.length === 0) {
      return false;
    }

    for (const detail of booking.details) {
      if (!detail.resourceDetails || detail.resourceDetails.length === 0) {
        return false;
      }

      for (const service of detail.resourceDetails) {
        if (service.completionStatus !== 'Check In' && service.completionStatus !== 'Complete') {
          return false;
        }
      }
    }

    return true;
  };

  // Helper: check if there are any services that are not complete (can be Check In, Start, or Stop)
  const hasServicesNotComplete = (booking) => {
    if (!booking.details || booking.details.length === 0) {
      return false;
    }

    for (const detail of booking.details) {
      if (!detail.resourceDetails || detail.resourceDetails.length === 0) {
        continue;
      }

      for (const service of detail.resourceDetails) {
        // Skip cancelled services
        if (service.completionStatus === 'Cancel') {
          continue;
        }

        // If any non-cancelled service is not 'Complete', return true
        if (service.completionStatus !== 'Complete') {
          return true;
        }
      }
    }

    return false;
  };

  // Helper: check if at least one non-cancelled service has been checked in (status is Check In, Start, or Stop, but not Pending)
  const hasAnyServiceCheckedIn = (booking) => {
    if (!booking.details || booking.details.length === 0) {
      return false;
    }

    for (const detail of booking.details) {
      if (!detail.resourceDetails || detail.resourceDetails.length === 0) {
        continue;
      }

      for (const service of detail.resourceDetails) {
        // Skip cancelled services
        if (service.completionStatus === 'Cancel') {
          continue;
        }

        // If any non-cancelled service has been checked in (Check In, Start, or Stop), return true
        if (service.completionStatus === 'Check In' ||
          service.completionStatus === 'Start' ||
          service.completionStatus === 'Stop') {
          return true;
        }
      }
    }

    return false;
  };

  // Helper: at least one non-cancelled service is eligible to be marked Done
  // Eligible = status in (Check In / Start / Stop) AND employee assigned
  const hasAnyDoneEligibleService = (booking) => {
    if (!booking.details || booking.details.length === 0) return false;

    for (const detail of booking.details) {
      if (!detail.resourceDetails || detail.resourceDetails.length === 0) continue;

      for (const service of detail.resourceDetails) {
        if (service.completionStatus === 'Cancel' || service.completionStatus === 'Complete') continue;

        const isCheckedInIsh =
          service.completionStatus === 'Check In' ||
          service.completionStatus === 'Start' ||
          service.completionStatus === 'Stop';

        if (isCheckedInIsh && service.employee_Id) return true;
      }
    }

    return false;
  };

  // Helper function to check if all non-cancelled services have employees assigned
  const checkIfAllServicesHaveEmployees = (booking) => {
    if (!booking.details || booking.details.length === 0) {
      return false;
    }

    // Check if there's at least one non-cancelled service
    let hasNonCancelledService = false;

    for (const detail of booking.details) {
      if (!detail.resourceDetails || detail.resourceDetails.length === 0) {
        continue;
      }

      for (const service of detail.resourceDetails) {
        // Skip cancelled services
        if (service.completionStatus === 'Cancel') {
          continue;
        }

        hasNonCancelledService = true;

        // Check if employee_Id is null or undefined for non-cancelled services
        if (!service.employee_Id) {
          return false;
        }
      }
    }

    // Return true only if there's at least one non-cancelled service with employee
    return hasNonCancelledService;
  };

  // Function to handle viewing a booking
  const handleView = async (booking) => {
    // Save list scroll so Close View can restore it, then scroll to form top after opening
    lastListScrollYRef.current = window.scrollY || 0;

    // Ensure all required data is loaded before processing
    if (services.length === 0 || packages.length === 0 || employees.length === 0 || rooms.length === 0) {
      try {
        await Promise.all([
          services.length === 0 ? loadServices() : Promise.resolve(),
          packages.length === 0 ? loadPackages() : Promise.resolve(),
          employees.length === 0 ? loadEmployees() : Promise.resolve(),
          rooms.length === 0 ? loadRooms() : Promise.resolve(),
        ]);
      } catch (error) {
        console.error('Error loading required data for view:', error);
        Swal.fire("Error", "Failed to load required data", "error");
        return;
      }
    }

    // Load existing booking details if available
    let selectedServices = [];
    let selectedPackages = [];
    let selectedPackageSeries = [];

    if (booking.details && Array.isArray(booking.details)) {
      for (const detail of booking.details) {
        if (detail.entity_Type === 'ser') {
          const service = services.find(s => s.value === detail.entity_Id);
          if (service) {
            selectedServices.push({
              ...service,
              bookingDetail_Id: detail.bookingDetail_Id,
              is_Cancelled: detail.is_Cancelled,
              cancel_Reason: detail.cancel_Reason
            });
          }
        } else if (detail.entity_Type === 'pac') {
          const pkg = packages.find(p => p.value === detail.entity_Id);
          if (pkg) {
            selectedPackages.push(pkg);
          }
        } else if (detail.entity_Type === 'sep') {
          const series = packageSeries.find(s => s.value === detail.entity_Id);
          if (series) {
            selectedPackageSeries.push(series);
          }
        } else if (detail.entity_Type === 'mem') {
          // Handle membership bookings - get package series from membership
          const membershipSeries = await getPackageSeriesFromMembership(detail.entity_Id);
          if (membershipSeries.length > 0) {
            // For membership, we'll show the first available series
            selectedPackageSeries.push({
              ...membershipSeries[0],
              isMembership: true,
              membershipId: detail.entity_Id
            });
          }
        }
      }
    }

    // Collect all services from resourceDetails (including those from packages and memberships)
    const allServiceIds = new Set();
    if (booking.details && Array.isArray(booking.details)) {
      for (const detail of booking.details) {
        if (detail.resourceDetails && Array.isArray(detail.resourceDetails)) {
          for (const resource of detail.resourceDetails) {
            if (resource.service_Id) {
              allServiceIds.add(resource.service_Id);
            }
          }
        }
      }
    }

    // Add all unique services to selectedServices
    for (const serviceId of allServiceIds) {
      const service = services.find(s => s.value === serviceId);
      if (service && !selectedServices.find(s => s.value === serviceId)) {
        selectedServices.push(service);
      }
    }

    // Handle Source_Id from API for view (can be Source_Id or source_Id)
    const sourceId = booking.Source_Id || booking.source_Id || null;

    // Get employee and room IDs from resource details (first available)
    const firstResource = booking.details?.[0]?.resourceDetails?.[0];
    const employeeId = firstResource?.employee_Id || null;
    const roomId = firstResource?.room_Id || null;

    // Handle Business_Id and Branch_Id from API (can be different casing)
    const businessId = booking.Business_Id || booking.business_Id || null;
    const branchId = booking.Branch_Id || booking.branch_Id || null;

    // Load branches for the selected business if businessId exists
    if (businessId) {
      await loadBranches(businessId);
    }

    setForm({
      booking_Id: booking.booking_Id,
      customer_Id: booking.customer_Id,
      employee_Id: employeeId,
      room_Id: roomId,
      from_Time: booking.from_Time ? new Date(booking.from_Time) : null,
      to_Time: booking.to_Time ? new Date(booking.to_Time) : null,
      entry_Date: new Date(booking.entry_Date),
      booking_Date: new Date(booking.booking_Date),
      is_Cancelled: booking.is_Cancelled,
      cancellation_Reason: booking.cancellation_Reason || "",
      notes: booking.notes || "",
      source_Id: sourceId, // 👈 SET SOURCE_ID FOR VIEW
      sourceOther: "", // Source details field (optional, not stored in booking)
      business_Id: businessId, // 👈 SET BUSINESS_ID FOR VIEW
      branch_Id: branchId, // 👈 SET BRANCH_ID FOR VIEW
      modified_By: booking.modified_By,
      modified_On: booking.modified_On,
      selectedServices: selectedServices,
      selectedPackages: selectedPackages,
      selectedPackageSeries: selectedPackageSeries,
    });

    // Populate service resources for viewing
    const serviceResources = [];
    if (booking.details && Array.isArray(booking.details)) {
      for (const detail of booking.details) {
        if (detail.resourceDetails && Array.isArray(detail.resourceDetails)) {
          for (const resource of detail.resourceDetails) {
            const service = services.find(s => s.value === resource.service_Id);
            const serviceName = service ? service.label : `Service ${resource.service_Id}`;
            const packageName = resource.package_Id ? packages.find(p => p.value === resource.package_Id)?.label : null;
            const employee = employees.find(emp => emp.value === resource.employee_Id);
            const employeeName = employee ? employee.label : (resource.employee_Id ? `Employee ${resource.employee_Id}` : '-');
            const room = rooms.find(room => room.value === resource.room_Id);
            const roomName = room ? room.label : (resource.room_Id ? `Room ${resource.room_Id}` : '-');

            console.log('Processing resource:', {
              serviceId: resource.service_Id,
              serviceName: serviceName,
              packageId: resource.package_Id,
              packageName: packageName,
              employeeId: resource.employee_Id,
              employeeName: employeeName,
              roomId: resource.room_Id,
              roomName: roomName
            });

            console.log('Available data for mapping:', {
              services: services.length,
              packages: packages.length,
              employees: employees.length,
              rooms: rooms.length,
              foundService: service,
              foundPackage: resource.package_Id ? packages.find(p => p.value === resource.package_Id) : null,
              foundEmployee: employee,
              foundRoom: room
            });

            serviceResources.push({
              id: `view-${resource.bookingResourceDetail_Id}`,
              serviceId: resource.service_Id,
              serviceName: serviceName,
              packageId: resource.package_Id,
              packageName: packageName,
              membershipId: resource.membership_Id,
              employeeId: resource.employee_Id,
              employeeName: employeeName,
              roomId: resource.room_Id,
              roomName: roomName,
              equipmentId: resource.equipment_Id,
              fromTime: resource.from_Time ? new Date(resource.from_Time) : null,
              toTime: resource.to_Time ? new Date(resource.to_Time) : null,
              bookingResourceDetail_Id: resource.bookingResourceDetail_Id,
              // Prefer BookingDetail_Id from the resource row (authoritative), fallback to parent detail
              bookingDetail_Id: resource.bookingDetail_Id || detail.bookingDetail_Id,
              completionStatus: resource.completionStatus || null,
              isCancelled: resource.completionStatus === 'Cancel',
              cancellationReason: resource.cancellation_Reason || null,
              checkIn_Time: resource.checkIn_Time || null,
              start_Time: resource.start_Time || null,
              stop_Time: resource.stop_Time || null
            });
          }
        }
      }
    }

    // Sort service resources by fromTime to display chronologically
    serviceResources.sort((a, b) => {
      if (!a.fromTime) return 1;
      if (!b.fromTime) return -1;
      return a.fromTime.getTime() - b.fromTime.getTime();
    });

    setServiceResources(serviceResources);

    // Check if customer has membership for the badge
    await checkCustomerMembership(booking.customer_Id);

    // Fetch and set customer data for display in readonly field
    if (booking.customer_Id) {
      try {
        const customerData = await getCustomers({ customer_Id: booking.customer_Id });
        if (customerData && customerData.length > 0) {
          const customer = customerData[0];
          setSelectedCustomerOption({
            value: customer.customer_Id,
            label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
            customer: customer
          });
        } else {
          // Fallback if customer not found
          setSelectedCustomerOption({
            value: booking.customer_Id,
            label: `Customer ID: ${booking.customer_Id}`
          });
        }
      } catch (error) {
        console.error("Error fetching customer for view:", error);
        // Fallback on error
        setSelectedCustomerOption({
          value: booking.customer_Id,
          label: `Customer ID: ${booking.customer_Id}`
        });
      }
    }

    setIsViewing(true);
    setIsEditing(false);
    setShowForm(true);
    scrollToFormTop();
  };

  // Function to handle individual entity cancellation during edit
  const handleCancelEntity = (entityType, index) => {
    let entityName = "";

    switch (entityType) {
      case 'service':
        entityName = form.selectedServices[index]?.label || 'Service';
        break;
      case 'package':
        entityName = form.selectedPackages[index]?.label || 'Package';
        break;
      case 'series':
        entityName = form.selectedPackageSeries[index]?.label || 'Package Series';
        break;
      default:
        entityName = 'Item';
    }

    // Set the entity to be cancelled and show the modal
    setCancellingEntity({ type: entityType, index: index, name: entityName });
    setEntityCancelReason("");
    setShowEntityCancelModal(true);
  };

  const confirmCancel = async () => {
    if (!cancelReason.trim()) {
      Swal.fire("Validation Error", "Please provide a cancellation reason", "error");
      return;
    }

    try {
      setLoading(true);
      const booking = bookings.find(b => b.booking_Id === cancellingBookingId);
      if (!booking) {
        Swal.fire("Error", "Booking not found", "error");
        return;
      }

      // Get current user ID from localStorage
      const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
      const currentUserId = authUser.userId || authUser.user_Id || 1;

      // Prepare details array with all existing booking details
      const details = [];
      const resourceDetails = [];
      let detailIndex = 1;

      if (booking.details && Array.isArray(booking.details)) {
        booking.details.forEach(detail => {
          const detailId = detailIndex++;
          const detailObj = {
            tempDetailId: detailId,
            bookingDetail_Id: detail.bookingDetail_Id || 0,
            entity_Id: detail.entity_Id,
            entity_Type: detail.entity_Type,
            is_Cancelled: false, // Keep detail as not cancelled, only cancel services
            cancel_Reason: null,
            created_By: detail.created_By || currentUserId,
            created_On: detail.created_On || new Date().toISOString(),
            modified_By: currentUserId,
            modified_On: new Date().toISOString(),
          };
          details.push(detailObj);

          // Process all resource details for this booking detail
          if (detail.resourceDetails && Array.isArray(detail.resourceDetails)) {
            detail.resourceDetails.forEach(resource => {
              const resourceDetailObj = {
                tempDetailId: detailId,
                bookingResourceDetail_Id: resource.bookingResourceDetail_Id || 0,
                bookingDetail_Id: detail.bookingDetail_Id || 0,
                service_Id: resource.service_Id,
                package_Id: resource.package_Id || null,
                membership_Id: resource.membership_Id || null,
                room_Id: resource.room_Id || null,
                equipment_Id: resource.equipment_Id || null,
                employee_Id: resource.employee_Id || null,
                from_Time: resource.from_Time || null,
                to_Time: resource.to_Time || null,
                modified_By: currentUserId,
                modified_On: new Date().toISOString(),
                completionStatus: 'Cancel', // Mark all services as cancelled
                // Per requirement: whole-booking cancel reason should be stored on Booking table, not per service
                cancellation_Reason: resource.cancellation_Reason || null,
                checkIn_Time: resource.checkIn_Time || null,
                start_Time: resource.start_Time || null,
                stop_Time: resource.stop_Time || null,
              };
              resourceDetails.push(resourceDetailObj);
            });
          }
        });
      }

      // Prepare booking data for cancellation
      const cancelData = {
        booking_Id: booking.booking_Id,
        customer_Id: booking.customer_Id,
        entry_Date: booking.entry_Date || new Date().toISOString(),
        booking_Date: booking.booking_Date || booking.booking_Date,
        is_Cancelled: true,
        // Per requirement: whole-booking cancel reason should be stored on Booking table
        cancellation_Reason: cancelReason.trim(),
        notes: booking.notes || booking.Notes || '',
        source_Id: booking.source_Id || booking.Source_Id || null,
        modified_By: currentUserId,
        modified_On: new Date().toISOString(),
        business_Id: booking.Business_Id || booking.business_Id || 1,
        branch_Id: booking.Branch_Id || booking.branch_Id || 1,
        details: details,
        resourceDetails: resourceDetails,
      };

      const result = await saveBooking(cancelData);

      if (result.id && result.msg) {
        Swal.fire("Cancelled!", "Booking has been cancelled.", "success");
        setShowCancelModal(false);
        setCancellingBookingId(null);
        setCancelReason("");
        loadBookings();
      } else {
        Swal.fire("Error", result.message || result.msg || "Failed to cancel booking", "error");
      }
    } catch (error) {
      console.error("Error cancelling booking:", error);
      Swal.fire("Error", "Failed to cancel booking", "error");
    } finally {
      setLoading(false);
    }
  };

  const confirmEntityCancel = async () => {
    if (!entityCancelReason.trim()) {
      Swal.fire("Validation Error", "Please provide a cancellation reason", "error");
      return;
    }

    try {
      const { type, index, name } = cancellingEntity;

      // Update the entity with cancellation reason and mark as cancelled
      switch (type) {
        case 'service':
          const newServices = [...form.selectedServices];
          newServices[index] = { ...newServices[index], cancel_Reason: entityCancelReason.trim(), is_Cancelled: true };
          handleFormChange('selectedServices', newServices);
          break;
        case 'package':
          const newPackages = [...form.selectedPackages];
          newPackages[index] = { ...newPackages[index], cancel_Reason: entityCancelReason.trim(), is_Cancelled: true };
          handleFormChange('selectedPackages', newPackages);
          break;
        case 'series':
          const newSeries = [...form.selectedPackageSeries];
          newSeries[index] = { ...newSeries[index], cancel_Reason: entityCancelReason.trim(), is_Cancelled: true };
          handleFormChange('selectedPackageSeries', newSeries);
          break;
      }

      Swal.fire("Cancelled!", `${name} has been cancelled with reason: ${entityCancelReason.trim()}`, "success");
      setShowEntityCancelModal(false);
      setCancellingEntity({ type: '', index: -1, name: '' });
      setEntityCancelReason("");
    } catch (error) {
      console.error("Error cancelling entity:", error);
      Swal.fire("Error", "Failed to cancel entity", "error");
    }
  };

  // Build flat table data from memberships (grouped by service, aggregated quantities)
  const buildMembershipTableData = () => {
    const membershipData = [];

    // 1. Process membership services (existing logic)
    if (allCustomerMemberships && allCustomerMemberships.length > 0) {
      // Count how many instances of each membership service are already added to serviceResources
      // IMPORTANT: Only count membership services (with membershipId), NOT standalone services
      const addedServiceCounts = {};
      serviceResources
        .filter(r => r.membershipId) // Only count membership services (exclude standalone billed services)
        .forEach(r => {
          const serviceId = r.serviceId;
          const packageId = r.packageId || null;
          // Create composite key to track service+package combination for membership services
          const compositeKey = packageId ? `${serviceId}_${packageId}` : `${serviceId}_direct`;
          addedServiceCounts[compositeKey] = (addedServiceCounts[compositeKey] || 0) + 1;
        });

      // Map to store aggregated service data: { serviceId: { serviceName, availableQty, packageNames, membershipIds, packageIds } }
      // Use composite key: serviceId_packageId to separate services by package
      const serviceMap = new Map();

      // Process all memberships
      allCustomerMemberships.forEach(membership => {
        if (!membership || !membership.details || membership.details.length === 0) return;

        // Filter only pending details and exclude products
        // UPDATED: Use RemainingQty from API instead of counting manually
        const pendingDetails = membership.details.filter(detail =>
          detail.completionStatus === "Pending" &&
          detail.serviceId &&
          !detail.productId && // Exclude products
          (detail.remainingQty || detail.RemainingQty || 0) > 0 // Only include if RemainingQty > 0
        );

        if (pendingDetails.length === 0) return;

        // Process each pending service detail
        pendingDetails.forEach(detail => {
          const serviceId = detail.serviceId;
          const packageId = detail.packageId || null;
          // Get RemainingQty from API (backend calculated)
          const remainingQty = detail.remainingQty || detail.RemainingQty || 0;

          // Get service name
          const service = services.find(s => s.value === serviceId);
          if (!service) return;

          // Get package name if available
          let packageName = null;
          if (packageId) {
            const pkg = packages.find(p => p.value === packageId);
            packageName = pkg ? pkg.label : null;
          }

          // Create composite key: serviceId_packageId (use 'direct' for null packageId)
          const compositeKey = packageId ? `${serviceId}_${packageId}` : `${serviceId}_direct`;

          // Aggregate service data by composite key
          // UPDATED: Use RemainingQty from API instead of counting
          if (!serviceMap.has(compositeKey)) {
            serviceMap.set(compositeKey, {
              serviceId: serviceId,
              serviceName: service.label,
              packageId: packageId,
              packageName: packageName || null,
              availableQty: remainingQty, // Use RemainingQty from API (already accounts for booked services)
              membershipIds: new Set()
            });
          } else {
            // If key already exists, use the maximum RemainingQty (should be same for all details in group)
            const serviceData = serviceMap.get(compositeKey);
            serviceData.availableQty = Math.max(serviceData.availableQty, remainingQty);
          }

          // Add membership ID to the set
          const serviceData = serviceMap.get(compositeKey);
          if (membership.membershipId) {
            serviceData.membershipIds.add(membership.membershipId);
          }
        });
      });

      // Convert Map to array and add to membershipData
      Array.from(serviceMap.values())
        .forEach(serviceData => {
          const serviceId = serviceData.serviceId;
          const packageId = serviceData.packageId;
          // Use composite key to match the counting logic
          const compositeKey = packageId ? `${serviceId}_${packageId}` : `${serviceId}_direct`;
          const alreadyAdded = addedServiceCounts[compositeKey] || 0;
          // UPDATED: serviceData.availableQty already contains RemainingQty from API (accounts for booked services)
          // Still subtract already added services from current session
          const availableQty = Math.max(0, serviceData.availableQty - alreadyAdded);

          // Only include services that still have available quantity
          if (availableQty <= 0) return;

          // Create a unique key for this service+package combination
          const selectionKey = packageId ? `${serviceId}_${packageId}` : `${serviceId}_direct`;

          // Determine membership type from the first membership
          let membershipType = null; // 'package', 'packageSeries', or null
          if (serviceData.membershipIds.size > 0) {
            const firstMembershipId = Array.from(serviceData.membershipIds)[0];
            const membership = allCustomerMemberships.find(m => m.membershipId === firstMembershipId);
            if (membership) {
              const entityType = membership.entity_Type?.toUpperCase();
              if (entityType === 'PAC') {
                membershipType = 'package';
              } else if (entityType === 'SEP') {
                membershipType = 'packageSeries';
              }
            }
          }

          membershipData.push({
            serviceId: serviceId,
            serviceName: serviceData.serviceName,
            availableQty: availableQty,
            packageNames: serviceData.packageName || 'Direct Service',
            packageId: packageId,
            membershipIds: Array.from(serviceData.membershipIds),
            selectionKey: selectionKey,
            membershipType: membershipType,
            isBilledService: false
          });
        });
    }

    // 2. Process billed standalone services
    if (billedStandaloneServices && billedStandaloneServices.length > 0) {
      // Count how many instances of each billed service are already added
      const addedBilledServiceCounts = {};
      serviceResources
        .filter(r => r.invoiceDetailId) // Only count billed services
        .forEach(r => {
          const detailId = r.invoiceDetailId;
          addedBilledServiceCounts[detailId] = (addedBilledServiceCounts[detailId] || 0) + 1;
        });

      billedStandaloneServices.forEach(billedService => {
        // Handle both camelCase and PascalCase from API
        const serviceId = billedService.Service_Id || billedService.service_Id;
        const detailId = billedService.Detail_Id || billedService.detail_Id;
        const alreadyAdded = addedBilledServiceCounts[detailId] || 0;
        const remainingQty = Math.max(0, (billedService.RemainingQuantity || billedService.remainingQuantity || 0) - alreadyAdded);

        // Only include services with remaining quantity > 0
        if (remainingQty > 0) {
          const service = services.find(s => s.value === serviceId);
          if (service) {
            const selectionKey = `billed_${detailId}`;
            membershipData.push({
              serviceId: serviceId,
              serviceName: service.label,
              availableQty: Math.floor(remainingQty), // Convert to integer
              packageNames: 'Standalone Service',
              packageId: null,
              membershipIds: [],
              selectionKey: selectionKey,
              membershipType: null, // Standalone service
              isBilledService: true, // Flag to identify billed services
              invoiceDetailId: detailId,
              invoiceId: billedService.Invoice_Id || billedService.invoice_Id,
              invoiceNo: billedService.Invoice_No || billedService.invoice_No,
              duration: (billedService.Duration || billedService.duration || service.duration || 30),
              invoiceSourceId: billedService.Invoice_SourceId || billedService.invoice_SourceId // Add source ID
            });
          } else {
            // Service not found in services list - use API data directly
            const serviceName = billedService.SpaServiceName_En || billedService.spaServiceName_En ||
              billedService.SpaServiceName_Ar || billedService.spaServiceName_Ar ||
              `Service ${serviceId}`;
            const selectionKey = `billed_${detailId}`;
            membershipData.push({
              serviceId: serviceId,
              serviceName: serviceName,
              availableQty: Math.floor(remainingQty),
              packageNames: 'Standalone Service',
              packageId: null,
              membershipIds: [],
              selectionKey: selectionKey,
              membershipType: null,
              isBilledService: true,
              invoiceDetailId: detailId,
              invoiceId: billedService.Invoice_Id || billedService.invoice_Id,
              invoiceNo: billedService.Invoice_No || billedService.invoice_No,
              duration: (billedService.Duration || billedService.duration || 30),
              invoiceSourceId: billedService.Invoice_SourceId || billedService.invoice_SourceId // Add source ID
            });
          }
        }
      });
    }

    // Sort: Membership services first (by type), then billed standalone services
    return membershipData.sort((a, b) => {
      // Billed services go last
      if (a.isBilledService && !b.isBilledService) return 1;
      if (!a.isBilledService && b.isBilledService) return -1;

      // Within membership services, sort by packageId: services with packageId come first, then direct services
      // Within packages, sort by packageId, then by service name
      // Direct services (packageId is null) come last, sorted by service name
      const aHasPackage = a.packageId !== null;
      const bHasPackage = b.packageId !== null;

      // If one has package and other doesn't, package comes first
      if (aHasPackage && !bHasPackage) return -1;
      if (!aHasPackage && bHasPackage) return 1;

      // If both have packages, sort by packageId, then service name
      if (aHasPackage && bHasPackage) {
        if (a.packageId !== b.packageId) {
          return a.packageId - b.packageId; // Sort by packageId
        }
        return a.serviceName.localeCompare(b.serviceName); // Then by service name
      }

      // Both are direct services, sort by service name
      return a.serviceName.localeCompare(b.serviceName);
    });
  };

  // Membership modal functions
  const openMembershipModal = () => {
    // Open modal if customer has memberships OR billed services
    const hasMemberships = hasMembership && allCustomerMemberships.length > 0;
    const hasBilledServices = billedStandaloneServices && billedStandaloneServices.length > 0;

    if (hasMemberships || hasBilledServices) {
      setShowMembershipModal(true);
      // Reset selections when opening modal
      setSelectedMembershipServices([]);
      setSelectedMembershipPackages([]);
      setSelectedMembershipPackageSeries([]);
      // Reset hierarchical selection to reflect current state
      setHierarchicalSelection({
        packageSeries: {},
        packages: {},
        services: {}
      });
      // Reset table selection
      setTableSelection({});
    }
  };

  const closeMembershipModal = () => {
    setShowMembershipModal(false);
    setSelectedMembershipServices([]);
    setSelectedMembershipPackages([]);
    setSelectedMembershipPackageSeries([]);
    setHierarchicalSelection({
      packageSeries: {},
      packages: {},
      services: {}
    });
    // Reset table selection
    setTableSelection({});
  };

  // Build hierarchical data structure for membership items
  const buildHierarchicalData = (memberships) => {
    // Handle both single membership (backward compatibility) and array of memberships
    const membershipList = Array.isArray(memberships) ? memberships : (memberships ? [memberships] : []);

    if (membershipList.length === 0) return { packageSeries: [], packages: [], services: [], memberships: [] };

    const hierarchicalData = {
      packageSeries: [],
      packages: [],
      services: [],
      memberships: [] // Store membership-organized data
    };

    // Get already added membership services from serviceResources
    const addedMembershipServices = serviceResources
      .filter(r => r.membershipId)
      .map(r => r.serviceId);

    // Process all memberships
    membershipList.forEach(membership => {
      if (!membership || !membership.details || membership.details.length === 0) return;

      // Filter only pending details
      const pendingDetails = membership.details.filter(detail => detail.completionStatus === "Pending");
      if (pendingDetails.length === 0) return;

      // Check membership type
      const isPackageSeriesMembership = membership.entity_Type === "sep" || membership.entity_Type === "SEP";
      const isPackageMembership = membership.entity_Type === "pac" || membership.entity_Type === "PAC";

      if (isPackageSeriesMembership && membership.installments && membership.installments.length > 0) {
        // Handle Package Series Membership (has installments)
        const uniqueSeriesIds = [...new Set(membership.installments.map(installment => installment.seriesId).filter(Boolean))];

        uniqueSeriesIds.forEach(seriesId => {
          const series = packageSeries.find(s => s.value === seriesId);
          if (series) {
            // Create a unique series key that includes membershipId to keep memberships separate
            const seriesKey = `${membership.membershipId}-${seriesId}`;
            // Check if this series already exists in hierarchicalData (by membership)
            let existingSeries = hierarchicalData.packageSeries.find(s => s.membershipId === membership.membershipId && s.value === seriesId);

            if (!existingSeries) {
              existingSeries = {
                ...series,
                value: seriesKey, // Use composite key for series too
                originalValue: seriesId, // Keep original for lookup
                membershipId: membership.membershipId,
                packages: []
              };
              hierarchicalData.packageSeries.push(existingSeries);
            }

            // Get packages from membership details that belong to this series
            // For series memberships, we need to match packages from details
            const uniquePackageIds = [...new Set(pendingDetails.map(detail => detail.packageId).filter(Boolean))];

            uniquePackageIds.forEach(packageId => {
              const pkg = packages.find(p => p.value === packageId);
              if (pkg) {
                // Check if this package already exists in the series for this membership
                let existingPackage = existingSeries.packages.find(p =>
                  p.value === packageId && p.membershipId === membership.membershipId
                );

                if (!existingPackage) {
                  existingPackage = {
                    ...pkg,
                    seriesId,
                    membershipId: membership.membershipId,
                    services: []
                  };
                  existingSeries.packages.push(existingPackage);
                }

                // Get services that belong to this package from this membership
                const packageServices = pendingDetails
                  .filter(detail => detail.packageId === packageId && detail.membershipId === membership.membershipId)
                  .map(detail => detail.serviceId)
                  .filter((serviceId, index, self) => serviceId && self.indexOf(serviceId) === index);

                packageServices.forEach(serviceId => {
                  // Check if service is already added
                  if (!addedMembershipServices.includes(serviceId)) {
                    const service = services.find(s => s.value === serviceId);
                    if (service && !existingPackage.services.find(s => s.value === serviceId)) {
                      existingPackage.services.push({
                        ...service,
                        packageId,
                        seriesId,
                        membershipId: membership.membershipId
                      });
                    }
                  }
                });
              }
            });
          }
        });
      } else if (isPackageMembership || (!isPackageSeriesMembership && pendingDetails.length > 0)) {
        // Handle Package Membership (no installments, has details with packages and services)
        // Group by package
        const packagesMap = new Map();

        pendingDetails.forEach(detail => {
          if (!detail.packageId || !detail.serviceId) return;
          if (addedMembershipServices.includes(detail.serviceId)) return;

          // Use composite key for packages: membershipId-packageId
          const packageKey = `${membership.membershipId}-${detail.packageId}`;
          if (!packagesMap.has(packageKey)) {
            const pkg = packages.find(p => p.value === detail.packageId);
            if (pkg) {
              packagesMap.set(packageKey, {
                ...pkg,
                services: [],
                membershipId: membership.membershipId,
                compositeKey: packageKey
              });
            }
          }

          const packageData = packagesMap.get(packageKey);
          if (packageData) {
            const service = services.find(s => s.value === detail.serviceId);
            if (service && !packageData.services.find(s => s.value === detail.serviceId && s.membershipId === membership.membershipId)) {
              packageData.services.push({
                ...service,
                packageId: detail.packageId,
                membershipId: membership.membershipId
              });
            }
          }
        });

        // Add standalone packages - create a series per membership to keep them separate
        const standalonePackages = Array.from(packagesMap.values()).filter(pkg => pkg.services.length > 0);

        if (standalonePackages.length > 0) {
          // Create a unique standalone series key per membership
          const standaloneSeriesKey = `standalone-${membership.membershipId}`;
          let standaloneSeries = hierarchicalData.packageSeries.find(s => s.value === standaloneSeriesKey);

          if (!standaloneSeries) {
            standaloneSeries = {
              value: standaloneSeriesKey,
              originalValue: 'standalone',
              label: 'Standalone Packages',
              membershipId: membership.membershipId,
              packages: []
            };
            hierarchicalData.packageSeries.push(standaloneSeries);
          }

          standalonePackages.forEach(pkg => {
            // Check if package already exists (by composite key or value)
            if (!standaloneSeries.packages.find(p =>
              (p.compositeKey && p.compositeKey === pkg.compositeKey) ||
              (p.membershipId === pkg.membershipId && p.value === pkg.value)
            )) {
              standaloneSeries.packages.push(pkg);
            }
          });
        }
      }
    });

    // Remove series that have no packages with services
    hierarchicalData.packageSeries = hierarchicalData.packageSeries.filter(series => {
      const packagesWithServices = series.packages.filter(pkg => pkg.services && pkg.services.length > 0);
      if (packagesWithServices.length === 0) return false;
      // Update series packages to only include those with services
      series.packages = packagesWithServices;
      return true;
    });

    return hierarchicalData;
  };

  // Helper function to create composite key
  const getCompositeKey = (membershipId, itemId) => {
    return `${membershipId}-${itemId}`;
  };

  // Handle table-based service selection
  const handleTableServiceSelection = (selectionKey, checked) => {
    setTableSelection(prev => {
      const newSelection = { ...prev };
      if (checked) {
        // Get service data to set default required quantity
        const tableData = buildMembershipTableData();
        const serviceData = tableData.find(s => s.selectionKey === selectionKey);
        if (serviceData) {
          newSelection[selectionKey] = {
            selected: true,
            requiredQty: 1, // Default to 1 instead of available quantity
            membershipIds: serviceData.membershipIds,
            packageId: serviceData.packageId,
            serviceId: serviceData.serviceId,
            invoiceDetailId: serviceData.invoiceDetailId || null // Include invoice detail ID for billed services
          };
        }
      } else {
        delete newSelection[selectionKey];
      }
      return newSelection;
    });
  };

  // Handle required quantity change
  const handleRequiredQtyChange = (selectionKey, qty) => {
    setTableSelection(prev => {
      const newSelection = { ...prev };
      if (newSelection[selectionKey]) {
        // Get available quantity from table data
        const tableData = buildMembershipTableData();
        const serviceData = tableData.find(s => s.selectionKey === selectionKey);
        const availableQty = serviceData ? serviceData.availableQty : 0;

        // Allow empty string for deletion, otherwise parse the value
        if (qty === '' || qty === null || qty === undefined) {
          // Allow empty value so user can delete and type new number
          newSelection[selectionKey] = {
            ...newSelection[selectionKey],
            requiredQty: '' // Store as empty string temporarily
          };
        } else {
          const requestedQty = parseInt(qty);
          // Validate: must be at least 1 and not more than available
          if (!isNaN(requestedQty) && requestedQty > 0) {
            newSelection[selectionKey] = {
              ...newSelection[selectionKey],
              requiredQty: Math.min(requestedQty, availableQty)
            };
          } else {
            // If invalid or 0, allow empty for user to type
            newSelection[selectionKey] = {
              ...newSelection[selectionKey],
              requiredQty: ''
            };
          }
        }
      }
      return newSelection;
    });
  };

  // Handle hierarchical checkbox selection
  const handleHierarchicalSelection = (type, id, checked, parentId = null, membershipId = null) => {
    setHierarchicalSelection(prev => {
      const newSelection = { ...prev };

      if (type === 'packageSeries') {
        newSelection.packageSeries[id] = checked;

        // If package series is selected/deselected, update all its packages and services
        const seriesData = hierarchicalData.packageSeries.find(s => s.value === id);
        if (seriesData) {
          seriesData.packages.forEach(pkg => {
            const packageKey = pkg.membershipId ? getCompositeKey(pkg.membershipId, pkg.value) : pkg.value;
            newSelection.packages[packageKey] = checked;
            pkg.services.forEach(service => {
              const serviceKey = service.membershipId ? getCompositeKey(service.membershipId, service.value) : service.value;
              newSelection.services[serviceKey] = checked;
            });
          });
        }
      } else if (type === 'package') {
        // id is already a composite key if membershipId is provided
        const packageKey = membershipId ? getCompositeKey(membershipId, id) : id;
        newSelection.packages[packageKey] = checked;

        // If package is selected/deselected, update all its services
        const packageData = hierarchicalData.packageSeries
          .flatMap(s => s.packages)
          .find(p => {
            if (membershipId) {
              return p.membershipId === membershipId && p.value === id;
            }
            return p.value === id;
          });
        if (packageData) {
          packageData.services.forEach(service => {
            const serviceKey = service.membershipId ? getCompositeKey(service.membershipId, service.value) : service.value;
            newSelection.services[serviceKey] = checked;
          });
        }

        // Check if all packages in the series are selected
        const seriesData = hierarchicalData.packageSeries.find(s => s.value === parentId);
        if (seriesData) {
          const allPackagesSelected = seriesData.packages.every(pkg => {
            const pkgKey = pkg.membershipId ? getCompositeKey(pkg.membershipId, pkg.value) : pkg.value;
            // Check if this is the package being toggled
            if (pkg.membershipId === membershipId && pkg.value === id) {
              return checked;
            }
            // Otherwise check if it's already selected
            return newSelection.packages[pkgKey] || false;
          });
          newSelection.packageSeries[parentId] = allPackagesSelected;
        }
      } else if (type === 'service') {
        // id is the serviceId, membershipId is passed separately
        const serviceKey = membershipId ? getCompositeKey(membershipId, id) : id;
        newSelection.services[serviceKey] = checked;

        // Check if all services in the package are selected
        // parentId is the composite package key (membershipId-packageId)
        const packageData = hierarchicalData.packageSeries
          .flatMap(s => s.packages)
          .find(p => {
            const pkgKey = p.membershipId ? getCompositeKey(p.membershipId, p.value) : p.value;
            return pkgKey === parentId;
          });
        if (packageData) {
          const allServicesSelected = packageData.services.every(service => {
            const serviceKey = service.membershipId ? getCompositeKey(service.membershipId, service.value) : service.value;
            return service.value === id && service.membershipId === membershipId ? checked : newSelection.services[serviceKey];
          });
          const pkgKey = packageData.membershipId ? getCompositeKey(packageData.membershipId, packageData.value) : packageData.value;
          newSelection.packages[pkgKey] = allServicesSelected;

          // Check if all packages in the series are selected
          const seriesData = hierarchicalData.packageSeries.find(s =>
            s.packages.some(p => {
              const pKey = p.membershipId ? getCompositeKey(p.membershipId, p.value) : p.value;
              return pKey === pkgKey;
            })
          );
          if (seriesData) {
            const allPackagesSelected = seriesData.packages.every(pkg => {
              const pKey = pkg.membershipId ? getCompositeKey(pkg.membershipId, pkg.value) : pkg.value;
              return newSelection.packages[pKey];
            });
            newSelection.packageSeries[seriesData.value] = allPackagesSelected;
          }
        }
      }

      return newSelection;
    });
  };

  // Build hierarchical data when modal opens - use all memberships
  const hierarchicalData = allCustomerMemberships.length > 0 ? buildHierarchicalData(allCustomerMemberships) : { packageSeries: [], packages: [], services: [] };

  const applyMembershipSelections = async () => {
    // Get selected services from table selection
    const selectedServices = [];
    const tableData = buildMembershipTableData();

    // Process table selections
    Object.keys(tableSelection).forEach(selectionKey => {
      const selection = tableSelection[selectionKey];
      if (selection && selection.selected) {
        const serviceData = tableData.find(s => s.selectionKey === selectionKey);
        if (serviceData) {
          const service = services.find(s => s.value === serviceData.serviceId);
          if (service) {
            // Get required quantity (default to 1 if missing)
            let requiredQty = selection.requiredQty;
            if (!requiredQty || requiredQty === '' || requiredQty === 0) {
              requiredQty = 1;
            }
            requiredQty = Math.min(requiredQty, serviceData.availableQty);

            // Check if this is a billed service
            if (serviceData.isBilledService && serviceData.invoiceDetailId) {
              // Handle billed standalone services
              for (let i = 0; i < requiredQty; i++) {
                selectedServices.push({
                  ...service,
                  membershipId: null,
                  packageId: null,
                  detailId: null,
                  invoiceDetailId: serviceData.invoiceDetailId, // Link to invoice detail
                  duration: serviceData.duration || service.duration || 30,
                  invoiceSourceId: serviceData.invoiceSourceId // Add source ID
                });
              }
            } else {
              // Handle membership services (existing logic)
              const membershipDetails = [];
              allCustomerMemberships.forEach(membership => {
                if (!membership.details) return;
                const pendingDetails = membership.details.filter((detail) => (
                  detail.serviceId === serviceData.serviceId &&
                  detail.completionStatus === "Pending" &&
                  !detail.productId &&
                  selection.membershipIds.includes(membership.membershipId) &&
                  (serviceData.packageId === null ? detail.packageId === null : detail.packageId === serviceData.packageId)
                ));
                membershipDetails.push(...pendingDetails);
              });

              // Add services up to required quantity
              for (let i = 0; i < requiredQty && i < membershipDetails.length; i++) {
                const detail = membershipDetails[i];
                selectedServices.push({
                  ...service,
                  membershipId: detail.membershipId,
                  packageId: detail.packageId,
                  detailId: detail.detailId || null,
                  invoiceDetailId: null // No invoice link for membership services
                });
              }
            }
          }
        }
      }
    });

    // Automatically update form's source_Id if a billed service with source is selected
    const billedServiceWithSource = selectedServices.find(s => s.invoiceSourceId);
    if (billedServiceWithSource && billedServiceWithSource.invoiceSourceId) {
      setForm(prev => ({
        ...prev,
        source_Id: billedServiceWithSource.invoiceSourceId
      }));
    }

    // Update service resources for membership selections
    const newServiceResources = [...serviceResources];

    // Calculate time slots for membership services - start after the last one if any exist
    const bookingDate = new Date(form.booking_Date);
    let baseTime = new Date(bookingDate);
    baseTime.setHours(9, 0, 0, 0); // Default to 9:00 AM if no resources exist

    if (serviceResources.length > 0) {
      let latestResEndTime = baseTime;
      serviceResources.forEach(resource => {
        if (resource.toTime) {
          const resourceToTime = new Date(resource.toTime);
          if (resourceToTime > latestResEndTime) {
            latestResEndTime = resourceToTime;
          }
        }
      });
      baseTime = latestResEndTime;
    }

    let cumulativeMinutes = 0;

    // Add selected services to service resources
    selectedServices.forEach(service => {
      // Create unique resource ID based on service type
      const resourceId = service.invoiceDetailId
        ? `billed-srv-${service.serviceId}-${service.invoiceDetailId}-${Date.now()}-${Math.random()}`
        : `mem-srv-${service.serviceId}-${service.membershipId}-${service.detailId || Date.now()}`;

      // Check if this exact service instance is already added
      const isDuplicate = service.invoiceDetailId
        ? newServiceResources.find(r =>
          r.serviceId === service.value &&
          r.invoiceDetailId === service.invoiceDetailId
        )
        : newServiceResources.find(r =>
          r.serviceId === service.value &&
          r.membershipId === service.membershipId &&
          r.detailId === service.detailId
        );

      if (!isDuplicate) {
        const packageId = service.packageId || null;
        const packageName = packageId ? packages.find(p => p.value === packageId)?.label : null;

        // Get service duration
        const serviceFromList = services.find(s => s.value === service.value);
        const duration = service.duration || serviceFromList?.duration || 30;

        // Calculate time slots using service duration
        const fromTime = new Date(baseTime.getTime() + (cumulativeMinutes * 60000));
        const toTime = new Date(fromTime.getTime() + (duration * 60000));

        newServiceResources.push({
          id: resourceId,
          serviceId: service.value,
          serviceName: service.label,
          packageId: packageId,
          packageName: packageName,
          membershipId: service.membershipId,
          detailId: service.detailId,
          invoiceDetailId: service.invoiceDetailId || null, // Add invoice detail ID for billed services
          employeeId: null,
          roomId: null,
          equipmentId: null,
          fromTime: fromTime,
          toTime: toTime
        });

        // Update cumulative minutes for next service
        cumulativeMinutes += duration;
      }
    });

    setServiceResources(newServiceResources);
    closeMembershipModal();
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      // Clean filter - remove empty strings and convert to proper format
      const cleanFilter = {
        ...(filter.customer_Id !== null && filter.customer_Id !== undefined && { Customer_Id: filter.customer_Id }),
        ...(filter.employee_Id !== null && filter.employee_Id !== undefined && { Employee_Id: filter.employee_Id }),
        ...(filter.room_Id !== null && filter.room_Id !== undefined && { Room_Id: filter.room_Id }),
        ...(filter.from_Date && { From_Date: filter.from_Date }),
        ...(filter.to_Date && { To_Date: filter.to_Date }),
        ...(filter.business_Id !== null && filter.business_Id !== undefined && { Business_Id: filter.business_Id }),
        ...(filter.branch_Id !== null && filter.branch_Id !== undefined && { Branch_Id: filter.branch_Id })
      };

      await exportBookingsToExcel(cleanFilter);
      Swal.fire("Success", "Booking report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting bookings:", error);
      Swal.fire("Error", "Failed to export booking report", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleExportDetails = async (bookingId) => {
    try {
      setLoading(true);
      await exportBookingDetailsToExcel(bookingId);
      Swal.fire("Success", "Booking details exported successfully", "success");
    } catch (error) {
      console.error("Error exporting booking details:", error);
      Swal.fire("Error", "Failed to export booking details", "error");
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = () => {
    setPage(1);
    loadBookings();
  };

  const clearFilter = () => {
    // Preserve session companyId/branchId when clearing filter
    const clearedFilter = getInitialFilter();
    setFilter(clearedFilter);
    setPage(1);
    setFilterKey(prev => prev + 1);
    setCustomerSearchOptions([]);
    setSelectedCustomerFilterOption(null);
    // Set selected options based on session values
    if (session.companyId && businesses.length > 0) {
      const companyId = typeof session.companyId === 'string' ? parseInt(session.companyId) : session.companyId;
      const businessOption = businesses.find(b => b.business_Id === companyId);
      if (businessOption) {
        setSelectedBusinessFilterOption({
          value: businessOption.business_Id,
          label: businessOption.business_NameEn || businessOption.business_Name
        });
      }
    } else {
      setSelectedBusinessFilterOption(null);
    }
    if (session.branchId && branches.length > 0 && clearedFilter.business_Id) {
      const branchId = typeof session.branchId === 'string' ? parseInt(session.branchId) : session.branchId;
      const branchOption = branches.find(b => b.branch_Id === branchId && b.branch_Buisness_Id === clearedFilter.business_Id);
      if (branchOption) {
        setSelectedBranchFilterOption({
          value: branchOption.branch_Id,
          label: branchOption.branch_NameEn || branchOption.branch_Name
        });
      }
    } else {
      setSelectedBranchFilterOption(null);
    }
    loadBookings(); // Use the main loadBookings function for consistency
  };


  const formatDateTime = (dateTimeStr) => {
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleString();
    } catch (error) {
      return "N/A";
    }
  };

  const formatTime = (dateTimeStr) => {
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return "N/A";
    }
  };

  const formatDate = (dateTimeStr) => {
    try {
      const date = new Date(dateTimeStr);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return "N/A";
    }
  };

  // Format date as local datetime string (YYYY-MM-DDTHH:mm:ss) without timezone conversion
  const formatLocalDateTime = (date) => {
    try {
      if (!date) return null;
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      const seconds = String(d.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    } catch (error) {
      console.error('Error formatting local datetime:', error);
      return null;
    }
  };

  // Helper function to check if a booking date is before today
  const isBookingPast = (bookingDateStr) => {
    try {
      if (!bookingDateStr) return false;
      const bookingDate = new Date(bookingDateStr);
      const today = new Date();

      // Reset time to midnight for date comparison
      bookingDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      return bookingDate < today;
    } catch (error) {
      return false;
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.value === customerId);
    if (!customer) return "N/A";

    const customerName = customer.label || customer.customer?.customer_NameEn || "N/A";
    const phoneNumber = customer.customer?.customer_ContactNo || "N/A";

    return `${customerName} - ${phoneNumber}`;
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(e => e.value === employeeId);
    return employee ? employee.label : "N/A";
  };

  const getRoomName = (roomId) => {
    const room = rooms.find(r => r.value === roomId);
    return room ? room.label : "N/A";
  };

  // Get availability status for employee
  const getEmployeeAvailability = (employeeId) => {
    const key = `Employee_${employeeId}`;
    return availabilityResults[key] || { isAvailable: true, conflicts: [] };
  };

  // Get availability status for room
  const getRoomAvailability = (roomId) => {
    const key = `Room_${roomId}`;
    return availabilityResults[key] || { isAvailable: true, conflicts: [] };
  };

  // Create employee option with availability indicator
  const createEmployeeOption = (employee) => {
    const availability = getEmployeeAvailability(employee.value);
    const isSelected = form.employee_Id === employee.value;

    return {
      ...employee,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{employee.label}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {availability.isAvailable ? (
              <span style={{ color: '#28a745', fontSize: '12px' }}>
                ✓ Available
              </span>
            ) : (
              <span style={{ color: '#dc3545', fontSize: '12px' }}>
                ✗ Busy
              </span>
            )}
            {checkingAvailability && (
              <div style={{ width: '12px', height: '12px', border: '2px solid #f3f3f3', borderTop: '2px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
            )}
          </div>
        </div>
      ),
      isDisabled: !availability.isAvailable && !isSelected
    };
  };

  // Create room option with availability indicator
  const createRoomOption = (room) => {
    const availability = getRoomAvailability(room.value);
    const isSelected = form.room_Id === room.value;

    return {
      ...room,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{room.label}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {availability.isAvailable ? (
              <span style={{ color: '#28a745', fontSize: '12px' }}>
                ✓ Available
              </span>
            ) : (
              <span style={{ color: '#dc3545', fontSize: '12px' }}>
                ✗ Busy
              </span>
            )}
            {checkingAvailability && (
              <div style={{ width: '12px', height: '12px', border: '2px solid #f3f3f3', borderTop: '2px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
            )}
          </div>
        </div>
      ),
      isDisabled: !availability.isAvailable && !isSelected
    };
  };

  const getStatusBadge = (booking) => {
    if (booking.is_Cancelled) {
      return <span className="badge bg-danger">Cancelled</span>;
    }

    if (areAllServicesComplete(booking)) {
      return <span className="badge bg-info">Completed</span>;
    }

    return <span className="badge bg-success">Active</span>;
  };


  return (
    <React.Fragment>
      <style>{spinnerStyle}</style>
      <div className="page-content">
        <Container fluid style={{ paddingTop: '0', paddingBottom: '0' }}>
          <Row style={{ marginTop: '8px', marginBottom: '0' }}>
            <Col lg="12" style={{ paddingTop: '0' }}>
              <Breadcrumb title="Booking Management" breadcrumbItem="Spa" />
              <Card style={{ marginTop: '0' }}>
                <CardBody style={{ paddingTop: '8px', paddingBottom: '8px' }}>

                  {/* Form - Only show when editing or viewing */}
                  {showForm && (isEditing || isViewing) && (
                    <>
                      <div ref={formTopRef} />
                      <Card className="mb-4">
                        <CardHeader>
                          <h5 className="card-title mb-0">
                            {isViewing ? "View Booking" : "Edit Booking"}
                          </h5>
                        </CardHeader>
                        <CardBody>
                          <Form onSubmit={handleSubmit}>
                            <Row>
                              <Col md={6}>
                                <FormGroup className="mb-3">
                                  <Label for="business_Id">Company <span className="text-danger">*</span></Label>
                                  <Select
                                    value={businessOptions.find(option => option.value === form.business_Id) || null}
                                    options={businessOptions}
                                    placeholder="Select Company"
                                    styles={selectStyles}
                                    isDisabled={true}
                                  />
                                </FormGroup>
                              </Col>
                              <Col md={6}>
                                <FormGroup className="mb-3">
                                  <Label for="branch_Id">Branch <span className="text-danger">*</span></Label>
                                  <Select
                                    value={branchOptions.find(option => option.value === form.branch_Id) || null}
                                    options={branchOptions}
                                    placeholder="Select Branch"
                                    styles={selectStyles}
                                    isDisabled={true}
                                  />
                                </FormGroup>
                              </Col>
                            </Row>

                            <Row>
                              <Col md="6">
                                <FormGroup className="mb-3">
                                  <Label for="customer" className="mb-2">
                                    Customer *
                                    {customerHasBillableItems && (
                                      <span className="badge bg-success ms-2">
                                        <i className="mdi mdi-crown me-1"></i>
                                        Membership
                                      </span>
                                    )}
                                  </Label>
                                  <div className="d-flex gap-2 align-items-start">
                                    <div style={{ flex: 1 }}>
                                      <div className="d-flex gap-2">
                                        <div style={{ flex: 1 }}>
                                          <Select
                                            key={`customer-${formKey}`}
                                            value={selectedCustomerOption}
                                            onChange={(option) => {
                                              setSelectedCustomerOption(option);
                                              handleFormChange('customer_Id', option?.value);
                                            }}
                                            options={customerSearchOptions}
                                            placeholder="Type at least 3 characters to search customers..."
                                            styles={selectStyles}
                                            isClearable
                                            isSearchable
                                            isDisabled={isEditing || isViewing}
                                            isLoading={customerSearchLoading}
                                            noOptionsMessage={() => "Type at least 3 characters to search"}
                                            loadingMessage={() => "Searching customers..."}
                                            filterOption={() => true}
                                            onInputChange={(inputValue) => {
                                              console.log('🔤 Input changed:', inputValue);

                                              // Clear previous timeout
                                              if (searchTimeout) {
                                                clearTimeout(searchTimeout);
                                              }

                                              // Set new timeout
                                              const timeout = setTimeout(() => {
                                                searchCustomersManually(inputValue);
                                              }, 300);
                                              setSearchTimeout(timeout);

                                              return inputValue;
                                            }}
                                            onMenuOpen={() => console.log('📂 Menu opened')}
                                            onMenuClose={() => console.log('📁 Menu closed')}
                                          />
                                        </div>
                                        <Button
                                          color="info"
                                          size="sm"
                                          onClick={() => setShowAdvancedSearchModal(true)}
                                          disabled={isEditing || isViewing}
                                          title="Advanced Customer Search"
                                          style={{ height: '38px' }}
                                        >
                                          <Search size={16} />
                                        </Button>
                                        <Button
                                          color="success"
                                          size="sm"
                                          onClick={() => setShowQuickAddCustomer(true)}
                                          disabled={isEditing || isViewing}
                                          title="Quick Add Customer"
                                          style={{ height: '38px' }}
                                        >
                                          <i className="fas fa-plus"></i>
                                        </Button>
                                        {selectedCustomerOption?.value && (
                                          <>
                                            <Button
                                              color="info"
                                              size="sm"
                                              onClick={() => setShowCustomerProfile(true)}
                                              disabled={isViewing}
                                              title="View Customer Profile"
                                              style={{ height: '38px' }}
                                            >
                                              <User size={16} />
                                            </Button>
                                            <Button
                                              type="button"
                                              color="warning"
                                              size="sm"
                                              onClick={() => setShowConsentModal(true)}
                                              disabled={isEditing || isViewing}
                                              title="Sign Consent Form"
                                              style={{ height: '38px' }}
                                            >
                                              <i className="fas fa-signature"></i>
                                            </Button>
                                          </>
                                        )}
                                        {customerHasBillableItems && (
                                          <Button
                                            color="info"
                                            size="sm"
                                            onClick={openMembershipModal}
                                            disabled={isViewing}
                                            title="Select Billed Services"
                                            style={{ height: '38px' }}
                                          >
                                            <i className="mdi mdi-crown"></i>
                                          </Button>
                                        )}
                                      </div>
                                      {/* Show note if customer has billable items */}
                                      {customerHasBillableItems && (
                                        <div className="mt-1" style={{ fontSize: '0.75rem' }}>
                                          <span className="text-muted">This customer has billed services. Click the <i className="mdi mdi-crown text-info" style={{ fontSize: '0.875rem' }}></i> icon to view.</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </FormGroup>
                              </Col>
                              <Col md="6">
                                <FormGroup className="mb-3">
                                  <Label for="bookingDate" className="mb-2">Booking Date *</Label>
                                  <div className="d-flex align-items-center gap-2">
                                    <DatePicker
                                      selected={form.booking_Date}
                                      onChange={(date) => handleFormChange('booking_Date', date)}
                                      className="form-control"
                                      dateFormat="dd/MM/yyyy"
                                      placeholderText="Select Date"
                                      disabled={isViewing || isEditing}
                                      style={{ flex: 1 }}
                                      title={isEditing ? "Use Reschedule button to change booking date" : ""}
                                    />
                                    {isEditing && !isViewing && form.booking_Id && form.booking_Id > 0 && (() => {
                                      // Check if booking is completed or cancelled
                                      const currentBooking = bookings.find(b => b.booking_Id === form.booking_Id);
                                      const isCompleted = currentBooking ? (
                                        currentBooking.completionStatus === 'Complete' ||
                                        currentBooking.CompletionStatus === 'Complete' ||
                                        currentBooking.is_Cancelled === true ||
                                        currentBooking.Is_Cancelled === true
                                      ) : false;

                                      return (
                                        <Button
                                          color="info"
                                          size="sm"
                                          onClick={handleOpenReschedule}
                                          disabled={!form.booking_Date || isCompleted}
                                          title={isCompleted ? "Cannot reschedule a completed or cancelled booking" : "Reschedule Booking"}
                                          style={{ whiteSpace: 'nowrap' }}
                                        >
                                          <i className="mdi mdi-calendar-clock me-1"></i>
                                          Reschedule
                                        </Button>
                                      );
                                    })()}
                                  </div>
                                </FormGroup>
                              </Col>
                            </Row>

                            {/* New row for packages and services */}
                            <Row>
                              <Col md="6">
                                <FormGroup className="mb-3">
                                  <Label for="packages" className="mb-2">Packages</Label>
                                  <Select
                                    key={`packages-${formKey}`}
                                    value={form.selectedPackages}
                                    onChange={(options) => handleFormChange('selectedPackages', options || [])}
                                    options={packages}
                                    placeholder="Select Packages"
                                    styles={selectStyles}
                                    isMulti
                                    isClearable={false}
                                    isSearchable
                                    isDisabled={isViewing}
                                    components={{
                                      MultiValueRemove: () => null
                                    }}
                                  />
                                </FormGroup>
                              </Col>
                              <Col md="6">
                                <FormGroup className="mb-3">
                                  <Label for="services" className="mb-2">Services</Label>
                                  <Select
                                    key={`services-${formKey}`}
                                    value={form.selectedServices}
                                    onChange={(options) => handleFormChange('selectedServices', options || [])}
                                    options={services}
                                    placeholder="Select Services"
                                    styles={selectStyles}
                                    isMulti
                                    isClearable={false}
                                    isSearchable
                                    isDisabled={isViewing}
                                    components={{
                                      MultiValueRemove: () => null
                                    }}
                                  />
                                </FormGroup>
                              </Col>
                              {/* Package Series dropdown - commented out for now
                          <Col md="4">
                            <FormGroup className="mb-3">
                              <Label for="packageSeries" className="mb-2">Package Series</Label>
                              <Select
                                key={`packageSeries-${formKey}`}
                                value={form.selectedPackageSeries}
                                onChange={(options) => handleFormChange('selectedPackageSeries', options || [])}
                                options={packageSeries}
                                placeholder="Select Package Series"
                                styles={selectStyles}
                                isMulti
                                isClearable={!isEditing}
                                isSearchable
                                isDisabled={isViewing}
                              />
                            </FormGroup>
                          </Col>
                          */}
                            </Row>

                            {/* Service Resource Allocation */}
                                            {serviceResources.length > 0 && (
                              <Row>
                                <Col md="12">
                                  <FormGroup className="mb-3">
                                    <Label className="mb-2">Service Resource Allocation</Label>
                                    <div className="border rounded p-3">
                                      <Table responsive striped>
                                        <thead>
                                          <tr>
                                            <th style={{ width: '20%', minWidth: '150px', verticalAlign: 'middle' }}>Service</th>
                                            <th style={{ width: '12%' }}>From Time</th>
                                            <th style={{ width: '12%' }}>To Time</th>
                                            <th style={{ width: '18%', minWidth: '180px', verticalAlign: 'middle' }}>Employee</th>
                                            <th style={{ width: '15%' }}>Room</th>
                                            <th style={{ width: '18%' }}>Equipment</th>
                                            {!isEditing && !isViewing && <th style={{ width: '5%', textAlign: 'center' }}>Remove</th>}
                                            {(isEditing || isViewing) && <th style={{ width: '8%', textAlign: 'center' }}>Actions</th>}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {serviceResources.map((resource) => {
                                            // Determine membership type and styling
                                            let membershipType = null;
                                            const isBilledService = resource.invoiceDetailId !== null && resource.invoiceDetailId !== undefined;

                                            if (resource.membershipId) {
                                              const membership = allCustomerMemberships.find(m => m.membershipId === resource.membershipId);
                                              if (membership) {
                                                const entityType = membership.entity_Type?.toUpperCase();
                                                if (entityType === 'PAC') {
                                                  membershipType = 'package';
                                                } else if (entityType === 'SEP') {
                                                  membershipType = 'packageSeries';
                                                }
                                              }
                                            }

                                            const getRowStyle = () => {
                                              // Priority 1: Standalone billed service (orange)
                                              if (isBilledService) {
                                                return {
                                                  backgroundColor: '#fff3e0',
                                                  borderLeft: '4px solid #ff9800'
                                                };
                                              }
                                              // Priority 2: Package membership (blue)
                                              if (membershipType === 'package') {
                                                return {
                                                  backgroundColor: '#e7f3ff',
                                                  borderLeft: '4px solid #0066cc'
                                                };
                                              }
                                              // Priority 3: Package series membership (green)
                                              if (membershipType === 'packageSeries') {
                                                return {
                                                  backgroundColor: '#e8f5e9',
                                                  borderLeft: '4px solid #2e7d32'
                                                };
                                              }
                                              // Priority 4: Regular package (non-membership)
                                              if (resource.packageId && !resource.membershipId) {
                                                return {
                                                  backgroundColor: '#f8f9fa',
                                                  borderLeft: '3px solid #007bff'
                                                };
                                              }
                                              return {};
                                            };

                                            const getServiceNameStyle = () => {
                                              // Priority 1: Standalone billed service (orange)
                                              if (isBilledService) {
                                                return { color: '#ff9800', fontWeight: '600' };
                                              }
                                              // Priority 2: Package membership (blue)
                                              if (membershipType === 'package') {
                                                return { color: '#0066cc', fontWeight: '600' };
                                              }
                                              // Priority 3: Package series membership (green)
                                              if (membershipType === 'packageSeries') {
                                                return { color: '#2e7d32', fontWeight: '600' };
                                              }
                                              return {};
                                            };

                                            const getIconClass = () => {
                                              if (isBilledService) {
                                                return 'mdi-receipt text-warning';
                                              } else if (membershipType === 'package') {
                                                return 'mdi-spa text-primary';
                                              } else if (membershipType === 'packageSeries') {
                                                return 'mdi-spa text-success';
                                              }
                                              return 'mdi-spa text-primary';
                                            };

                                            return (
                                              <tr
                                                key={resource.id}
                                                style={getRowStyle()}
                                              >
                                                <td>
                                                  <div>
                                                    <i className={`mdi ${getIconClass()} me-2`}></i>
                                                    <strong style={getServiceNameStyle()}>{resource.serviceName}</strong>
                                                    {resource.packageName && (
                                                      <div className="text-muted small mt-1">
                                                        <i className="mdi mdi-package me-1"></i>
                                                        Package: {resource.packageName}
                                                      </div>
                                                    )}
                                                    {resource.membershipId && (
                                                      <div className={`small mt-1 ${membershipType === 'package' ? 'text-primary' : membershipType === 'packageSeries' ? 'text-success' : 'text-info'}`}>
                                                        <i className="mdi mdi-crown me-1"></i>
                                                        {membershipType === 'package' ? 'Package Membership' : membershipType === 'packageSeries' ? 'Package Series Membership' : 'Membership'}
                                                      </div>
                                                    )}
                                                    {isBilledService && (
                                                      <div className="small mt-1 text-warning">
                                                        <i className="mdi mdi-receipt me-1"></i>
                                                        Standalone Billed Service
                                                      </div>
                                                    )}
                                                    {(resource.isCancelled || resource.completionStatus === 'Cancel') && resource.cancellationReason && (
                                                      <div className="mt-2 text-danger small" style={{ fontStyle: 'italic' }}>
                                                        <i className="mdi mdi-information-outline me-1"></i>
                                                        Reason: {resource.cancellationReason}
                                                      </div>
                                                    )}
                                                  </div>
                                                </td>
                                                <td>
                                                  {isViewing ? (
                                                    <span>{(() => {
                                                      // For completed services, show start_Time if available, otherwise fromTime
                                                      const timeToShow = (resource.completionStatus === 'Complete' && resource.start_Time)
                                                        ? new Date(resource.start_Time)
                                                        : resource.fromTime;
                                                      return timeToShow ? timeToShow.toLocaleTimeString('en-US', {
                                                        hour: 'numeric',
                                                        minute: '2-digit',
                                                        hour12: true
                                                      }) : 'N/A';
                                                    })()}</span>
                                                  ) : (
                                                    <DatePicker
                                                      selected={resource.fromTime}
                                                      onChange={(date) => {
                                                        handleFromTimeChange(resource.id, date);
                                                      }}
                                                      showTimeSelect
                                                      showTimeSelectOnly
                                                      timeIntervals={15}
                                                      timeCaption="Time"
                                                      dateFormat="h:mm aa"
                                                      className="form-control"
                                                      style={{ width: '100%' }}
                                                      disabled={isViewing || resource.isCancelled || resource.completionStatus === 'Cancel' || resource.completionStatus === 'Complete'}
                                                    />
                                                  )}
                                                </td>
                                                <td>
                                                  {isViewing ? (
                                                    <span>{(() => {
                                                      // For completed services, show stop_Time if available, otherwise toTime
                                                      const timeToShow = (resource.completionStatus === 'Complete' && resource.stop_Time)
                                                        ? new Date(resource.stop_Time)
                                                        : resource.toTime;
                                                      return timeToShow ? timeToShow.toLocaleTimeString('en-US', {
                                                        hour: 'numeric',
                                                        minute: '2-digit',
                                                        hour12: true
                                                      }) : 'N/A';
                                                    })()}</span>
                                                  ) : (
                                                    <DatePicker
                                                      selected={resource.toTime}
                                                      onChange={(date) => {
                                                        // Update toTime in state
                                                        setServiceResources(prev =>
                                                          prev.map(r =>
                                                            r.id === resource.id
                                                              ? { ...r, toTime: date }
                                                              : r
                                                          )
                                                        );

                                                        // COMMENTED OUT per user request - availability checks not needed
                                                        // Then check availability with the updated times
                                                        // const updatedResource = { ...resource, toTime: date };
                                                        // if (updatedResource.fromTime && updatedResource.toTime) {
                                                        //   setCheckingTimeAvailability(true);
                                                        //   setTimeout(() => {
                                                        //     checkTimeAvailability(resource.id, updatedResource.fromTime, updatedResource.toTime).finally(() => {
                                                        //       setCheckingTimeAvailability(false);
                                                        //       // Also trigger bulk availability check to refresh all dropdowns
                                                        //       checkAvailabilityForSelection();
                                                        //     });
                                                        //   }, 500);
                                                        // }
                                                      }}
                                                      showTimeSelect
                                                      showTimeSelectOnly
                                                      timeIntervals={15}
                                                      timeCaption="Time"
                                                      dateFormat="h:mm aa"
                                                      className="form-control"
                                                      style={{ width: '100%' }}
                                                      disabled={isViewing || resource.isCancelled || resource.completionStatus === 'Cancel' || resource.completionStatus === 'Complete'}
                                                    />
                                                  )}
                                                </td>
                                                <td>
                                                  {isViewing ? (
                                                    <span>{resource.employeeName || (resource.employeeId ? `Employee ${resource.employeeId}` : '-')}</span>
                                                  ) : (
                                                    <Select
                                                      key={`employee-${resource.id}`}
                                                      value={employees.find(emp => emp.value === resource.employeeId)}
                                                      onChange={(option) => {
                                                        // COMMENTED OUT per user request - availability checks not needed
                                                        // const timeAvailability = timeAvailabilityResults[resource.id];
                                                        // 
                                                        // // Check if employee is available before allowing selection
                                                        // if (timeAvailability && option?.value) {
                                                        //   const employee = timeAvailability.availableEmployees?.find(emp => emp.employee_Id === option.value);
                                                        //   
                                                        //   if (employee && !employee.isAvailable) {
                                                        //     Swal.fire("Warning", "This employee is not available for the selected time slot", "warning");
                                                        //     return; // Don't update if not available
                                                        //   }
                                                        // }
                                                        // 
                                                        // // Only proceed if no availability check or employee is available
                                                        // if (!timeAvailability || !option?.value) {
                                                        //   handleResourceChange(resource.id, 'employeeId', option?.value);
                                                        // } else {
                                                        //   const employee = timeAvailability.availableEmployees?.find(emp => emp.employee_Id === option.value);
                                                        //   if (employee && employee.isAvailable) {
                                                        //     handleResourceChange(resource.id, 'employeeId', option?.value);
                                                        //   }
                                                        // }
                                                        handleResourceChange(resource.id, 'employeeId', option?.value);
                                                      }}
                                                      options={formatOptionsWithAvailability(employees, 'Employee', resource.id)}
                                                      placeholder="Select"
                                                      styles={selectStyles}
                                                      isClearable
                                                      isSearchable
                                                      isDisabled={isViewing || resource.isCancelled || resource.completionStatus === 'Cancel' || resource.completionStatus === 'Complete'}
                                                    />
                                                  )}
                                                </td>
                                                <td>
                                                  {isViewing ? (
                                                    <span>{resource.roomName || (resource.roomId ? `Room ${resource.roomId}` : '-')}</span>
                                                  ) : (
                                                    <Select
                                                      key={`room-${resource.id}`}
                                                      value={rooms.find(room => room.value === resource.roomId)}
                                                      onChange={(option) => {
                                                        // COMMENTED OUT per user request - availability checks not needed
                                                        // const timeAvailability = timeAvailabilityResults[resource.id];
                                                        // 
                                                        // // Check if room is available before allowing selection
                                                        // if (timeAvailability && option?.value) {
                                                        //   const room = timeAvailability.availableRooms?.find(room => room.room_Id === option.value);
                                                        //   
                                                        //   if (room && !room.isAvailable) {
                                                        //     Swal.fire("Warning", "This room is not available for the selected time slot", "warning");
                                                        //     return; // Don't update if not available
                                                        //   }
                                                        // }
                                                        // 
                                                        // // Only proceed if no availability check or room is available
                                                        // if (!timeAvailability || !option?.value) {
                                                        //   handleResourceChange(resource.id, 'roomId', option?.value);
                                                        // } else {
                                                        //   const room = timeAvailability.availableRooms?.find(room => room.room_Id === option.value);
                                                        //   if (room && room.isAvailable) {
                                                        //     handleResourceChange(resource.id, 'roomId', option?.value);
                                                        //   }
                                                        // }
                                                        handleResourceChange(resource.id, 'roomId', option?.value);
                                                      }}
                                                      options={formatOptionsWithAvailability(rooms, 'Room', resource.id)}
                                                      placeholder="Select"
                                                      styles={selectStyles}
                                                      isClearable
                                                      isSearchable
                                                      isDisabled={isViewing || resource.isCancelled || resource.completionStatus === 'Cancel' || resource.completionStatus === 'Complete'}
                                                    />
                                                  )}
                                                </td>
                                                <td>
                                                  {isViewing ? (
                                                    <span>
                                                      {resource.equipmentId ?
                                                        (equipments.find(eq => eq.value === resource.equipmentId)?.label || `Equipment ${resource.equipmentId}`)
                                                        : '-'
                                                      }
                                                    </span>
                                                  ) : (
                                                    <Select
                                                      key={`equipment-${resource.id}-${formKey}`}
                                                      value={equipments.find(eq => eq.value === resource.equipmentId)}
                                                      onChange={(option) => handleResourceChange(resource.id, 'equipmentId', option?.value)}
                                                      options={equipments}
                                                      placeholder="Select"
                                                      styles={selectStyles}
                                                      isClearable
                                                      isSearchable
                                                      isDisabled={isViewing || resource.isCancelled || resource.completionStatus === 'Cancel' || resource.completionStatus === 'Complete'}
                                                    />
                                                  )}
                                                </td>
                                                {!isEditing && !isViewing && (
                                                  <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                    <Button
                                                      color="danger"
                                                      size="sm"
                                                      onClick={() => handleRemoveService(resource.id)}
                                                      style={{ fontSize: '11px', padding: '4px 8px' }}
                                                      title="Remove Service"
                                                    >
                                                      <X size={14} />
                                                    </Button>
                                                  </td>
                                                )}
                                                {(isEditing || isViewing) && (
                                                  <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                    {(() => {
                                                      const isCancelled = resource.isCancelled || resource.completionStatus === 'Cancel';
                                                      const isDone = resource.completionStatus === 'Complete';

                                                      return (
                                                        <div className="d-flex flex-column align-items-center gap-1">
                                                          {/* Status badges */}
                                                          {isCancelled && <span className="badge bg-danger">Cancelled</span>}
                                                          {isDone && <span className="badge bg-success">Done</span>}

                                                          {/* Edit-mode actions */}
                                                          {isEditing && !isViewing && !isCancelled && !isDone && (
                                                            <Button
                                                              color="soft-warning"
                                                              size="sm"
                                                              onClick={() => handleCancelServiceResource(resource.id)}
                                                              style={{ fontSize: '11px', padding: '4px 8px' }}
                                                              title="Cancel Service"
                                                            >
                                                              Cancel
                                                            </Button>
                                                          )}
                                                        </div>
                                                      );
                                                    })()}
                                                  </td>
                                                )}
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </Table>
                                    </div>
                                  </FormGroup>
                                </Col>
                              </Row>
                            )}

                            <Row>
                              <Col md="6">
                                <FormGroup className="mb-3">
                                  <Label for="source" className="mb-2">Source *</Label>
                                  <Select
                                    key={`source-${formKey}`}
                                    value={sourceOptions.find(s => s.value === form.source_Id)}
                                    onChange={(option) => handleFormChange('source_Id', option?.value)}
                                    options={sourceOptions}
                                    placeholder={sourcesLoading ? "Loading sources..." : "Select Source"}
                                    styles={selectStyles}
                                    isClearable
                                    isSearchable
                                    isDisabled={isViewing || sourcesLoading}
                                    isLoading={sourcesLoading}
                                    noOptionsMessage={() => sourcesLoading ? "Loading..." : "No sources found"}
                                  />
                                </FormGroup>
                              </Col>
                            </Row>

                            <Row>
                              <Col md="12">
                                <FormGroup check className="mb-3 ms-1">
                                  <Label check style={{ cursor: 'pointer', fontWeight: '500' }}>
                                    <Input
                                      type="checkbox"
                                      checked={form.sendNotification}
                                      onChange={(e) => handleFormChange('sendNotification', e.target.checked)}
                                      className="me-2"
                                      disabled={isViewing}
                                    />
                                    Send Notification
                                  </Label>
                                </FormGroup>
                              </Col>
                            </Row>

                            <Row>
                              <Col md="12">
                                <FormGroup className="mb-4">
                                  <Label for="notes" className="mb-2">Notes</Label>
                                  <Input
                                    type="textarea"
                                    id="notes"
                                    value={form.notes}
                                    onChange={(e) => handleFormChange('notes', e.target.value)}
                                    rows="3"
                                    placeholder="Enter any additional notes..."
                                    disabled={isViewing}
                                  />
                                </FormGroup>
                              </Col>
                            </Row>

                            <Row>
                              <Col md="12">
                                <div className="d-flex gap-2">
                                  {!isViewing && (
                                    <Button
                                      type="submit"
                                      color="primary"
                                      disabled={loading}
                                    >
                                      {loading ? (
                                        <ClipLoader size={16} color="#fff" />
                                      ) : (
                                        "Save Booking"
                                      )}
                                    </Button>
                                  )}
                                  {!isViewing && (
                                    <Button
                                      type="button"
                                      color="secondary"
                                      onClick={resetForm}
                                    >
                                      Clear
                                    </Button>
                                  )}
                                  {(isEditing || isViewing) && (
                                    <Button
                                      type="button"
                                      color="secondary"
                                      onClick={() => {
                                        setShowForm(false);
                                        resetForm();
                                        restoreListScroll();
                                      }}
                                    >
                                      {isViewing ? "Close View" : "Back"}
                                    </Button>
                                  )}
                                </div>
                              </Col>
                            </Row>
                          </Form>
                        </CardBody>
                      </Card>
                    </>
                  )}

                  {/* Booking List - Always visible */}
                  <Row className="mb-1" style={{ marginTop: '0' }}>
                    <Col md="12" style={{ paddingTop: '0' }}>
                      <Card style={{ marginTop: '0', marginBottom: '0' }}>
                        {/* Show info message for users without edit permission */}
                        {!hasEditOrHigher && (
                          <CardBody>
                            <div className="alert alert-info mb-0" role="alert">
                              <i className="fas fa-info-circle me-2"></i>
                              You can only view your own bookings. Contact your administrator for access to all bookings.
                            </div>
                          </CardBody>
                        )}
                        {showFilters && hasEditOrHigher && (
                          <CardBody style={{ paddingTop: '12px', paddingBottom: '12px' }}>
                            <Row>
                              <Col md="3">
                                <FormGroup>
                                  <Label className="mb-2">Customer</Label>
                                  <Select
                                    key={`customer-filter-${filterKey}`}
                                    value={selectedCustomerFilterOption}
                                    onChange={(option) => {
                                      setSelectedCustomerFilterOption(option);
                                      handleFilterChange('customer_Id', option?.value);
                                    }}
                                    options={customerSearchOptions}
                                    placeholder="Type at least 3 characters to search customers..."
                                    styles={selectStyles}
                                    isClearable
                                    isSearchable
                                    isLoading={customerSearchLoading}
                                    noOptionsMessage={() => "Type at least 3 characters to search"}
                                    loadingMessage={() => "Searching customers..."}
                                    filterOption={() => true}
                                    onInputChange={(inputValue) => {
                                      console.log('🔤 Filter Input changed:', inputValue);

                                      // Clear previous timeout
                                      if (searchTimeout) {
                                        clearTimeout(searchTimeout);
                                      }

                                      // Set new timeout
                                      const timeout = setTimeout(() => {
                                        searchCustomersFilterManually(inputValue);
                                      }, 300);
                                      setSearchTimeout(timeout);

                                      return inputValue;
                                    }}
                                    onMenuOpen={() => console.log('📂 Filter Menu opened')}
                                    onMenuClose={() => console.log('📁 Filter Menu closed')}
                                  />
                                </FormGroup>
                              </Col>
                              <Col md="3">
                                <FormGroup>
                                  <Label className="mb-2">Employee</Label>
                                  <Select
                                    key={`employee-filter-${filterKey}`}
                                    value={employees.find(e => e.value === filter.employee_Id)}
                                    onChange={(option) => handleFilterChange('employee_Id', option?.value)}
                                    options={employees}
                                    placeholder="All Employees"
                                    styles={selectStyles}
                                    isClearable
                                    isSearchable
                                  />
                                </FormGroup>
                              </Col>
                              <Col md="3">
                                <FormGroup>
                                  <Label className="mb-2">Room</Label>
                                  <Select
                                    key={`room-filter-${filterKey}`}
                                    value={rooms.find(r => r.value === filter.room_Id)}
                                    onChange={(option) => handleFilterChange('room_Id', option?.value)}
                                    options={rooms}
                                    placeholder="All Rooms"
                                    styles={selectStyles}
                                    isClearable
                                    isSearchable
                                  />
                                </FormGroup>
                              </Col>
                              <Col md="3">
                                <FormGroup>
                                  <Label className="mb-2">Company</Label>
                                  <Select
                                    key={`business-filter-${filterKey}`}
                                    value={selectedBusinessFilterOption}
                                    onChange={(option) => {
                                      setSelectedBusinessFilterOption(option);
                                      handleFilterChange('business_Id', option?.value);
                                    }}
                                    options={filterBusinessOptions}
                                    placeholder="All Companies"
                                    styles={selectStyles}
                                    isClearable={!session.companyId}
                                    isSearchable
                                    isDisabled={!!session.companyId}
                                  />
                                </FormGroup>
                              </Col>
                            </Row>
                            <Row>
                              <Col md="3">
                                <FormGroup>
                                  <Label className="mb-2">Branch</Label>
                                  <Select
                                    key={`branch-filter-${filterKey}`}
                                    value={selectedBranchFilterOption}
                                    onChange={(option) => {
                                      setSelectedBranchFilterOption(option);
                                      handleFilterChange('branch_Id', option?.value);
                                    }}
                                    options={filterBranchOptions}
                                    placeholder={!filter.business_Id ? "Select Company first" : "All Branches"}
                                    styles={selectStyles}
                                    isClearable={!session.branchId}
                                    isSearchable
                                    isDisabled={!!session.branchId || !filter.business_Id}
                                  />
                                </FormGroup>
                              </Col>
                              <Col md="3">
                                <FormGroup>
                                  <Label className="mb-2">From Date</Label>
                                  <DatePicker
                                    selected={filter.from_Date}
                                    onChange={(date) => handleFilterChange('from_Date', date)}
                                    className="form-control"
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="Select Date"
                                    isClearable
                                  />
                                </FormGroup>
                              </Col>
                              <Col md="3">
                                <FormGroup>
                                  <Label className="mb-2">To Date</Label>
                                  <DatePicker
                                    selected={filter.to_Date}
                                    onChange={(date) => handleFilterChange('to_Date', date)}
                                    className="form-control"
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="Select Date"
                                    isClearable
                                  />
                                </FormGroup>
                              </Col>
                              <Col md="9" className="d-flex align-items-end gap-2">
                                <Button
                                  color="primary"
                                  onClick={applyFilter}
                                  disabled={loading}
                                >
                                  {loading ? (
                                    <ClipLoader size={16} color="#fff" />
                                  ) : (
                                    "Apply Filter"
                                  )}
                                </Button>
                                <Button
                                  color="secondary"
                                  onClick={clearFilter}
                                  disabled={loading}
                                >
                                  Clear Filter
                                </Button>
                              </Col>
                            </Row>
                          </CardBody>
                        )}
                      </Card>
                    </Col>
                  </Row>

                  {/* Booking List Sub-tabs with Show Filters and Export Excel buttons */}
                  <Row style={{ marginTop: '0', marginBottom: '12px' }}>
                    <Col md="12" style={{ paddingTop: '0' }}>
                      <div className="d-flex justify-content-between align-items-center" style={{ borderBottom: '2px solid #dee2e6', paddingBottom: '8px' }}>
                        <Nav tabs className="nav-tabs-custom" style={{ borderBottom: 'none', marginBottom: 0 }}>
                          <NavItem>
                            <NavLink
                              style={{
                                cursor: "pointer",
                                fontWeight: bookingListSubTab === "current-future" ? "bold" : "normal",
                                color: bookingListSubTab === "current-future" ? "#1976D2" : "#74788d",
                                borderBottom: bookingListSubTab === "current-future" ? "2px solid #1976D2" : "none",
                                padding: "10px 20px",
                                fontSize: "14px"
                              }}
                              className={bookingListSubTab === "current-future" ? "active" : ""}
                              onClick={() => setBookingListSubTab("current-future")}
                            >
                              Current & Future
                            </NavLink>
                          </NavItem>
                          <NavItem>
                            <NavLink
                              style={{
                                cursor: "pointer",
                                fontWeight: bookingListSubTab === "past" ? "bold" : "normal",
                                color: bookingListSubTab === "past" ? "#1976D2" : "#74788d",
                                borderBottom: bookingListSubTab === "past" ? "2px solid #1976D2" : "none",
                                padding: "10px 20px",
                                fontSize: "14px"
                              }}
                              className={bookingListSubTab === "past" ? "active" : ""}
                              onClick={() => setBookingListSubTab("past")}
                            >
                              Past Bookings
                            </NavLink>
                          </NavItem>
                        </Nav>
                        <div className="d-flex gap-2 align-items-center">
                          {/* Only show filter button if user has edit permission */}
                          {hasEditOrHigher && (
                            <Button
                              color="primary"
                              onClick={() => setShowFilters(!showFilters)}
                            >
                              {showFilters ? "Hide Filters" : "Show Filters"}
                            </Button>
                          )}
                          <Button
                            color="success"
                            onClick={handleExportExcel}
                            disabled={loading || totalCount === 0}
                            className="waves-effect waves-light"
                            style={{ marginBottom: '0' }}
                          >
                            <Download className="me-1" size={16} />
                            Export Excel
                          </Button>
                        </div>
                      </div>
                    </Col>
                  </Row>

                  <div className="table-responsive">
                    <Table className="table-nowrap mb-0" style={{ width: '100%' }}>
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '3%', fontSize: '13px', fontWeight: '700' }}>#</th>
                          <th style={{ width: '8%', fontSize: '13px', fontWeight: '700' }}>Actions</th>
                          <th style={{ width: '16%', fontSize: '13px', fontWeight: '700' }}>Customer</th>
                          {/* Removed Employee, Room, From Time, To Time columns as they vary per resource */}
                          <th style={{ width: '10%', fontSize: '13px', fontWeight: '700' }}>Booking Date</th>
                          <th style={{ width: '10%', fontSize: '13px', fontWeight: '700' }}>Entry Date</th>
                          <th style={{ width: '18%', fontSize: '13px', fontWeight: '700' }}>Entity</th>
                          <th style={{ width: '10%', fontSize: '13px', fontWeight: '700' }}>Balance</th>
                          <th style={{ width: '8%', fontSize: '13px', fontWeight: '700' }}>Status</th>
                          <th style={{ width: '17%', fontSize: '13px', fontWeight: '700' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody style={{ position: 'relative' }}>
                        {loading || loadingCustomers ? (
                          <tr>
                            <td colSpan="7" className="text-center" style={{ position: 'relative', minHeight: '200px' }}>
                              <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '40px 0',
                                gap: '10px'
                              }}>
                                <ClipLoader size={30} color="#556ee6" />
                                <span style={{ color: '#6c757d', fontSize: '14px' }}>
                                  {loading ? 'Loading bookings...' : 'Loading customer data...'}
                                </span>
                              </div>
                            </td>
                          </tr>
                        ) : bookings.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="text-center">
                              No bookings found
                            </td>
                          </tr>
                        ) : (
                          (() => {
                            // Filter bookings based on selected sub-tab
                            const filteredBookings = bookings.filter(booking => {
                              const isPast = isBookingPast(booking.booking_Date);
                              if (bookingListSubTab === "past") {
                                return isPast;
                              } else {
                                return !isPast; // current-future
                              }
                            });

                            if (filteredBookings.length === 0) {
                              return (
                                <tr>
                                  <td colSpan="7" className="text-center">
                                    No {bookingListSubTab === "past" ? "past" : "current or future"} bookings found
                                  </td>
                                </tr>
                              );
                            }

                            return filteredBookings.map((booking, index) => {
                              const isPast = isBookingPast(booking.booking_Date);
                              return (
                                <tr key={booking.booking_Id}>
                                  <td style={{ fontSize: '13px', verticalAlign: 'middle' }}>{(page - 1) * pageSize + index + 1}</td>
                                  <td>
                                    <div className="d-flex gap-1 justify-content-start">
                                      <Button
                                        color="info"
                                        size="sm"
                                        onClick={() => handleView(booking)}
                                        className="waves-effect waves-light"
                                        title="View Booking"
                                        style={{ fontSize: '11px', padding: '3px 7px', minWidth: 'auto', height: '26px' }}
                                      >
                                        <Eye size={13} strokeWidth={2} />
                                      </Button>
                                      {hasEditOrHigher && (
                                        <Button
                                          color="warning"
                                          size="sm"
                                          onClick={() => handleEdit(booking)}
                                          disabled={booking.is_Cancelled}
                                          className="waves-effect waves-light"
                                          title="Edit Booking"
                                          style={{ fontSize: '11px', padding: '3px 7px', minWidth: 'auto', height: '26px', color: '#fff' }}
                                        >
                                          <SquarePen size={13} strokeWidth={2} />
                                        </Button>
                                      )}
                                    </div>
                                  </td>
                                  <td style={{ fontSize: '13px', verticalAlign: 'middle', maxWidth: '180px', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.2' }}>
                                    <span 
                                      onClick={() => {
                                        setTableRowCustomerId(booking.customer_Id);
                                        setShowCustomerProfile(true);
                                      }}
                                      style={{ color: '#556ee6', cursor: 'pointer', fontWeight: '500' }}
                                      title="View Customer Profile"
                                    >
                                      {getCustomerName(booking.customer_Id)}
                                    </span>
                                  </td>
                                  {/* Removed Employee, Room, From Time, To Time cells */}
                                  <td style={{ fontSize: '13px', verticalAlign: 'middle' }}>{formatDate(booking.booking_Date)}</td>
                                  <td style={{ fontSize: '13px', verticalAlign: 'middle' }}>{formatDate(booking.entry_Date)}</td>
                                  <td style={{ fontSize: '13px', verticalAlign: 'middle', fontWeight: '500', maxWidth: '200px', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.2' }}>{booking.entityName || booking.EntityName || "N/A"}</td>
                                  <td style={{ fontSize: '13px', verticalAlign: 'middle', fontWeight: '600', color: (booking.balanceAmount || booking.BalanceAmount) > 0 ? '#f46a6a' : '#34c38f' }}>
                                    {currencyCode} {(booking.balanceAmount || booking.BalanceAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                  <td style={{ fontSize: '13px', verticalAlign: 'middle' }}>{getStatusBadge(booking)}</td>
                                  <td>
                                    <div className="d-flex gap-1 justify-content-start flex-wrap">
                                      <Button
                                        color="success"
                                        size="sm"
                                        onClick={() => handleExportDetails(booking.booking_Id)}
                                        disabled={loading}
                                        title="Export Details"
                                        style={{ fontSize: '12px', padding: '3px 7px', minWidth: 'auto', height: '26px' }}
                                      >
                                        <Download size={13} strokeWidth={2} />
                                      </Button>
                                      {/* For past bookings, only show Cancel, Edit, View, Export, and Consent (if signed) */}
                                      {/* 
                                        TEMP (testing phase): past bookings were previously restricted to a limited action set.
                                        Per testing requirement, we are temporarily making past-booking actions match current/future bookings.
                                        Keep the old restriction commented so it can be restored later.
                                      */}
                                      {/* {!isPast && ( */}
                                      <>
                                        {hasEditOrHigher && (booking.completionStatus === 'Complete' || booking.CompletionStatus === 'Complete') && (
                                          (() => {
                                            // Use Booking.isBilled as the primary indicator
                                            // isBilled = 0/false means there are unbilled complete services (show Bill button)
                                            // isBilled = 1/true means all complete services are billed (hide Bill button)
                                            // Handle different field name variations (camelCase, PascalCase, snake_case)
                                            // Also handle both boolean (true/false) and numeric (1/0) values from API
                                            const isBilled =
                                              booking.isBilled === 1 || booking.isBilled === true || booking.isBilled === '1' ||
                                              booking.IsBilled === 1 || booking.IsBilled === true || booking.IsBilled === '1' ||
                                              booking.is_Billed === 1 || booking.is_Billed === true || booking.is_Billed === '1';
                                            
                                            const hasBalance = (booking.balanceAmount || booking.BalanceAmount || 0) > 0;

                                            if (!isBilled || hasBalance) {
                                              // Show "Bill" button - there are unbilled complete services or unpaid installments
                                              return (
                                                <Button
                                                  color={hasBalance && isBilled ? "warning" : "success"}
                                                  size="sm"
                                                  onClick={() => handlePayBill(booking)}
                                                  disabled={booking.is_Cancelled}
                                                  style={{ fontSize: '11px', padding: '3px 7px', minWidth: 'auto', height: '26px', lineHeight: '1.2' }}
                                                  title={hasBalance && isBilled ? "Pay Remaining Balance" : "Generate Bill"}
                                                >
                                                  <Receipt size={13} strokeWidth={2} />
                                                </Button>
                                              );
                                            } else {
                                              // All complete services are billed - hide Bill button
                                              // TODO: View Bill button logic will be implemented later
                                              return null;

                                              /* COMMENTED OUT - View Bill button logic (to be implemented later)
                                              // All complete services are billed - show "View Bill" if invoice exists
                                              const hasRegularInvoice = booking.invoice_Id;
                                              const packageInvoiceId = bookingPackageInvoiceMap[booking.booking_Id];
                                              const hasInvoice = hasRegularInvoice || packageInvoiceId;
                                              
                                              if (hasInvoice) {
                                                return (
                                                  <Button
                                                    color="info"
                                                    size="sm"
                                                    onClick={() => handleViewBill(booking, packageInvoiceId || booking.invoice_Id)}
                                                    style={{ fontSize: '11px', padding: '3px 7px', minWidth: 'auto', height: '26px', lineHeight: '1.2' }}
                                                  >
                                                    <FileText size={13} strokeWidth={2} />
                                                  </Button>
                                                );
                                              } else {
                                                // All billed but no invoice (edge case - shouldn't happen normally)
                                                return null;
                                              }
                                              */
                                            }
                                          })()
                                        )}
                                        {/* Check In Button - Show when any service is Pending */}
                                        {hasEditOrHigher && !booking.is_Cancelled && booking.details && booking.details.length > 0 && checkIfAnyServicePending(booking) && (
                                          <Button
                                            color="primary"
                                            size="sm"
                                            onClick={() => handleBulkCheckIn(booking)}
                                            disabled={checkInLoading}
                                            title="Check in all services"
                                            style={{
                                              fontSize: '11px',
                                              padding: '3px 7px',
                                              minWidth: 'auto',
                                              height: '26px',
                                              lineHeight: '1.2'
                                            }}
                                          >
                                            {checkInLoading ? (
                                              <ClipLoader size={12} color="#fff" />
                                            ) : (
                                              <CheckCircle size={13} strokeWidth={2} />
                                            )}
                                          </Button>
                                        )}

                                        {/* Done Button - Show when at least one service is eligible (Check In/Start/Stop + employee assigned) */}
                                        {hasEditOrHigher && !booking.is_Cancelled && booking.details && booking.details.length > 0 && hasAnyDoneEligibleService(booking) && hasServicesNotComplete(booking) && (
                                          <Button
                                            color="success"
                                            size="sm"
                                            onClick={() => handleBulkCompletionStatusUpdate(booking)}
                                            disabled={allDoneLoading}
                                            title={"Mark eligible services as done"}
                                            style={{
                                              fontSize: '11px',
                                              padding: '3px 7px',
                                              minWidth: 'auto',
                                              height: '26px',
                                              lineHeight: '1.2',
                                              opacity: 1,
                                              cursor: allDoneLoading ? 'not-allowed' : 'pointer',
                                              display: 'flex',
                                              alignItems: 'center'
                                            }}
                                          >
                                            {allDoneLoading ? (
                                              <ClipLoader size={12} color="#fff" />
                                            ) : (
                                              <CheckCircle size={13} strokeWidth={2} />
                                            )}
                                          </Button>
                                        )}

                                        {/* Mark Not Done Button - Show when all services are Complete */}
                                        {/* {hasEditOrHigher && !booking.is_Cancelled && booking.details && booking.details.length > 0 && areAllServicesComplete(booking) && (
                                            <Button
                                              color="warning"
                                              size="sm"
                                              onClick={() => handleBulkCompletionStatusUpdate(booking)}
                                              disabled={!checkIfAllServicesHaveEmployees(booking)}
                                              title={
                                                !checkIfAllServicesHaveEmployees(booking) 
                                                  ? "Select employee for all services to mark as pending" 
                                                  : "Mark all services as pending"
                                              }
                                              style={{ 
                                                fontSize: '11px',
                                                padding: '3px 8px', 
                                                minWidth: 'auto',
                                                height: '28px',
                                                lineHeight: '1.2',
                                                opacity: !checkIfAllServicesHaveEmployees(booking) ? 0.5 : 1,
                                                cursor: !checkIfAllServicesHaveEmployees(booking) ? 'not-allowed' : 'pointer'
                                              }}
                                            >
                                              Mark Not Done
                                            </Button>
                                          )} */}

                                        {/* Consent Button - Show Sign button for current/future bookings */}
                                        {hasEditOrHigher && (
                                          <Button
                                            color={booking.isConsentSigned ? "info" : "warning"}
                                            size="sm"
                                            onClick={() => {
                                              // Open consent form in new tab
                                              const consentUrl = `${window.location.origin}/GmsConsentForm?bookingId=${booking.booking_Id}`;
                                              window.open(consentUrl, '_blank');
                                            }}
                                            style={{ fontSize: '11px', padding: '3px 7px', minWidth: 'auto', height: '26px', lineHeight: '1.2' }}
                                            title={booking.isConsentSigned ? "View Signed Consent" : "Sign Consent Form"}
                                          >
                                            <FileSignature size={12} strokeWidth={2} />
                                          </Button>
                                        )}
                                      </>
                                      {/* )} */}

                                      {/* Cancel Button - Show for both past and current/future bookings with existing logic */}
                                      {hasDeletePermission && !booking.is_Cancelled && !areAllServicesComplete(booking) && (
                                        <Button
                                          color="danger"
                                          size="sm"
                                          onClick={() => handleCancel(booking.booking_Id)}
                                          style={{ fontSize: '11px', padding: '3px 7px', minWidth: 'auto', height: '26px', lineHeight: '1.2' }}
                                          title="Cancel Booking"
                                        >
                                          <XCircle size={12} strokeWidth={2} />
                                        </Button>
                                      )}

                                      {/* View Bill Button - Show for past bookings when booking is done and has invoice */}
                                      {/*
                                        TEMP (testing phase): past bookings now render the same Bill/View Bill button as current/future above.
                                        Keep this past-only block commented to avoid duplicate buttons; restore when re-enabling past restrictions.
                                      */}
                                      {/* {isPast && hasEditOrHigher && (booking.completionStatus === 'Complete' || booking.CompletionStatus === 'Complete' || areAllServicesComplete(booking)) && booking.invoice_Id && (
                                        <Button
                                          color="info"
                                          size="sm"
                                          onClick={() => handleViewBill(booking)}
                                          style={{ fontSize: '11px', padding: '3px 8px', minWidth: 'auto', height: '28px', lineHeight: '1.2' }}
                                          title="View Bill"
                                        >
                                          View Bill
                                        </Button>
                                      )} */}

                                      {/* Consent View Button - Show only if signed for past bookings */}
                                      {/*
                                        TEMP (testing phase): past bookings now use the same Sign/Signed consent button as current/future above.
                                        Keep this past-only block commented to avoid duplicate buttons; restore when re-enabling past restrictions.
                                      */}
                                      {/* {isPast && hasEditOrHigher && booking.isConsentSigned && (
                                        <Button
                                          color="info"
                                          size="sm"
                                          onClick={() => {
                                            // Open consent form in new tab
                                            const consentUrl = `${window.location.origin}/GmsConsentForm?bookingId=${booking.booking_Id}`;
                                            window.open(consentUrl, '_blank');
                                          }}
                                          style={{ fontSize: '11px', padding: '3px 8px', minWidth: 'auto', height: '28px', lineHeight: '1.2' }}
                                          title="View Consent"
                                        >
                                          Signed
                                        </Button>
                                      )} */}
                                    </div>
                                  </td>
                                </tr>
                              );
                            });
                          })()
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {totalCount > 0 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div>
                        <p className="text-muted mb-0">
                          Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} entries
                        </p>
                      </div>
                      <div className="pagination-wrapper">
                        <Pagination
                          className="ant-pagination"
                          current={page}
                          total={totalCount}
                          pageSize={pageSize}
                          onChange={(pageNum) => setPage(pageNum)}
                          showTitle={false}
                        />
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Cancellation Modal */}
      <Modal isOpen={showCancelModal} toggle={() => setShowCancelModal(false)}>
        <ModalHeader toggle={() => setShowCancelModal(false)}>
          Cancel Booking
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="cancelReason" className="mb-2">Cancellation Reason *</Label>
            <Input
              type="textarea"
              id="cancelReason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows="3"
              placeholder="Please provide a reason for cancellation..."
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowCancelModal(false)}>
            Cancel
          </Button>
          <Button color="danger" onClick={confirmCancel} disabled={loading}>
            {loading ? <ClipLoader size={16} color="#fff" /> : "Confirm Cancellation"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Entity Cancellation Modal */}
      <Modal isOpen={showEntityCancelModal} toggle={() => setShowEntityCancelModal(false)}>
        <ModalHeader toggle={() => setShowEntityCancelModal(false)}>
          Cancel {cancellingEntity.name}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="entityCancelReason" className="mb-2">Cancellation Reason *</Label>
            <Input
              type="textarea"
              id="entityCancelReason"
              value={entityCancelReason}
              onChange={(e) => setEntityCancelReason(e.target.value)}
              rows="3"
              placeholder="Please provide a reason for cancelling this item..."
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowEntityCancelModal(false)}>
            Cancel
          </Button>
          <Button color="danger" onClick={confirmEntityCancel}>
            Confirm Cancellation
          </Button>
        </ModalFooter>
      </Modal>

      {/* Reschedule Modal */}
      <Modal isOpen={showRescheduleModal} toggle={() => setShowRescheduleModal(false)} size="lg">
        <ModalHeader toggle={() => setShowRescheduleModal(false)}>
          Reschedule Booking
        </ModalHeader>
        <ModalBody>
          <FormGroup className="mb-3">
            <Label for="rescheduleDate" className="mb-2">New Booking Date *</Label>
            <DatePicker
              selected={rescheduleDate}
              onChange={(date) => {
                setRescheduleDate(date);
                // Check if date is in the past and show warning
                if (date) {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const selectedDate = new Date(date);
                  selectedDate.setHours(0, 0, 0, 0);
                  if (selectedDate < today) {
                    // Warning will be shown in the UI below
                  }
                }
              }}
              className="form-control"
              dateFormat="dd/MM/yyyy"
              placeholderText="Select New Date"
            />
            {rescheduleDate && form.booking_Date && (() => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const selectedDate = new Date(rescheduleDate);
              selectedDate.setHours(0, 0, 0, 0);
              const currentBookingDate = new Date(form.booking_Date);
              currentBookingDate.setHours(0, 0, 0, 0);

              // Only show warning if:
              // 1. Selected date is different from current booking date
              // 2. Selected date is in the past
              if (selectedDate.getTime() !== currentBookingDate.getTime() && selectedDate < today) {
                return (
                  <div className="alert alert-warning mt-2 mb-0" style={{ fontSize: '13px' }}>
                    <i className="mdi mdi-alert me-1"></i>
                    <strong>Warning:</strong> You are rescheduling to a past date. This may affect reports and historical data.
                  </div>
                );
              }
              return null;
            })()}
          </FormGroup>
          <FormGroup className="mb-3">
            <Label for="rescheduleComment" className="mb-2">Comment / Notes *</Label>
            <Input
              type="textarea"
              id="rescheduleComment"
              value={rescheduleComment}
              onChange={(e) => setRescheduleComment(e.target.value)}
              rows="3"
              placeholder="Enter notes or comments about this reschedule (required)..."
              required
            />
          </FormGroup>
          <div className="alert alert-info mb-0" style={{ fontSize: '13px' }}>
            <i className="mdi mdi-information-outline me-1"></i>
            <strong>Note:</strong> After rescheduling, all staff, room, and equipment selections will be cleared. You will need to reassign them.
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => {
            setShowRescheduleModal(false);
            setRescheduleDate(null);
            setRescheduleComment("");
          }}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleRescheduleSave}
            disabled={rescheduleLoading || !rescheduleDate || !rescheduleComment || rescheduleComment.trim() === ""}
          >
            {rescheduleLoading ? (
              <>
                <ClipLoader size={16} color="#fff" className="me-2" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Service Resource Cancellation Modal */}
      <Modal isOpen={showServiceCancelModal} toggle={() => {
        setShowServiceCancelModal(false);
        setCancellingResourceId(null);
        setServiceCancelReason("");
      }}>
        <ModalHeader toggle={() => {
          setShowServiceCancelModal(false);
          setCancellingResourceId(null);
          setServiceCancelReason("");
        }}>
          Cancel Service
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="serviceCancelReason" className="mb-2">Cancellation Reason *</Label>
            <Input
              type="textarea"
              id="serviceCancelReason"
              value={serviceCancelReason}
              onChange={(e) => setServiceCancelReason(e.target.value)}
              rows="3"
              placeholder="Please provide a reason for cancelling this service..."
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => {
            setShowServiceCancelModal(false);
            setCancellingResourceId(null);
            setServiceCancelReason("");
          }}>
            Close
          </Button>
          <Button color="warning" onClick={confirmCancelServiceResource}>
            Confirm Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Membership Selection Modal - Now includes billed services */}
      <Modal isOpen={showMembershipModal} toggle={closeMembershipModal} size="xl">
        <ModalHeader toggle={closeMembershipModal}>
          <i className="mdi mdi-crown me-2"></i>
          Select Billed Services
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <p className="text-muted">
              Select services from your membership or billed standalone services. Only services with pending completion status (for membership) or available quantity (for billed services) are shown.
              Products are not displayed in this selection.
            </p>
          </div>

          {(() => {
            const tableData = buildMembershipTableData();
            return tableData.length > 0 ? (
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                <Table responsive striped hover className="mb-0">
                  <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <tr>
                      <th width="5%" style={{ textAlign: 'center' }}>Select</th>
                      <th width="30%">Service Name</th>
                      <th width="25%">Package Name</th>
                      <th width="15%" style={{ textAlign: 'center' }}>Available Qty</th>
                      <th width="20%" style={{ textAlign: 'center' }}>Required Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.map((service) => {
                      // Determine row color based on membership type or billed service
                      const getRowStyle = () => {
                        if (service.isBilledService) {
                          return {
                            backgroundColor: '#fff3e0',
                            borderLeft: '4px solid #ff9800'
                          };
                        } else if (service.membershipType === 'package') {
                          return {
                            backgroundColor: '#e7f3ff',
                            borderLeft: '4px solid #0066cc'
                          };
                        } else if (service.membershipType === 'packageSeries') {
                          return {
                            backgroundColor: '#e8f5e9',
                            borderLeft: '4px solid #2e7d32'
                          };
                        }
                        return {};
                      };

                      const getServiceNameStyle = () => {
                        if (service.isBilledService) {
                          return { color: '#ff9800', fontWeight: '600' };
                        } else if (service.membershipType === 'package') {
                          return { color: '#0066cc', fontWeight: '600' };
                        } else if (service.membershipType === 'packageSeries') {
                          return { color: '#2e7d32', fontWeight: '600' };
                        }
                        return {};
                      };

                      const getIconClass = () => {
                        if (service.isBilledService) {
                          return 'mdi-receipt text-warning';
                        } else if (service.membershipType === 'package') {
                          return 'mdi-spa text-primary';
                        } else if (service.membershipType === 'packageSeries') {
                          return 'mdi-spa text-success';
                        }
                        return 'mdi-spa text-primary';
                      };

                      return (
                        <tr key={service.selectionKey} style={getRowStyle()}>
                          <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`service-table-${service.selectionKey}`}
                                checked={tableSelection[service.selectionKey]?.selected || false}
                                onChange={(e) => handleTableServiceSelection(service.selectionKey, e.target.checked)}
                              />
                            </div>
                          </td>
                          <td style={{ verticalAlign: 'middle' }}>
                            <i className={`mdi ${getIconClass()} me-2`}></i>
                            <strong style={getServiceNameStyle()}>{service.serviceName}</strong>
                          </td>
                          <td style={{ verticalAlign: 'middle' }}>
                            <span className="text-muted">{service.packageNames}</span>
                          </td>
                          <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                            <span className="badge bg-info" style={{ fontSize: '13px', padding: '6px 12px' }}>
                              {service.availableQty}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                            {tableSelection[service.selectionKey]?.selected ? (
                              <Input
                                type="number"
                                min="1"
                                max={service.availableQty}
                                value={tableSelection[service.selectionKey]?.requiredQty !== undefined && tableSelection[service.selectionKey]?.requiredQty !== ''
                                  ? tableSelection[service.selectionKey].requiredQty
                                  : ''}
                                onChange={(e) => handleRequiredQtyChange(service.selectionKey, e.target.value)}
                                onBlur={(e) => {
                                  // If empty on blur, set to 1
                                  if (!e.target.value || e.target.value === '') {
                                    handleRequiredQtyChange(service.selectionKey, '1');
                                  }
                                }}
                                style={{ width: '80px', textAlign: 'center' }}
                                className="d-inline-block"
                                placeholder="1"
                              />
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="text-center text-muted py-4">
                <i className="mdi mdi-information-outline me-2"></i>
                No pending membership services available for selection.
              </div>
            );
          })()}
        </ModalBody>
        <ModalFooter>
          <div className="d-flex align-items-center gap-3 me-auto" style={{ fontSize: '13px' }}>
            <div className="d-flex align-items-center gap-2">
              <div style={{ width: '16px', height: '16px', backgroundColor: '#e7f3ff', borderLeft: '3px solid #0066cc', borderRadius: '2px' }}></div>
              <span style={{ color: '#0066cc', fontWeight: '500' }}>Package Membership</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div style={{ width: '16px', height: '16px', backgroundColor: '#e8f5e9', borderLeft: '3px solid #2e7d32', borderRadius: '2px' }}></div>
              <span style={{ color: '#2e7d32', fontWeight: '500' }}>Package Series Membership</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div style={{ width: '16px', height: '16px', backgroundColor: '#fff3e0', borderLeft: '3px solid #ff9800', borderRadius: '2px' }}></div>
              <span style={{ color: '#ff9800', fontWeight: '500' }}>Standalone Billed Service</span>
            </div>
          </div>
          <Button color="secondary" onClick={closeMembershipModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={applyMembershipSelections}>
            Apply Selections
          </Button>
        </ModalFooter>
      </Modal>

      {/* Quick Add Customer Modal */}
      <QuickAddCustomer
        isOpen={showQuickAddCustomer}
        toggle={() => setShowQuickAddCustomer(false)}
        onCustomerAdded={handleCustomerAdded}
      />

      {/* Customer Profile Modal */}
      <CustomerProfileModal
        isOpen={showCustomerProfile}
        toggle={() => {
          setShowCustomerProfile(false);
          setTableRowCustomerId(null);
        }}
        customerId={tableRowCustomerId || selectedCustomerOption?.value || null}
        customer={tableRowCustomerId ? { customer_Id: tableRowCustomerId } : (selectedCustomerOption ? { customer_Id: selectedCustomerOption.value } : null)}
      />

      {/* Advanced Customer Search Modal */}
      <CustomerAdvancedSearchModal
        isOpen={showAdvancedSearchModal}
        toggle={() => setShowAdvancedSearchModal(false)}
        userBranchId={session.branchId}
        onCustomerSelected={(customer) => {
          // Set the selected customer in the form and dropdown
          setForm(prev => ({
            ...prev,
            customer_Id: customer.customer_Id
          }));
          setSelectedCustomerOption({
            value: customer.customer_Id,
            label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
            customer: customer
          });
          // Check for membership and billable items
          if (customer.customer_Id) {
            checkCustomerMembership(customer.customer_Id);
          }
        }}
      />

      {/* Consent Form Modal */}
      {selectedCustomerOption?.value && (
        <ConsentFormModal
          isOpen={showConsentModal}
          toggle={() => setShowConsentModal(false)}
          bookingId={form.booking_Id || 0}
          customerName={selectedCustomerOption?.customer?.customer_NameEn || (selectedCustomerOption?.label ? selectedCustomerOption.label.split(' — ')[0] : '') || ''}
          customerMobile={selectedCustomerOption?.customer?.customer_ContactNo || (selectedCustomerOption?.label ? selectedCustomerOption.label.split(' — ')[1] : '') || ''}
          isConsentSigned={false}
          witnessName={consentData.witnessName || loggedInEmployeeName}
          witnessSignature={consentData.witnessSignature}
          guestSignature={consentData.guestSignature}
          onSignConsent={handleSignConsent}
          loading={consentLoading}
        />
      )}

      {/* Done Popup Modal */}
      <Modal isOpen={showDoneModal} toggle={() => {
        setShowDoneModal(false);
        setSelectedBookingForDone(null);
        setDoneServiceTimes([]);
      }} size="lg">
        <ModalHeader toggle={() => {
          setShowDoneModal(false);
          setSelectedBookingForDone(null);
          setDoneServiceTimes([]);
        }}>
          <i className="mdi mdi-check-circle me-2 text-success"></i>
          Mark Services as Done
        </ModalHeader>
        <ModalBody>
          {selectedBookingForDone && (
            <>
              <div className="mb-3">
                <p className="mb-1"><strong>Customer:</strong> {(() => {
                  if (!selectedBookingForDone.customer_Id) return 'N/A';
                  const customer = customers.find(c => c.value === selectedBookingForDone.customer_Id);
                  if (customer) {
                    // If label contains " — ", split it, otherwise use label directly
                    return customer.label?.split(' — ')[0] || customer.label || 'N/A';
                  }
                  return 'N/A';
                })()}</p>
                <p className="mb-0"><strong>Booking Date:</strong> {selectedBookingForDone.booking_Date ? formatDate(selectedBookingForDone.booking_Date) : 'N/A'}</p>
              </div>
              <div className="border rounded p-3">
                <Table responsive striped>
                  <thead>
                    <tr>
                      <th style={{ width: '5%', textAlign: 'center' }}>Select</th>
                      <th style={{ width: '5%', textAlign: 'center' }}>#</th>
                      <th style={{ width: '25%' }}>Service Name</th>
                      <th style={{ width: '18%' }}>Start Time</th>
                      <th style={{ width: '18%' }}>Stop Time</th>
                      <th style={{ width: '19%' }}>Employee Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doneServiceTimes.map((serviceTime, index) => {
                      // Find the corresponding resource
                      let resource = null;
                      selectedBookingForDone.details?.forEach(detail => {
                        detail.resourceDetails?.forEach(res => {
                          if (res.bookingResourceDetail_Id === serviceTime.bookingResourceDetail_Id) {
                            resource = res;
                          }
                        });
                      });

                      if (!resource) return null;

                      // Only show services with status Pending, Start, or Stop (exclude Complete)
                      if (resource.completionStatus === 'Complete' || resource.completionStatus === 'Cancel') {
                        return null;
                      }

                      const serialNumber = index + 1;

                      // Get service name - prefer from serviceTime, then from services list, then from resource
                      const service = services.find(s => s.value === resource.service_Id);
                      const serviceName = serviceTime?.serviceName || service?.label || resource.serviceName || `Service ${resource.service_Id}`;

                      // Get employee name - prefer from serviceTime, then from employees list, then from resource
                      const employee = employees.find(e => e.value === resource.employee_Id);
                      const employeeName = serviceTime?.employeeName || employee?.label || resource.employeeName || 'N/A';

                      // Get booking date for time pickers
                      const bookingDate = serviceTime?.bookingDate || (selectedBookingForDone.booking_Date ? new Date(selectedBookingForDone.booking_Date) : new Date());

                      // Helper to create date with time from time picker
                      const createDateTime = (timeDate, baseDate) => {
                        if (!timeDate) return null;
                        const result = new Date(baseDate);
                        result.setHours(timeDate.getHours(), timeDate.getMinutes(), 0, 0);
                        return result;
                      };

                      // Get current start/stop times based on completion status
                      let currentStartTime = serviceTime?.startTime;
                      let currentStopTime = serviceTime?.stopTime;

                      const status = resource.completionStatus || serviceTime?.completionStatus;

                      // Handle times based on status
                      if (status === 'Pending' || status === 'Check In') {
                        // For Pending and Check In: use from_Time and to_Time
                        if (!currentStartTime && resource.from_Time) {
                          const fromTime = new Date(resource.from_Time);
                          currentStartTime = new Date(bookingDate);
                          currentStartTime.setHours(fromTime.getHours(), fromTime.getMinutes(), 0, 0);
                        } else if (!currentStartTime) {
                          currentStartTime = new Date(bookingDate);
                          currentStartTime.setHours(9, 0, 0, 0);
                        }

                        if (!currentStopTime && resource.to_Time) {
                          const toTime = new Date(resource.to_Time);
                          currentStopTime = new Date(bookingDate);
                          currentStopTime.setHours(toTime.getHours(), toTime.getMinutes(), 0, 0);
                        } else if (!currentStopTime) {
                          currentStopTime = new Date(bookingDate);
                          currentStopTime.setHours(10, 0, 0, 0);
                        }
                      } else if (status === 'Start') {
                        // For Start: use start_Time if available, otherwise from_Time. For stop, only use stop_Time if it exists, otherwise leave empty
                        if (!currentStartTime) {
                          if (resource.start_Time) {
                            currentStartTime = new Date(resource.start_Time);
                          } else if (resource.from_Time) {
                            const fromTime = new Date(resource.from_Time);
                            currentStartTime = new Date(bookingDate);
                            currentStartTime.setHours(fromTime.getHours(), fromTime.getMinutes(), 0, 0);
                          } else {
                            currentStartTime = new Date(bookingDate);
                            currentStartTime.setHours(9, 0, 0, 0);
                          }
                        }

                        // Stop time: only use stop_Time if it exists, do NOT fall back to to_Time for Start status
                        if (!currentStopTime) {
                          if (resource.stop_Time) {
                            currentStopTime = new Date(resource.stop_Time);
                          }
                          // If stop_Time is null/empty, currentStopTime remains null (do not use to_Time)
                        }
                      } else if (status === 'Stop') {
                        // For Stop: use both start_Time and stop_Time from booking resource table
                        if (!currentStartTime) {
                          if (resource.start_Time) {
                            currentStartTime = new Date(resource.start_Time);
                          } else if (resource.from_Time) {
                            const fromTime = new Date(resource.from_Time);
                            currentStartTime = new Date(bookingDate);
                            currentStartTime.setHours(fromTime.getHours(), fromTime.getMinutes(), 0, 0);
                          } else {
                            currentStartTime = new Date(bookingDate);
                            currentStartTime.setHours(9, 0, 0, 0);
                          }
                        }

                        if (!currentStopTime) {
                          if (resource.stop_Time) {
                            currentStopTime = new Date(resource.stop_Time);
                          } else if (resource.to_Time) {
                            const toTime = new Date(resource.to_Time);
                            currentStopTime = new Date(bookingDate);
                            currentStopTime.setHours(toTime.getHours(), toTime.getMinutes(), 0, 0);
                          } else {
                            currentStopTime = new Date(bookingDate);
                            currentStopTime.setHours(10, 0, 0, 0);
                          }
                        }
                      }

                      return (
                        <tr key={`${serviceTime.bookingResourceDetail_Id}-${index}`}>
                          <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={serviceTime?.selected || false}
                                onChange={(e) => {
                                  const updated = doneServiceTimes.map(st =>
                                    st.bookingResourceDetail_Id === serviceTime.bookingResourceDetail_Id
                                      ? { ...st, selected: e.target.checked }
                                      : st
                                  );
                                  setDoneServiceTimes(updated);
                                }}
                              />
                            </div>
                          </td>
                          <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>{serialNumber}</td>
                          <td style={{ verticalAlign: 'middle' }}>
                            {serviceName}
                          </td>
                          <td style={{ verticalAlign: 'middle' }}>
                            <DatePicker
                              selected={currentStartTime}
                              onChange={(date) => {
                                const updatedTime = createDateTime(date, bookingDate);

                                // Auto-calculate stop time based on service duration
                                const duration = service?.duration || 30; // Use service duration from API (default 30 minutes)
                                const calculatedStopTime = new Date(updatedTime.getTime() + (duration * 60000));

                                const updated = doneServiceTimes.map(st =>
                                  st.bookingResourceDetail_Id === serviceTime.bookingResourceDetail_Id
                                    ? { ...st, startTime: updatedTime, stopTime: calculatedStopTime, bookingDate: bookingDate }
                                    : st
                                );
                                setDoneServiceTimes(updated);
                              }}
                              showTimeSelect
                              showTimeSelectOnly
                              timeIntervals={15}
                              timeCaption="Time"
                              dateFormat="h:mm aa"
                              className="form-control"
                              style={{ width: '100%' }}
                            />
                          </td>
                          <td style={{ verticalAlign: 'middle' }}>
                            <DatePicker
                              selected={currentStopTime}
                              onChange={(date) => {
                                const updatedTime = createDateTime(date, bookingDate);
                                const updated = doneServiceTimes.map(st =>
                                  st.bookingResourceDetail_Id === serviceTime.bookingResourceDetail_Id
                                    ? { ...st, stopTime: updatedTime, bookingDate: bookingDate }
                                    : st
                                );
                                setDoneServiceTimes(updated);
                              }}
                              showTimeSelect
                              showTimeSelectOnly
                              timeIntervals={15}
                              timeCaption="Time"
                              dateFormat="h:mm aa"
                              className="form-control"
                              style={{ width: '100%' }}
                              placeholderText={status === 'Start' && !resource.stop_Time ? "Set stop time" : "Select time"}
                            />
                          </td>
                          <td style={{ verticalAlign: 'middle' }}>
                            {employeeName}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => {
            setShowDoneModal(false);
            setSelectedBookingForDone(null);
            setDoneServiceTimes([]);
          }}>
            Cancel
          </Button>
          <Button
            color="success"
            onClick={handleMarkServicesDone}
            disabled={doneServiceTimes.length === 0 || markSelectedDoneLoading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {markSelectedDoneLoading ? (
              <>
                <ClipLoader size={14} color="#fff" />
                Processing...
              </>
            ) : (
              doneServiceTimes.filter(st => st.selected).length > 0 ? 'Mark Selected as Done' : 'All Done'
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Invoice Print Modal */}
      {invoicePrintData && (
        <InvoicePrintModal
          isOpen={showInvoicePrintModal}
          toggle={() => {
            setShowInvoicePrintModal(false);
            setInvoicePrintData(null);
          }}
          invoiceData={invoicePrintData}
          businesses={businesses}
        />
      )}

    </React.Fragment>
  );
};

// Invoice Print Modal Component
const InvoicePrintModal = ({ isOpen, toggle, invoiceData, businesses = [] }) => {
  if (!invoiceData || !invoiceData.header) return null;

  const printRef = useRef(null);

  const header = invoiceData.header;
  const details = invoiceData.details || [];
  const taxes = invoiceData.taxes || [];

  // Extract values with fallback for camelCase/PascalCase
  const invoiceNo = header.invoice_No || header.Invoice_No;
  const invoiceDate = header.invoice_Date || header.Invoice_Date;
  const customerName = header.customer_NameEn || header.Customer_NameEn;
  const customerAddress = header.customer_AddressEn || header.Customer_AddressEn;
  const customerTRN = header.customer_TRN || header.Customer_TRN;
  const customerContactNo = header.customer_ContactNo || header.Customer_ContactNo;
  const businessName = header.business_NameEn || header.Business_NameEn;
  const businessTRN = header.business_TRN || header.Business_TRN;
  const companyLogoPath = header.company_LogoPath || header.Company_LogoPath;

  const invoiceBusinessId = header.invoice_BusinessId || header.Invoice_BusinessId;
  const business = businesses.find(b => b.business_Id === invoiceBusinessId);
  const businessLogoPath = business?.business_LogoPath || null;
  const businessAddress = business?.business_Address || business?.business_AddressEn || header.business_AddressEn || header.Business_AddressEn || '';
  const logoPath = businessLogoPath || companyLogoPath;
  const overallDiscount = header.overallDiscountAmount || header.OverallDiscountAmount || 0;
  const grandTotal = header.invoice_NetAmount || header.Invoice_NetAmount || 0;
  const tipAmount = header.tipAmount || header.TipAmount || 0;
  const pointsAmount = header.pointsAmount || header.PointsAmount || 0;
  const giftCardAmount = header.giftCardAmount || header.GiftCardAmount || 0;
  
  const formatAmount = (amount) => {
    return (amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      return 'N/A';
    }
  };

  const getInvoiceTypeLabel = (type) => {
    if (!type) return 'N/A';
    const lowType = type.toString().toLowerCase();
    if (lowType === 'ca') return 'Cash';
    if (lowType === 'cr') return 'Credit';
    return type;
  };

  const invoiceTypeLabel = getInvoiceTypeLabel(header.invoice_TypeEn || header.Invoice_TypeEn || header.invoice_Type || header.Invoice_Type);

  const handlePrint = () => {
    if (!printRef.current) return;
    const element = printRef.current;
    const opt = {
      margin:       [10, 10, 10, 10],
      filename:     `Invoice - ${invoiceNo}${Date.now()}.pdf`,
      image:        { type: 'jpeg', quality: 0.98 },
      html2canvas:  { scale: 2, useCORS: true, logging: false },
      jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak:    { mode: ['css', 'legacy'] }
    };
    html2pdf().set(opt).from(element).save();
  };

  const ITEMS_PER_PAGE = 8;
  const pagesCount = Math.max(1, Math.ceil((details.length || 0) / ITEMS_PER_PAGE));
  
  const renderHeader = (hideBorderBottom = false) => (
      <React.Fragment>
          <tr>
            <th colSpan="8" style={{ border: 'none', padding: '0 0 20px 0' }}>
              <div className="invoice-header-bg">
                <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                  <h3 style={{ fontSize: '24px', margin: '0', color: 'black', fontWeight: 'bold', letterSpacing: '1px' }}>TAX INVOICE</h3>
                </div>
                 <table style={{ width: '100%', border: 'none', borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr>
                      {logoPath && (
                        <td style={{ width: '120px', border: 'none', padding: '0 20px 0 0', verticalAlign: 'middle' }}>
                          <img
                            src={`${BASE_URL}${logoPath.startsWith('/') ? logoPath : '/' + logoPath}`}
                            alt="Business Logo"
                            style={{ maxHeight: '90px', maxWidth: '110px', objectFit: 'contain' }}
                            crossOrigin="anonymous"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </td>
                      )}
                      <td style={{ border: 'none', verticalAlign: 'middle', textAlign: 'left' }}>
                        <h4 className="mb-1" style={{ fontSize: '20px', color: 'black', fontWeight: '800' }}>{(businessName || 'COMPANY').toUpperCase()}</h4>
                        {businessAddress && <p className="mb-2" style={{ color: 'black', fontSize: '13px', fontWeight: '600', lineHeight: '1.4', maxWidth: '500px' }}>{businessAddress}</p>}
                        {businessTRN && businessTRN.trim() !== '' && (
                          <div className="trn-badge">TRN: {businessTRN}</div>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </th>
          </tr>
          <tr>
            <th colSpan="8" style={{ border: 'none', padding: '0 0 20px 0' }}>
              <table style={{ width: '100%', border: 'none', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    <td style={{ width: '50%', border: 'none', padding: '0 8px', verticalAlign: 'top', textAlign: 'left' }}>
                      <p className="mb-1" style={{color: 'black', fontSize: '13px'}}><strong>Date:</strong> {formatDate(invoiceDate)}</p>
                      <p className="mb-1" style={{color: 'black', fontSize: '13px'}}><strong>Invoice No:</strong> {(header.isReturn || header.IsReturn) ? '' : 'INV-'}{invoiceNo}</p>
                      <p className="mb-1" style={{color: 'black', fontSize: '13px'}}><strong>Invoice Type:</strong> {invoiceTypeLabel}</p>
                    </td>
                    <td style={{ width: '50%', border: 'none', padding: '0 8px', verticalAlign: 'top', textAlign: 'left' }}>
                      <p className="mb-1" style={{color: 'black', fontSize: '13px'}}><strong>Customer Name:</strong> {customerName || 'N/A'}{customerContactNo && customerContactNo.trim() !== '' && ` (${customerContactNo})`}</p>
                      {customerAddress && customerAddress.trim() !== '' && (
                        <p className="mb-1" style={{color: 'black', fontSize: '13px'}}><strong>Address:</strong> {customerAddress}</p>
                      )}
                      {customerTRN && customerTRN.trim() !== '' && (
                        <p className="mb-1" style={{color: 'black', fontSize: '13px'}}><strong>Customer TRN:</strong> {customerTRN}</p>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </th>
          </tr>
          <tr className="invoice-table-header-row">
            <th style={{color: 'black'}}>S.No / الرقم</th>
            <th style={{color: 'black'}}>Description / الوصف</th>
            <th className="text-end" style={{color: 'black'}}>Price / السعر</th>
            <th className="text-end" style={{color: 'black'}}>Qty / الكمية</th>
            <th className="text-end" style={{color: 'black'}}>Disc. / الخصم</th>
            <th className="text-end" style={{color: 'black'}}>Taxable Amount / المبلغ الخاضع للضريبة</th>
            <th className="text-end" style={{color: 'black'}}>VAT / ضريبة القيمة المضافة</th>
            <th className="text-end" style={{color: 'black'}}>Total / الإجمالي</th>
          </tr>
      </React.Fragment>
  );

  const renderTotals = () => (
      <React.Fragment>
          <tr>
            <td colSpan="6" rowSpan={2 + (taxes?.length || 0) + (overallDiscount > 0 ? 1 : 0) + (tipAmount > 0 ? 1 : 0) + (pointsAmount > 0 ? 1 : 0) + (giftCardAmount > 0 ? 1 : 0)} style={{ border: 'none' }}></td>
            <td className="text-end" style={{color: 'black'}}><strong>Subtotal (AED):</strong></td>
            <td className="text-end" style={{color: 'black'}}><strong>{formatAmount(details.reduce((sum, item) => sum + ((item.netAmount || item.NetAmount || 0) - (item.taxAmt || item.TaxAmt || 0)), 0))}</strong></td>
          </tr>
          {taxes && taxes.length > 0 && (
            (() => {
              const taxTotals = {};
              taxes.forEach(tax => {
                const name = tax.tax_NameEn || tax.Tax_NameEn || 'Tax';
                const perc = tax.tax_Perc || tax.Tax_Perc || 5;
                const key = `${name}_${perc}`;
                if (!taxTotals[key]) taxTotals[key] = { name, perc, amount: 0 };
              });
              details.forEach(item => {
                const itemTax = item.taxAmt || item.TaxAmt || 0;
                const firstTaxKey = Object.keys(taxTotals)[0];
                if (firstTaxKey) taxTotals[firstTaxKey].amount += itemTax;
              });
              return Object.values(taxTotals).map(tax => (
                <tr key={`${tax.name}_${tax.perc}`}>
                  <td className="text-end" style={{color: 'black'}}><strong>{tax.name} ({tax.perc}%) (AED):</strong></td>
                  <td className="text-end" style={{color: 'black'}}><strong>{formatAmount(tax.amount)}</strong></td>
                </tr>
              ));
            })()
          )}
          {overallDiscount > 0 && (
            <tr>
              <td className="text-end" style={{color: 'black'}}><strong>Discount (AED):</strong></td>
              <td className="text-end" style={{color: 'black'}}><strong>{formatAmount(overallDiscount)}</strong></td>
            </tr>
          )}
          {tipAmount > 0 && (
            <tr>
              <td className="text-end" style={{color: 'black'}}><strong>Tip (AED):</strong></td>
              <td className="text-end" style={{color: 'black'}}><strong>{formatAmount(tipAmount)}</strong></td>
            </tr>
          )}
          {pointsAmount > 0 && (
            <tr>
              <td className="text-end" style={{color: 'black'}}><strong>Loyalty (AED):</strong></td>
              <td className="text-end" style={{color: 'black'}}><strong>- {formatAmount(pointsAmount)}</strong></td>
            </tr>
          )}
          {giftCardAmount > 0 && (
            <tr>
              <td className="text-end" style={{color: 'black'}}><strong>Gift Card (AED):</strong></td>
              <td className="text-end" style={{color: 'black'}}><strong>- {formatAmount(giftCardAmount)}</strong></td>
            </tr>
          )}
          <tr style={{ backgroundColor: '#ebf2f8' }}>
            <td className="text-end" style={{color: 'black'}}><strong>Grand Total (AED):</strong></td>
            <td className="text-end" style={{color: 'black'}}><strong>{formatAmount(grandTotal)}</strong></td>
          </tr>
      </React.Fragment>
  );

  const renderTerms = () => (
    <tfoot>
      <tr>
        <td colSpan="8" style={{ border: 'none', padding: 0 }}>
          <div className="row mt-4 mb-4" style={{ pageBreakInside: 'avoid' }}>
            <div className="col-12">
              <div className="border border-secondary rounded p-3" style={{ backgroundColor: '#fdfdfd' }}>
                <h6 style={{ fontSize: '14px', fontWeight: 'bold', color: '#5d4037' }}>Terms & Notes / الشروط والملاحظات:</h6>
                <ul className="ps-3 mb-0" style={{ fontSize: '12px', listStyleType: 'disc', color: 'black' }}>
                  <li>Discounts are applied per service as shown / يتم تطبيق الخصومات لكل خدمة كما هو موضح</li>
                  <li>VAT charged as per UAE regulations / يتم احتساب ضريبة القيمة المضافة حسب لوائح الإمارات العربية المتحدة</li>
                  <li>No refund after service completion / لا يوجد استرداد بعد اكتمال الخدمة</li>
                  <li>This is a computer-generated invoice and does not require a signature / هذه فاتورة تم إنشاؤها بواسطة الكمبيوتر ولا تتطلب توقيعًا</li>
                </ul>
              </div>
            </div>
          </div>
        </td>
      </tr>
    </tfoot>
  );

  let currentItemIndex = 0;
  const pagesList = [];
  
  for (let i = 0; i < pagesCount; i++) {
      const isLastRowPage = (i === pagesCount - 1);
      const pageItems = details.slice(i * ITEMS_PER_PAGE, (i + 1) * ITEMS_PER_PAGE);
      const requiresExtraTotalsPage = isLastRowPage && pageItems.length > 5;
      
      const showTotalsAndTermsOnThisPage = isLastRowPage && !requiresExtraTotalsPage;
      
      pagesList.push(
          <div key={`page-${i}`} style={{ pageBreakAfter: (showTotalsAndTermsOnThisPage && !requiresExtraTotalsPage) ? 'auto' : 'always', marginBottom: '20px' }}>
              <table className="table invoice-table" style={{ width: '100%', borderCollapse: 'collapse', color: 'black' }}>
                  <thead>{renderHeader()}</thead>
                  <tbody>
                      {pageItems.map((detail, index) => {
                           const priceWithTax = detail.netAmount || detail.NetAmount || 0;
                           const taxAmt = detail.taxAmt || detail.TaxAmt || 0;
                           const taxableAmount = priceWithTax - taxAmt;
                           const originalPrice = detail.price || detail.Price || 0;
                           
                           return (
                              <tr key={index}>
                                <td style={{color: 'black'}}>{currentItemIndex + index + 1}</td>
                                <td style={{color: 'black'}}>
                                  {detail.entityName || detail.EntityName}
                                  {(detail.entityType === 'gif' || detail.EntityType === 'gif') && detail.stockData && ` (${detail.stockData})`}
                                </td>
                                <td className="text-end" style={{color: 'black'}}>{formatAmount(originalPrice)}</td>
                                <td className="text-end" style={{color: 'black'}}>{Math.round(detail.quantity || detail.Quantity || 0)}</td>
                                <td className="text-end" style={{color: 'black'}}>{formatAmount(detail.disAmount || detail.DisAmount || 0)}</td>
                                <td className="text-end" style={{color: 'black'}}>{formatAmount(taxableAmount)}</td>
                                <td className="text-end" style={{color: 'black'}}>{formatAmount(taxAmt)}</td>
                                <td className="text-end" style={{color: 'black'}}>{formatAmount(priceWithTax)}</td>
                              </tr>
                           );
                      })}
                      {showTotalsAndTermsOnThisPage && renderTotals()}
                  </tbody>
                  {showTotalsAndTermsOnThisPage && renderTerms()}
              </table>
          </div>
      );
      currentItemIndex += ITEMS_PER_PAGE;
      
      if (requiresExtraTotalsPage) {
           pagesList.push(
              <div key={`page-totals`} style={{ pageBreakAfter: 'auto' }}>
                  <table className="table invoice-table" style={{ width: '100%', borderCollapse: 'collapse', color: 'black' }}>
                      <thead>{renderHeader(true)}</thead>
                      <tbody>
                          <tr><td colSpan="8" style={{ border: 'none', padding: '10px 0' }}></td></tr>
                          {renderTotals()}
                      </tbody>
                      {renderTerms()}
                  </table>
              </div>
          );
      }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <style>
        {`
          .invoice-print-content, 
          .invoice-print-content * {
            color: black !important;
            font-family: 'Inter', sans-serif !important;
          }
          .invoice-header-bg {
            background-color: #ebf2f8 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            border-radius: 12px;
            padding: 20px;
            margin-bottom: 25px;
            border: 1px solid #d6e4f0;
          }
          .trn-badge {
            background-color: #d6e4f0 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            padding: 4px 15px;
            border-radius: 6px;
            font-weight: bold;
            display: inline-block;
            border: 1px solid #c1d5e6;
            color: #2c3e50;
            font-size: 13px;
          }
          .invoice-table {
            margin-bottom: 0 !important;
            border-collapse: collapse !important;
            border: none !important;
          }
          .invoice-table th {
            background-color: #f0f5fa !important;
            border: 1px solid #dee2e6 !important;
            padding: 12px 10px !important;
            font-weight: 700 !important;
            font-size: 13px !important;
            text-align: left;
          }
          .invoice-table-header-row th:first-child {
            border-top-left-radius: 12px !important;
          }
          .invoice-table-header-row th:last-child {
            border-top-right-radius: 12px !important;
          }
          .invoice-table td {
            border: 1px solid #dee2e6 !important;
            padding: 10px !important;
            font-size: 13px !important;
          }
          .invoice-table .text-end {
            text-align: right !important;
          }
        `}
      </style>
      <ModalHeader toggle={toggle}>
        Invoice - {(header.isReturn || header.IsReturn) ? '' : 'INV-'}${header.invoice_No || header.Invoice_No}
      </ModalHeader>
      <ModalBody>
        <div ref={printRef} className="p-4 invoice-print-content" style={{ color: 'black' }}>
          {pagesList}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={handlePrint} className="waves-effect waves-light d-flex align-items-center">
          <Printer size={16} className="me-2" />
          Print
        </Button>
        <Button color="secondary" onClick={toggle} className="waves-effect waves-light">
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BookingPage;
